import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Inbox, Repeat, BarChart3, Store, Package, Home, Wrench,
  AlertTriangle, Shield, Recycle, Truck,
  Calendar, Loader2, Info, X, Flag, Settings, Eye, EyeOff,
  Award, Smartphone, Tv, WashingMachine, Tag,
  ChevronUp, ChevronDown, GripVertical, Pencil, RotateCcw, Check, Users, UserCheck,
  SlidersHorizontal, RefreshCw, ChevronLeft, ChevronRight, Presentation, Minus, Plus,
  TrendingUp, Crown, Trash2, BookOpen, Copy, FileDown,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { getDashboard, getDistintos, getSalesDay, getVisibility, setVisibility, getAprovacoesConfigs, getAprovacoesOSList, getMetricOrder, setMetricOrder, setAprovacaoThreshold, deleteAprovacaoThreshold, getHeadcount, saveHeadcount, getGdProjection, setGdProjection, getLastUpdates, getAcessorios, saveAcessoriosManual, saveAcessoriosMeta, getSeminovos, saveSeminovosMeta, getAcessoriosItens, getB2BAnalytics, getStorePlusSMB, getCustomSlides, saveCustomSlides, getSiteMeta, getRoteiros, syncRoteiros, generateRoteiro, saveRoteiro } from '../api';
import { COMPANIES } from '../App';
import { businessDaysBetween } from './executive/execUtils';
import { useAuth } from './AuthContext';
import B2BSalesPanel from './B2BSalesPanel';
import StorePlusSMBChart from './StorePlusSMBChart';
import MapeamentoModal from './MapeamentoModal';
import BacklogMapeamentoModal from './BacklogMapeamentoModal';

// ===== HIDDEN (per metric per loja) =====
const HIDDEN_TABS = {
  'fix-csp': ['ii', 'ih', 'ih_completo', 'eco_troca', 'one_stop', 'aprov_lm_ih', 'aprov_lb'],
  'fix-msc': ['recepcao', 'gd', 'one_stop', 'ad', 'balcao', 'aprov_mx', 'aprov_lm'],
  'vip': ['one_stop'],
};

const fmtDate = (d) => d.toISOString().split('T')[0];
const fmtDateBR = (s) => { if (!s) return ''; const [y,m,d] = s.split('-'); return `${d}/${m}/${y}`; };
const fmtNum = (n) => new Intl.NumberFormat('pt-BR').format(n || 0);
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style:'currency', currency:'BRL' }).format(v || 0);

// "2026-06-10T09:37:26" -> "10/06 09:37"
const fmtUpdateTs = (iso) => {
  if (!iso) return null;
  const [date, time] = iso.split('T');
  const [, m, d] = date.split('-');
  return `${d}/${m} ${(time || '').slice(0, 5)}`.trim();
};
// Considera desatualizado se o último upload não foi nem hoje nem ontem
const isUpdateStale = (iso) => {
  if (!iso) return true;
  const ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
  return iso.slice(0, 10) < fmtDate(ontem);
};
const TIPO_UPLOAD_LABEL = {
  gspn: 'GSPN', qless: 'Qless', gd: 'GD', backlog: 'Backlog', one_stop: 'One Stop',
  aprov_nota: 'Aprov. Nota', aprov_pecas: 'Aprov. Peças', aprov_mao_obra: 'Aprov. Mão de Obra',
  aprov_ov: 'Relatório de OV',
};
const tipoUploadLabel = (t) =>
  TIPO_UPLOAD_LABEL[t] || (String(t).startsWith('aprovacoes_') ? 'Aprovações' : String(t).toUpperCase());

function parseTimeToSec(s) {
  if (!s) return 0;
  const p = String(s).split(':');
  if (p.length === 3) return +p[0]*3600 + +p[1]*60 + +p[2];
  return 0;
}
function fmtMS(sec) {
  if (!sec || sec <= 0) return '—';
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function sumDaily(arr, ...fields) {
  if (!arr) return 0;
  return arr.reduce((a, d) => a + fields.reduce((s,f) => s + (d[f]||0), 0), 0);
}

function summarizeSection(s) {
  if (!s) return null;
  const ent = s.entradas_diario || [], rep = s.reparos_diario || [];
  return {
    eIw: sumDaily(ent,'iw'), eOw: sumDaily(ent,'ow'),
    eTotal: sumDaily(ent,'iw') + sumDaily(ent,'ow'),
    rIw: sumDaily(rep,'iw'), rOw: sumDaily(rep,'ow'),
    rTotal: sumDaily(rep,'iw') + sumDaily(rep,'ow'),
    // OS fechadas sem contato com o cliente (SCONT no ASC Job) — só vem nas seções de In Home.
    scont: s.sem_contato_diario ? sumDaily(s.sem_contato_diario, 'iw', 'ow') : null,
    backlog: s.backlog || { iw: 0, ow: 0, total: 0 },
  };
}
// ===== Roteiro da apresentação (IA): payload ESTRUTURADO do slide =====
// A IA não calcula nada — recebe exatamente os números que o slide exibe.
// Arredonda p/ 2 casas (estabiliza o hash no backend → só regenera se o DADO mudar).
// Tempo em linguagem humana p/ a IA: 252s → "4 minutos e 12 segundos" (nunca 252,31).
function fmtTempoHumano(v) {
  if (v == null || !isFinite(v)) return null;
  const total = Math.round(v);
  const m = Math.floor(total / 60), sec = total % 60;
  if (m <= 0) return `${sec} segundos`;
  if (sec === 0) return `${m} minuto${m > 1 ? 's' : ''}`;
  return `${m} minuto${m > 1 ? 's' : ''} e ${sec} segundo${sec > 1 ? 's' : ''}`;
}

function modelToPayload(model) {
  if (!model || !model.stores || !model.stores.length) return null;
  const r2 = (v) => (v == null || !isFinite(v)) ? null : Math.round(v * 100) / 100;
  // Valor p/ a IA conforme o formato: tempos SEMPRE humanizados; o resto numérico.
  const iaVal = (v, fmt) => fmt === 'ms' ? fmtTempoHumano(v) : r2(v);
  const payload = {
    indicador_principal: model.primaryLabel || '',
    lojas: model.stores.map(s => ({ loja: s.name, valor_principal: iaVal(s.primaryValue, model.primaryFmt) })),
  };
  if (model.headerChips && model.headerChips.length) payload.contexto = model.headerChips;
  if (model.primaryVsTarget) {
    payload.meta_ou_esperado_por_loja = Object.fromEntries(
      model.stores.map(s => [s.name, r2(model.primaryVsTarget[s.slug])]));
  }
  const linhas = (model.secundarios || []).filter(row => !row.info).map(row => ({
    metrica: row.label + (row.hint ? ` (${row.hint})` : ''),
    ...(row.lowerBetter ? { menor_e_melhor: true } : {}),
    ...(row.hint && /contexto/i.test(row.hint) ? { apenas_contexto: true } : {}),
    valores: Object.fromEntries(model.stores.map(s => [s.name, iaVal(row.valores?.[s.slug], row.fmt)])),
  }));
  if (linhas.length) payload.indicadores = linhas;
  // Bloco financeiro (Modelo 2) e consolidado do canal (Modelo 5) — já calculados.
  if (model.financeiro) {
    payload.bloco_financeiro = model.financeiro.linhas.map(ln => ({
      metrica: ln.label + (ln.hint ? ` (${ln.hint})` : ''),
      valores: Object.fromEntries(model.stores.map(s => [s.name, r2(ln.valores?.[s.slug])])),
    }));
  }
  if (model.consolidado) {
    payload.canal_consolidado = {
      titulo: model.consolidado.titulo, ritmo_pct: r2(model.consolidado.ritmo),
      realizado: r2(model.consolidado.realizado), esperado_ate_hoje: r2(model.consolidado.esperado),
      todas_abaixo_do_ritmo: !!model.consolidado.alerta,
    };
  }
  if (model.nota) payload.observacao_operacional = model.nota;
  // Guia de interpretação: o que define desempenho, direção, e o que é SÓ contexto.
  if (model.iaContexto) payload.guia_interpretacao = model.iaContexto;
  return payload;
}

function summarizeProjeto(s) {
  if (!s) return null;
  return {
    entradas: sumDaily(s.entradas_diario||[], 'total'),
    saidas: sumDaily(s.saidas_diario||[], 'total'),
    backlog: s.backlog,
  };
}
function summarizeRecepcao(arr) {
  if (!arr || arr.length === 0) return null;
  let qtd = 0, tmeW = 0, tmaW = 0;
  arr.forEach(d => {
    const q = d.qtd_tickets || 0; qtd += q;
    tmeW += parseTimeToSec(d.tme) * q;
    tmaW += parseTimeToSec(d.tma) * q;
  });
  return { qtd, tme: qtd>0 ? tmeW/qtd : 0, tma: qtd>0 ? tmaW/qtd : 0 };
}
function summarizeGDTA(g) {
  if (!g) return null;
  // gds = GDs VÁLIDOS (GD COMPLETION = 'Y' — o que a Samsung contabiliza); 'total' = todos os criados.
  return { gds: sumDaily(g.gd_diario||[], 'gdok'), tas: sumDaily(g.ta_diario||[], 'total') };
}

// ============= MODAL: ITENS DE VENDA DE ACESSÓRIOS =============

function AcessoriosListModal({ company, data, loading, onClose }) {
  const itens = data?.itens || [];
  const total = data?.total || 0;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0',
        width: '100%', maxWidth: 720, maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag size={16} color="#10b981" /> Acessórios vendidos · {company.fullName || company.name}
            </h3>
            <p style={{ fontSize: 11.5, color: '#64748b', marginTop: 3, fontFamily: monoFont }}>
              {itens.length} {itens.length === 1 ? 'item' : 'itens'} · {fmtBRL(total)}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 8, padding: 7, cursor: 'pointer', color: '#475569' }}><X size={16} /></button>
        </div>
        <div style={{ overflowY: 'auto', padding: '8px 12px 16px' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', padding: 24, justifyContent: 'center' }}>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Carregando…
            </div>
          ) : itens.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: '#64748b', fontSize: 13 }}>Nenhum acessório vendido no período.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: monoFont }}>
              <thead>
                <tr style={{ color: '#64748b', textAlign: 'left', textTransform: 'uppercase', fontSize: 9.5, letterSpacing: '0.08em' }}>
                  <th style={{ padding: '6px 8px' }}>NF</th>
                  <th style={{ padding: '6px 8px' }}>Produto</th>
                  <th style={{ padding: '6px 8px' }}>Cliente</th>
                  <th style={{ padding: '6px 8px' }}>Data</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((it, i) => (
                  <tr key={i} style={{ borderTop: '1px solid rgba(15,23,42,0.06)' }}>
                    <td style={{ padding: '7px 8px', color: '#1e293b' }}>{it.nf || it.os || '—'}</td>
                    <td style={{ padding: '7px 8px', color: '#0f766e' }}>
                      {it.produto || '—'}{it.marca ? <span style={{ color: '#64748b' }}> · {it.marca}</span> : null}
                    </td>
                    <td style={{ padding: '7px 8px', color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.cliente || '—'}</td>
                    <td style={{ padding: '7px 8px', color: '#64748b' }}>{it.data ? it.data.split('-').reverse().join('/') : '—'}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', color: '#15803d', fontWeight: 700 }}>{fmtBRL(it.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ METRICAS (Warrom + Aprovações) ============

// Metas e equipe agora são geridas na aba "Metas e Equipe" (self-service por loja).
// Os lápis de edição de meta dentro dos cards do Sales Day ficam desligados.
const META_EDIT_ENABLED = false;

const METRICAS = [
  // Warrom
  { key:'recepcao', grupo:'Warrom', titulo:'Recepção', subtitulo:'Senhas Qless · TME · TMA', Icone: Inbox, accent:'#b45309', type:'recepcao', fonte:'QLESS',
    descricao:'Quantas senhas foram tiradas no Qless, com o tempo médio de espera (TME) e de atendimento (TMA). Mede o fluxo e a velocidade do balcão de chegada.',
    filtros:[] },
  { key:'gd', grupo:'Warrom', titulo:'GD', subtitulo:'Galaxy Diagnostic · Projeção mensal', Icone: Repeat, accent:'#fbbf24', type:'gdta', fonte:'GSPN',
    descricao:'GD = Galaxy Diagnostic (diagnóstico do aparelho Samsung). A meta projetada equivale a 50% das OS abertas no Balcão (CI) no mesmo período. Status: Dentro (≥100%), Em atenção (80-99%), Abaixo (<80%).',
    filtros:[{ campo:'gd_tipo', label:'Tipo de GD (informativo)', valoresUsados:[] }] },
  { key:'total', grupo:'Warrom', titulo:'Total CI + IH', subtitulo:'Volume consolidado de reparos', Icone: BarChart3, accent:'#475569', type:'section', dataKey:'total_ci_ih', fonte:'GSPN',
    descricao:'Volume consolidado de reparos do balcão (CI) somado ao atendimento em domicílio (IH). Visão geral da operação.',
    filtros:[
      { campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['CI','PS','AD','SR','SH','IH','II'] },
      { campo:'type_calc', label:'Type (categoria)', valoresUsados:['HHP','NPC','WEA','MON','VD','AV','WM','REF','AC'] },
    ] },
  { key:'balcao', grupo:'Warrom', titulo:'Balcão (CI)', subtitulo:'Entradas e reparos no balcão', Icone: Store, accent:'#0f766e', type:'section', dataKey:'balcao_total', fonte:'GSPN',
    descricao:'Aparelhos que chegaram, foram reparados e ainda estão na fila do balcão. IW = em garantia, OW = fora de garantia. SR (Stock Repair) é apurado separadamente.',
    filtros:[
      { campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['CI','PS','AD','SH'] },
      { campo:'type_calc', label:'Type (categoria)', valoresUsados:['HHP','NPC','WEA','MON','VD','AV'] },
    ] },
  { key:'sr', grupo:'Warrom', titulo:'Stock Repair', subtitulo:'Reparo em estoque', Icone: Package, accent:'#9f1239', type:'section', dataKey:'sr_total', fonte:'GSPN',
    descricao:'Reparos feitos em peças/aparelhos do estoque, separados do balcão regular. Backlog: toda OS sem data de encerramento, independente de status — somente OS com data_entrega preenchida saem do backlog.',
    filtros:[{ campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['SR'] }] },
  { key:'ih', grupo:'Warrom', titulo:'In Home', subtitulo:'Domicílio + Instalação Inicial (IH + II)', Icone: Home, accent:'#34d399', type:'section', dataKey:'in_home_ii', fonte:'GSPN',
    descricao:'Atendimentos feitos na casa do cliente (TVs, refrigeradores, ar-condicionado, etc). "Sem contato (SCONT)" = OS fechadas sem conseguir contato com o cliente (ASC Job começando com SCONT, regra da Samsung de 13/10/2026); "OS realizadas" = reparos − sem contato.',
    filtros:[
      { campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['IH','II'] },
      { campo:'type_calc', label:'Type (categoria)', valoresUsados:['WM','REF','AC','MON','VD','AV'] },
    ] },
  // In Home COMPLETO (pedido 08/10/2026): tudo do In Home menos o balcão (CI) — é aqui
  // que se mede a produtividade (Services por ADM e Reparos por técnico In Home).
  { key:'ih_completo', grupo:'Warrom', titulo:'In Home Completo', subtitulo:'Tudo do In Home: IH + II + RH + SR', Icone: Home, accent:'#059669', type:'section', dataKey:'in_home_completo', fonte:'GSPN',
    descricao:'Todo o In Home, sem o balcão (CI): atendimento em domicílio (IH), instalação inicial (II, inclusive Eco Troca), RH e Stock Repair (SR), só dos produtos de In Home (TV, monitor, áudio e linha branca). Celular, notebook e wearable ficam fora (são bancada). Reparos do SR vêm do WTY quando a loja sobe esse relatório. Produtividade: Services por ADM = entradas ÷ ADMs In Home; Reparos por técnico = OS realizadas ÷ técnicos LM + LB + híbridos. "Sem contato (SCONT)" = OS fechadas sem conseguir contato com o cliente (ASC Job começando com SCONT, regra da Samsung de 13/10/2026); "OS realizadas" = reparos − sem contato.',
    filtros:[
      { campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['IH','II','RH','SR'] },
      { campo:'type_calc', label:'Type (categoria)', valoresUsados:['WM','REF','AC','MON','VD','AV','(sem type)'] },
    ] },
  { key:'ii', grupo:'Warrom', titulo:'Instalação Inicial', subtitulo:'Setup inicial', Icone: Wrench, accent:'#fb923c', type:'section', dataKey:'instalacao_inicial', fonte:'GSPN',
    descricao:'Setup inicial de aparelhos novos (II), normalmente TVs e linha branca grandes.',
    filtros:[{ campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['II'] }] },
  { key:'ad', grupo:'Warrom', titulo:'Acidental Damage', subtitulo:'Danos acidentais', Icone: AlertTriangle, accent:'#f87171', type:'projeto', dataKey:'ad', fonte:'GSPN',
    descricao:'Reparos por dano acidental (queda, líquido, etc). Cobertos por seguros tipo Care+.',
    filtros:[{ campo:'tipo_servico', label:'Tipo de Serviço', valoresUsados:['AD'] }] },
  { key:'garantia_ext', grupo:'Warrom', titulo:'Garantia Estendida', subtitulo:'GE / Care+', Icone: Shield, accent:'#4d7c0f', type:'projeto', dataKey:'garantia_ext', fonte:'GSPN',
    descricao:'Atendimentos cobertos por garantia estendida (Care+ / GE).',
    filtros:[{ campo:'garantia_ext', label:'Coluna Garantia Estendida', valoresUsados:['Extendida'] }] },
  { key:'eco_troca', grupo:'Warrom', titulo:'Eco Troca', subtitulo:'Trocas ecológicas', Icone: Recycle, accent:'#84cc16', type:'projeto', dataKey:'eco_troca', fonte:'GSPN',
    descricao:'Casos onde o reparo não compensa e o cliente troca por aparelho novo (programa Samsung).',
    filtros:[{ campo:'eco_troca', label:'Coluna Eco Troca', valoresUsados:['Eco troca'] }] },
  { key:'one_stop', grupo:'Warrom', titulo:'One Stop', subtitulo:'Frete / Logística', Icone: Truck, accent:'#f59e0b', type:'projeto', dataKey:'one_stop_dados', fonte:'ONE STOP',
    descricao:'Logística de frete (coleta/entrega de aparelhos via transportadora).',
    filtros:[{ campo:'one_stop', label:'Coluna One Stop', valoresUsados:['One Stop'] }] },
  // Aprovações (Sales Day)
  { key:'aprov_fgPre', grupo:'Aprovações', titulo:'FG Pré Aprovada', subtitulo:'Pré-aprovações fora de garantia', Icone: Award, accent:'#fbbf24', type:'aprovacoes', abaKeys:['salesDay','fgPre'],
    descricao:'OS de Fora de Garantia que JÁ chegam pré-aprovadas pelo cliente. Mede a conversão de pré-aprovados que vira faturamento de fato.' },
  { key:'aprov_mx', grupo:'Aprovações', titulo:'Aprovações MX', subtitulo:'Mobile · Celulares · NPC', Icone: Smartphone, accent:'#be123c', type:'aprovacoes', abaKeys:['mx'],
    descricao:'Aprovações de Mobile (celular, NPC, info, site). Mede quantos orçamentos foram aprovados pelo cliente vs total de OS no período.' },
  { key:'venda_acessorios', grupo:'Aprovações', titulo:'Venda de Acessórios', subtitulo:'Total faturado em acessórios', Icone: Tag, accent:'#10b981', type:'acessorios',
    descricao:'Valor total faturado em venda de acessórios no período (balcão). JMV vem do import (peças, cobertura BALCAO); FIX CSP, FIX MSC e VIP vêm da API do Gestor. Na VIP, o aparelho seminovo faturado na mesma operação de vendas fica fora daqui e conta em Seminovos.' },
  { key:'seminovos_mx', grupo:'Aprovações', titulo:'Seminovos MX', subtitulo:'Celulares e tablets seminovos', Icone: Tag, accent:'#f59e0b', type:'seminovos', categoria:'mx',
    descricao:'Venda de aparelhos seminovos MX (celular/tablet) no período, separada pela descrição do produto na nota. FIX CSP e FIX MSC: notas de saída OP 22. JMV: o produto vem do Relatório peça OV do Care (campo opcional na importação de Aprovações), ligado à venda pela OV; sem ele o card mostra "—". VIP: aparelho seminovo faturado na operação de vendas (a mesma dos acessórios), reconhecido pelo início da descrição (TELEVISOR, CELULAR, REFRIGERADOR…).' },
  { key:'seminovos_ih', grupo:'Aprovações', titulo:'Seminovos In Home', subtitulo:'TV e Linha Branca seminovos', Icone: Tag, accent:'#d97706', type:'seminovos', categoria:'ih',
    descricao:'Venda de aparelhos seminovos In Home (linha marrom + linha branca) no período, separada pela descrição do produto na nota. FIX CSP e FIX MSC: notas de saída OP 22. JMV: o produto vem do Relatório peça OV do Care (campo opcional na importação de Aprovações), ligado à venda pela OV; sem ele o card mostra "—". VIP: aparelho seminovo faturado na operação de vendas (a mesma dos acessórios), reconhecido pelo início da descrição (TELEVISOR, CELULAR, REFRIGERADOR…).' },
  { key:'aprov_lm', grupo:'Aprovações', titulo:'LM Balcão', subtitulo:'Linha Marrom no balcão', Icone: Tv, accent:'#b45309', type:'aprovacoes', abaKeys:['lm','lmBalcao'],
    descricao:'Aprovações de Linha Marrom (TVs, áudio) que chegaram via balcão da loja. Mede taxa de orçamento aprovado.' },
  { key:'aprov_lm_ih', grupo:'Aprovações', titulo:'LM In-Home', subtitulo:'Linha Marrom em domicílio', Icone: Tv, accent:'#475569', type:'aprovacoes', abaKeys:['lm_ih','lmInhome'],
    descricao:'Linha Marrom (TVs grandes) atendidas no domicílio do cliente. Mede aprovação após visita técnica.' },
  { key:'aprov_lb', grupo:'Aprovações', titulo:'Linha Branca', subtitulo:'AR · Refrigeradores · Lava e Seca', Icone: WashingMachine, accent:'#34d399', type:'aprovacoes', abaKeys:['lb','linhaBranca'],
    descricao:'Linha Branca (ar-condicionado, refrigerador, lava e seca) em domicílio. Mede aprovação após visita técnica.' },
];

// Aprovações: fonte depende da loja. JMV = CARE (manual); CSP/MSC/VIP = GESTOR API
// (coletor automático ligado em 25/09/2026 — dados vêm direto da API da Abase).
const fonteAprovacoes = (slug) => slug === 'jmv' ? 'CARE' : 'GESTOR API';

const FONTE_COLORS = {
  'QLESS':    { bg: 'rgba(100,116,139,0.10)', color: '#334155', border: 'rgba(100,116,139,0.30)' },
  'GSPN':     { bg: 'rgba(13,148,136,0.10)',  color: '#0f766e', border: 'rgba(13,148,136,0.30)' },
  'ONE STOP': { bg: 'rgba(245,158,11,0.10)',  color: '#b45309', border: 'rgba(245,158,11,0.30)' },
  'CARE':     { bg: 'rgba(244,114,182,0.10)', color: '#be185d', border: 'rgba(244,114,182,0.30)' },
  'GESTOR':   { bg: 'rgba(52,211,153,0.10)',  color: '#047857', border: 'rgba(52,211,153,0.30)' },
  'GESTOR API': { bg: 'rgba(52,211,153,0.10)', color: '#047857', border: 'rgba(52,211,153,0.30)' },
};

// Paleta profissional (tons "boardroom" — teal, ocre, vinho, verde-profundo).
// Substitui o azul/roxo/rosa saturado por cores sóbrias, boas p/ dados e reunião.
const COMPANY_THEME = {
  'jmv':     { color: '#0f766e' },  // teal
  'fix-csp': { color: '#b45309' },  // ocre / âmbar-terroso
  'fix-msc': { color: '#9f1239' },  // vinho / framboesa
  'vip':     { color: '#047857' },  // verde-esmeralda profundo
};

const monoFont = `'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace`;

// =================== ATOMS ===================

function StoreChip({ company }) {
  const c = COMPANY_THEME[company.slug]?.color || '#475569';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '4px 10px 4px 8px',
      background: 'rgba(15,23,42,0.04)',
      border: '1px solid rgba(15,23,42,0.08)',
      borderRadius: 999,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, boxShadow: `0 0 8px ${c}aa` }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', letterSpacing: '0.02em' }}>{company.name}</span>
    </div>
  );
}

function DeltaBadge({ value, avg }) {
  if (!isFinite(value) || !avg || avg === 0) return null;
  const delta = ((value - avg) / avg) * 100;
  if (!isFinite(delta)) return null;
  const positivo = delta > 0;
  const neutro = Math.abs(delta) < 0.5;
  const cor = neutro ? '#475569' : positivo ? '#10b981' : '#ef4444';
  const bg = neutro ? 'rgba(156,163,175,0.10)' : positivo ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)';
  const border = neutro ? 'rgba(156,163,175,0.25)' : positivo ? 'rgba(16,185,129,0.30)' : 'rgba(239,68,68,0.30)';
  const sign = neutro ? '' : positivo ? '+' : '';
  return (
    <div style={{ padding: '5px 10px', borderRadius: 10, background: bg, border: `1px solid ${border}`, textAlign: 'right' }}>
      <p style={{ fontSize: 9, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.10em', whiteSpace: 'nowrap' }}>vs Mês Anterior</p>
      <p style={{ fontSize: 16, fontWeight: 800, color: cor, lineHeight: 1.1, fontFamily: monoFont, letterSpacing: '-0.02em', marginTop: 2 }}>
        {neutro ? '0%' : `${sign}${delta.toFixed(delta % 1 === 0 ? 0 : 1)}%`}
      </p>
    </div>
  );
}

function StatPair({ label, value, sub, accent }) {
  return (
    <div>
      <p style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6 }}>{label}</p>
      <p style={{ fontSize: 28, fontWeight: 700, color: accent || '#0f172a', fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1 }}>{value}</p>
      {sub && <p style={{ fontSize: 10.5, color: '#64748b', marginTop: 5, fontFamily: monoFont, letterSpacing: '0.02em' }}>{sub}</p>}
    </div>
  );
}

function MicroStat({ label, value, accent }) {
  return (
    <div style={{ padding: '10px 12px', background: 'rgba(15,23,42,0.025)', border: '1px solid rgba(15,23,42,0.04)', borderRadius: 8 }}>
      <p style={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>{label}</p>
      <p style={{ fontSize: 16, fontWeight: 700, color: accent || '#0f172a', fontFamily: monoFont, letterSpacing: '-0.02em', marginTop: 4 }}>{value}</p>
    </div>
  );
}

function TaxaReparoBadge({ label, pct, color, showFlag }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 8, background: `${color}12`, border: `1px solid ${color}30` }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#475569', letterSpacing: '0.06em' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 800, color, fontFamily: monoFont, letterSpacing: '-0.02em' }}>{pct.toFixed(0)}%</span>
      {showFlag && <Flag size={11} color={color} fill={color} />}
    </div>
  );
}

function TaxaReparoBlock({ summary }) {
  const eIw = summary.eIw || 0, rIw = summary.rIw || 0;
  const eOw = summary.eOw || 0, rOw = summary.rOw || 0;
  if (eIw === 0 && eOw === 0) return null;
  const cor = (pct) => pct < 50 ? '#ef4444' : pct < 80 ? '#facc15' : '#10b981';
  const pctIw = eIw > 0 ? (rIw / eIw) * 100 : null;
  const pctOw = eOw > 0 ? (rOw / eOw) * 100 : null;
  return (
    <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: 'rgba(15,23,42,0.025)', border: '1px solid rgba(15,23,42,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Taxa de Reparo</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {pctIw !== null && <TaxaReparoBadge label="IW" pct={pctIw} color={cor(pctIw)} showFlag={pctIw < 50} />}
          {pctOw !== null && <TaxaReparoBadge label="OW" pct={pctOw} color={cor(pctOw)} showFlag={pctOw < 50} />}
        </div>
      </div>
    </div>
  );
}

function BacklogStrip({ backlog }) {
  if (!backlog || backlog.total === undefined) return null;
  return (
    <div style={{
      marginTop: 14, padding: '11px 14px',
      background: 'linear-gradient(90deg, rgba(100,116,139,0.10) 0%, rgba(100,116,139,0.03) 100%)',
      border: '1px solid rgba(100,116,139,0.18)', borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Backlog atual</span>
        <span style={{ fontSize: 22, fontWeight: 700, color: '#7e22ce', fontFamily: monoFont, letterSpacing: '-0.03em' }}>{fmtNum(backlog.total)}</span>
      </div>
      {(backlog.iw !== undefined || backlog.ow !== undefined) && (
        <p style={{ fontSize: 10.5, color: '#64748b', marginTop: 4, fontFamily: monoFont, letterSpacing: '0.02em' }}>
          IW <span style={{ color: '#b45309', fontWeight: 700 }}>{backlog.iw || 0}</span>
          {'  '}·  OW <span style={{ color: '#dc2626', fontWeight: 700 }}>{backlog.ow || 0}</span>
        </p>
      )}
    </div>
  );
}

// =================== CARDS ===================

function EmptyCard({ company, onShowRule }) {
  return (
    <div className="warrom-card">
      <StoreChip company={company} />
      <div style={{ padding: '24px 0 8px', textAlign: 'center' }}>
        <p style={{ fontSize: 11, color: '#94a3b8' }}>Sem dados no período</p>
      </div>
      {onShowRule && <VerRegraBtn onClick={onShowRule} />}
    </div>
  );
}

function CardSection({ company, summary, onShowRule }) {
  if (!summary || (summary.eTotal === 0 && summary.rTotal === 0 && (summary.backlog?.total || 0) === 0))
    return <EmptyCard company={company} onShowRule={onShowRule} />;
  return (
    <div className="warrom-card">
      <StoreChip company={company} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <StatPair label="Entradas" value={fmtNum(summary.eTotal)} sub={`IW ${summary.eIw} · OW ${summary.eOw}`} accent="#0369a1" />
        <StatPair label="Reparos" value={fmtNum(summary.rTotal)} sub={`IW ${summary.rIw} · OW ${summary.rOw}`} accent="#15803d" />
      </div>
      <TaxaReparoBlock summary={summary} />
      <BacklogStrip backlog={summary.backlog} />
      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

function CardProjeto({ company, summary, onShowRule }) {
  if (!summary || (summary.entradas === 0 && summary.saidas === 0)) return <EmptyCard company={company} onShowRule={onShowRule} />;
  const saldo = summary.entradas - summary.saidas;
  const pct = summary.entradas > 0 ? (summary.saidas / summary.entradas) * 100 : 0;
  const corBarra = pct >= 80 ? '#10b981' : pct >= 50 ? '#facc15' : '#ef4444';
  const saldoCor = saldo > 0 ? '#f87171' : saldo < 0 ? '#fbbf24' : '#34d399';
  return (
    <div className="warrom-card">
      <StoreChip company={company} />

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Saídas / Entradas</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: corBarra, fontFamily: monoFont }}>{pct.toFixed(0)}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 32, fontWeight: 800, color: '#15803d', fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1 }}>{fmtNum(summary.saidas)}</span>
          <span style={{ fontSize: 18, color: '#cbd5e1', fontFamily: monoFont, lineHeight: 1 }}>/</span>
          <span style={{ fontSize: 22, fontWeight: 700, color: '#c2410c', fontFamily: monoFont, letterSpacing: '-0.03em', lineHeight: 1 }}>{fmtNum(summary.entradas)}</span>
        </div>
        <div style={{ height: 6, background: 'rgba(15,23,42,0.06)', borderRadius: 99, marginTop: 12, overflow: 'hidden', border: '1px solid rgba(15,23,42,0.04)' }}>
          <div style={{
            height: '100%',
            width: `${Math.min(pct, 100)}%`,
            background: `linear-gradient(90deg, ${corBarra} 0%, ${corBarra}99 100%)`,
            borderRadius: 99,
            transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: `0 0 8px ${corBarra}55`,
          }} />
        </div>
      </div>

      {saldo !== 0 && (
        <div style={{
          marginTop: 14, padding: '9px 14px', borderRadius: 10,
          background: `linear-gradient(90deg, ${saldoCor}15 0%, ${saldoCor}05 100%)`,
          border: `1px solid ${saldoCor}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: saldoCor, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            {saldo > 0 ? 'Em aberto' : 'Saídas em excesso'}
          </span>
          <span style={{ fontSize: 18, fontWeight: 800, color: saldoCor, fontFamily: monoFont, letterSpacing: '-0.02em' }}>{fmtNum(Math.abs(saldo))}</span>
        </div>
      )}
      {summary.backlog && summary.backlog.total !== undefined && summary.backlog.total !== 0 && (
        <BacklogStrip backlog={summary.backlog} />
      )}
      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

function CardRecepcao({ company, summary, avgEmpresa, onShowRule, recepcionistas }) {
  if (!summary || summary.qtd === 0) return <EmptyCard company={company} />;
  const prod = recepcionistas > 0 ? summary.qtd / recepcionistas : null;
  return (
    <div className="warrom-card">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <StoreChip company={company} />
        <DeltaBadge value={summary.qtd} avg={avgEmpresa} />
      </div>
      <div style={{ marginTop: 18 }}>
        <p style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6 }}>Senhas no período</p>
        <p style={{
          fontSize: 38, fontWeight: 800,
          background: 'linear-gradient(135deg, #334155 0%, #475569 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          fontFamily: monoFont, letterSpacing: '-0.045em', lineHeight: 1,
        }}>{fmtNum(summary.qtd)}</p>
      </div>
      <ProdutividadeRow
        icon={Users}
        accent="#64748b"
        label="Recepcionistas"
        qtd={recepcionistas || 0}
        prod={prod}
        unit="senhas / recep"
      />
      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <MicroStat label="TME médio" value={fmtMS(summary.tme)} accent="#c2410c" />
        <MicroStat label="TMA médio" value={fmtMS(summary.tma)} accent="#0e7490" />
      </div>
      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

function ProdutividadeRow({ icon: Icon, accent, label, qtd, prod, unit }) {
  return (
    <div style={{
      marginTop: 14, padding: '10px 12px', borderRadius: 10,
      background: qtd > 0 ? `linear-gradient(90deg, ${accent}12 0%, ${accent}03 100%)` : 'rgba(15,23,42,0.02)',
      border: `1px solid ${qtd > 0 ? accent + '30' : 'rgba(15,23,42,0.04)'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <Icon size={14} color={qtd > 0 ? accent : '#94a3b8'} />
        <div>
          <p style={{ fontSize: 9, fontWeight: 800, color: qtd > 0 ? accent : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{label}</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: qtd > 0 ? '#0f172a' : '#64748b', fontFamily: monoFont, lineHeight: 1.1, marginTop: 1 }}>
            {qtd > 0 ? qtd : '—'}
          </p>
        </div>
      </div>
      {prod !== null && qtd > 0 && (
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.10em' }}>Produtividade</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: '#15803d', fontFamily: monoFont, lineHeight: 1.1, marginTop: 1 }}>
            {d1BR(prod)} <span style={{ fontSize: 9, color: '#64748b', fontWeight: 600 }}>{unit}</span>
          </p>
        </div>
      )}
    </div>
  );
}

// Fração do mês decorrida em DIAS ÚTEIS (seg-sex) até a data de referência. As lojas só
// abrem em dias úteis, então o "esperado pra hoje" do GD é proporcional aos dias úteis.
// lojaSlug desconta também os feriados LOCAIS da cidade da loja (FERIADOS_LOCAIS);
// sem slug = régua nacional (métricas gerais, ex. meta do site).
function businessMonthFactor(dataFim, lojaSlug) {
  const ref = dataFim ? new Date(dataFim + 'T00:00:00') : new Date();
  const y = ref.getFullYear(), m = ref.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const first = `${y}-${pad(m + 1)}-01`;
  const last = `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`;
  const refStr = `${y}-${pad(m + 1)}-${pad(ref.getDate())}`;
  const total = businessDaysBetween(first, last, lojaSlug);
  return total > 0 ? Math.min(businessDaysBetween(first, refStr, lojaSlug) / total, 1) : 0;
}

function CardGDTA({ company, summary, balcaoMX, factorPct = 50, metaFixa = 0, dataFim, onShowRule }) {
  if (!summary || summary.gds === 0) return <EmptyCard company={company} onShowRule={onShowRule} />;
  const gd = summary.gds;
  // Meta FIXA (manual) tem prioridade; senão factorPct% das OS abertas do Balcão (CI).
  const metaProjetada = metaFixa > 0 ? Math.round(metaFixa) : Math.round((balcaoMX || 0) * (factorPct / 100));
  const atingimento = metaProjetada > 0 ? (gd / metaProjetada) * 100 : 0;

  // Esperado pra hoje = Meta × (DIAS ÚTEIS decorridos ÷ dias úteis do mês)
  const _refGd = dataFim ? new Date(dataFim + 'T00:00:00') : new Date();
  const _pad = (n) => String(n).padStart(2, '0');
  const _first = `${_refGd.getFullYear()}-${_pad(_refGd.getMonth() + 1)}-01`;
  const _last = `${_refGd.getFullYear()}-${_pad(_refGd.getMonth() + 1)}-${_pad(new Date(_refGd.getFullYear(), _refGd.getMonth() + 1, 0).getDate())}`;
  const _refStr = `${_refGd.getFullYear()}-${_pad(_refGd.getMonth() + 1)}-${_pad(_refGd.getDate())}`;
  const uteisDecorridos = businessDaysBetween(_first, _refStr, company?.slug);
  const uteisMes = businessDaysBetween(_first, _last, company?.slug);
  const fator = uteisMes > 0 ? Math.min(uteisDecorridos / uteisMes, 1) : 0;
  const esperadoHoje = Math.round(metaProjetada * fator);
  const atingHoje = esperadoHoje > 0 ? (gd / esperadoHoje) * 100 : 0;

  let status, statusCor;
  if (metaProjetada === 0) { status = 'Sem base de cálculo'; statusCor = '#64748b'; }
  else if (atingimento >= 100) { status = 'Dentro da projeção'; statusCor = '#10b981'; }
  else if (atingHoje >= 100) { status = 'No ritmo do esperado'; statusCor = '#34d399'; }
  else if (atingHoje >= 80) { status = 'Em atenção'; statusCor = '#facc15'; }
  else { status = 'Abaixo da projeção'; statusCor = '#ef4444'; }

  return (
    <div className="warrom-card">
      <StoreChip company={company} />

      {/* GD realizado */}
      <div style={{ marginTop: 16 }}>
        <p style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 4 }}>GD realizados (período)</p>
        <p style={{ fontSize: 36, fontWeight: 800, color: '#b45309', fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1 }}>{fmtNum(gd)}</p>
      </div>

      {/* Esperado pra hoje */}
      <div style={{
        marginTop: 14, padding: '9px 12px', borderRadius: 10,
        background: 'linear-gradient(90deg, rgba(13,148,136,0.10) 0%, rgba(13,148,136,0.02) 100%)',
        border: '1px solid rgba(13,148,136,0.25)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, color: '#0f766e', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Esperado p/ hoje</span>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: atingHoje >= 100 ? '#15803d' : atingHoje >= 80 ? '#b45309' : '#dc2626', fontFamily: monoFont }}>{d1BR(atingHoje)}%</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#0f766e', fontFamily: monoFont }}>{fmtNum(esperadoHoje)}</span>
          <span style={{ fontSize: 10, color: '#64748b', fontFamily: monoFont }}>{uteisDecorridos}/{uteisMes} dias úteis</span>
        </div>
      </div>

      {/* Meta projetada */}
      <div style={{
        marginTop: 10, padding: '10px 12px', borderRadius: 10,
        background: `linear-gradient(90deg, ${statusCor}10 0%, ${statusCor}03 100%)`,
        border: `1px solid ${statusCor}30`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, color: statusCor, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Meta mensal</span>
          <span style={{ fontSize: 12, fontWeight: 800, color: statusCor, fontFamily: monoFont }}>{d1BR(atingimento)}%</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#334155' }}>Meta: <code style={{ color: '#1e293b', fontFamily: monoFont, fontWeight: 700 }}>{fmtNum(metaProjetada)}</code></span>
          <span style={{ fontSize: 10, color: '#475569', fontFamily: monoFont }}>{metaFixa > 0 ? 'Meta fixa (manual)' : `${factorPct}% Balcão CI (${fmtNum(balcaoMX || 0)})`}</span>
        </div>
        <div style={{ height: 6, background: 'rgba(15,23,42,0.04)', borderRadius: 99, marginTop: 8, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${Math.min(atingimento, 100)}%`,
            background: `linear-gradient(90deg, ${statusCor} 0%, ${statusCor}99 100%)`,
            borderRadius: 99,
            transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: `0 0 8px ${statusCor}55`,
          }} />
        </div>
        <p style={{ fontSize: 11, fontWeight: 800, color: statusCor, marginTop: 8 }}>{status}</p>
      </div>

      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

// ============= APROVAÇÕES CARD =============

function corPctMeta(rate, meta) {
  if (rate >= meta) return '#10b981';
  if (rate >= meta * 0.85) return '#facc15';
  return '#ef4444';
}

function VerRegraBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: 14, width: '100%',
        padding: '8px 12px',
        background: 'rgba(15,23,42,0.025)',
        border: '1px solid rgba(15,23,42,0.08)',
        borderRadius: 8, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        fontSize: 11, fontWeight: 600, color: '#475569', letterSpacing: '0.02em',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(15,23,42,0.06)'; e.currentTarget.style.color = '#1e293b'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(15,23,42,0.025)'; e.currentTarget.style.color = '#475569'; }}
    >
      <Info size={11} />
      <span>Ver regra da métrica</span>
    </button>
  );
}

function FonteBadge({ fonte }) {
  const ft = FONTE_COLORS[fonte] || FONTE_COLORS['GSPN'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 9px',
      background: ft.bg, border: `1px solid ${ft.border}`,
      borderRadius: 999, fontSize: 9.5, fontWeight: 800,
      color: ft.color, letterSpacing: '0.10em',
      fontFamily: monoFont, textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }} title={`Fonte dos dados: ${fonte}`}>
      {fonte}
    </span>
  );
}

function CardAprovacoes({ company, aba, comparison, diasNoPeriodo, diasNoMes, fatorEsperado, onShowRule, onShowOSList, tecnicos, showTecnicos, tecnicosLabel = "Técnicos MX", tecnicosUnit = "OS / téc" }) {
  if (!aba) return <EmptyCard company={company} onShowRule={onShowRule} />;
  // Operador que a conta usa: 'gt' = maior que (ex.: VIP, as de R$ 129,90 não contam); senão ≥.
  const op = comparison === 'gt' ? '>' : '≥';
  const cor = corPctMeta(aba.approvalRate || 0, aba.metaAprovacao || 0);
  // Esperado por DIAS ÚTEIS (feriados fora) quando o fator vem; senão calendário (legado).
  const _fat = (fatorEsperado != null) ? fatorEsperado : (diasNoPeriodo / diasNoMes);
  const espReceita = aba.metaReceita ? (aba.metaReceita * _fat) : 0;
  const corReceita = corPctMeta(aba.totalRevenue || 0, espReceita || 1);
  const fonte = fonteAprovacoes(company.slug);
  return (
    <div className="warrom-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <StoreChip company={company} />
        <FonteBadge fonte={fonte} />
      </div>

      {/* Taxa de aprovação */}
      <div style={{ marginTop: 16 }}>
        <p style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6 }}>
          Taxa de Aprovação · meta {aba.metaAprovacao}%
        </p>
        <p style={{ fontSize: 32, fontWeight: 800, color: cor, fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1 }}>
          {d1BR(aba.approvalRate || 0)}%
        </p>
        <div style={{ position:'relative', height: 8, background: '#e2e8f0', borderRadius: 5, marginTop: 10 }}>
          <div style={{ height: '100%', borderRadius: 5, width: `${Math.min(aba.approvalRate || 0, 100)}%`, background: cor, transition: 'width 0.8s' }} />
          <div style={{
            position: 'absolute', top: -2, left: `${Math.min(aba.metaAprovacao, 100)}%`,
            width: 2, height: 12, background: '#0f172a', opacity: 0.6, borderRadius: 1,
          }} title={`Meta: ${aba.metaAprovacao}%`} />
        </div>
        {onShowOSList ? (
          <button
            onClick={onShowOSList}
            style={{
              marginTop: 6, padding: '4px 8px',
              background: 'rgba(13,148,136,0.10)', border: '1px solid rgba(13,148,136,0.25)',
              borderRadius: 7, cursor: 'pointer',
              fontSize: 10.5, color: '#0f766e', fontFamily: monoFont, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
            title="Clique para ver a lista de OS"
          >
            <span style={{ color: '#0f172a' }}>{aba.approvedOS} / {aba.totalOS} OS</span>
            <span style={{ opacity: 0.6 }}>·</span>
            <span>{op} {fmtBRL(aba.threshold)}</span>
            <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.85 }}>↗</span>
          </button>
        ) : (
          <p style={{ fontSize: 10.5, color: '#64748b', marginTop: 6, fontFamily: monoFont }}>
            {aba.approvedOS} / {aba.totalOS} OS · {op} {fmtBRL(aba.threshold)}
          </p>
        )}
      </div>

      {/* Receita */}
      <div style={{
        marginTop: 14, padding: '12px 14px', borderRadius: 10,
        background: `linear-gradient(135deg, ${corReceita}12 0%, ${corReceita}03 100%)`,
        border: `1px solid ${corReceita}30`,
      }}>
        <p style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Receita</p>
        <p style={{ fontSize: 20, fontWeight: 800, color: corReceita, fontFamily: monoFont, letterSpacing: '-0.03em', marginTop: 4 }}>
          {fmtBRL(aba.totalRevenue)}
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <div>
            <p style={{ fontSize: 9.5, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.10em' }}>Esperado</p>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#b45309', fontFamily: monoFont }}>{fmtBRL(espReceita)}</p>
          </div>
          <div>
            <p style={{ fontSize: 9.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.10em' }}>Meta</p>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#475569', fontFamily: monoFont }}>{fmtBRL(aba.metaReceita)}</p>
          </div>
          {aba.ticketMedio > 0 && (
            <div>
              <p style={{ fontSize: 9.5, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.10em' }}>Ticket</p>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#475569', fontFamily: monoFont }}>{fmtBRL(aba.ticketMedio)}</p>
            </div>
          )}
        </div>
      </div>
      {showTecnicos && (
        <ProdutividadeRow
          icon={UserCheck}
          accent={tecnicosLabel.includes("LB") ? "#34d399" : "#f472b6"}
          label={tecnicosLabel}
          qtd={tecnicos || 0}
          prod={tecnicos > 0 ? (aba.totalOS || 0) / tecnicos : null}
          unit={tecnicosUnit}
        />
      )}
      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

// ============= VENDA DE ACESSÓRIOS CARD =============

function CardAcessorios({ company, total, meta, diasNoPeriodo, diasNoMes, fatorEsperado, loading, semDados, notaSemDados, editable, ano, mes, onSaveManual, onSaveMeta, onShowItens, onShowRule }) {
  const accent = '#10b981';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaDraft, setMetaDraft] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  const _fat = (fatorEsperado != null) ? fatorEsperado : (diasNoPeriodo / diasNoMes);
  const espValor = meta ? (meta * _fat) : 0;
  // total null = não dá para afirmar o valor (ex.: seminovos sem separação MX/IH):
  // mostra "—" em cinza, nunca "R$ 0,00" vermelho.
  const semValor = total == null;
  const corTotal = semValor ? '#94a3b8' : (espValor > 0 ? corPctMeta(total || 0, espValor) : accent);

  const startEdit = () => { setDraft(total ? String(total) : ''); setEditing(true); };
  const cancelEdit = () => { setEditing(false); };
  const saveEdit = async () => {
    const val = parseFloat(String(draft).replace(',', '.'));
    if (!isFinite(val) || val < 0) { alert('Valor inválido'); return; }
    setSaving(true);
    try {
      await onSaveManual(val);
      setEditing(false);
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const startEditMeta = () => { setMetaDraft(meta ? String(meta) : ''); setEditingMeta(true); };
  const saveMeta = async () => {
    const val = parseFloat(String(metaDraft).replace(',', '.'));
    if (!isFinite(val) || val < 0) { alert('Meta inválida'); return; }
    setSavingMeta(true);
    try { await onSaveMeta(val); setEditingMeta(false); }
    catch (e) { alert('Erro: ' + (e.response?.data?.detail || e.message)); }
    setSavingMeta(false);
  };

  return (
    <div className="warrom-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <StoreChip company={company} />
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
          background: editable ? 'rgba(52,211,153,0.10)' : 'rgba(13,148,136,0.10)',
          border: `1px solid ${editable ? 'rgba(52,211,153,0.30)' : 'rgba(13,148,136,0.30)'}`,
          borderRadius: 999, fontSize: 9.5, fontWeight: 800,
          color: editable ? '#047857' : '#0f766e', letterSpacing: '0.10em',
          fontFamily: monoFont, textTransform: 'uppercase',
        }}>{editable ? 'Manual' : fonteAprovacoes(company.slug)}</span>
      </div>

      <div style={{ marginTop: 18 }}>
        <p style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6 }}>
          Total faturado no período
        </p>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', padding: '8px 0' }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 12 }}>Carregando…</span>
          </div>
        ) : editing ? (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              type="number" min="0" step="0.01" autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="ex: 38749.19"
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8,
                border: '1px solid #cbd5e1', background: '#f4f6fb', color: accent,
                fontSize: 20, fontWeight: 800, fontFamily: monoFont, outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={saveEdit} disabled={saving} style={{
                flex: 1, padding: '7px 8px', borderRadius: 7, border: 'none',
                background: 'linear-gradient(180deg, #10b981, #059669)',
                color: '#fff', fontSize: 12, fontWeight: 800, cursor: saving ? 'wait' : 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}>{saving ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={12} />} Salvar</button>
              <button onClick={cancelEdit} disabled={saving} style={{
                padding: '7px 12px', borderRadius: 7, border: '1px solid rgba(15,23,42,0.08)',
                background: 'rgba(15,23,42,0.04)', color: '#475569',
                fontSize: 12, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
              }}>Cancelar</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
            {onShowItens ? (
              <button onClick={onShowItens} title="Ver acessórios vendidos" style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                display: 'inline-flex', alignItems: 'baseline', gap: 6,
              }}>
                <span style={{ fontSize: 30, fontWeight: 800, color: total > 0 ? corTotal : '#94a3b8', fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1 }}>
                  {semValor ? '—' : fmtBRL(total || 0)}
                </span>
                <span style={{ fontSize: 11, color: '#0f766e', fontWeight: 700 }}>↗</span>
              </button>
            ) : (
              <p style={{ fontSize: 30, fontWeight: 800, color: total > 0 ? corTotal : '#94a3b8', fontFamily: monoFont, letterSpacing: '-0.04em', lineHeight: 1 }}>
                {semValor ? '—' : fmtBRL(total || 0)}
              </p>
            )}
            {editable && (
              <button onClick={startEdit} title="Editar valor manual" style={{
                background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.30)',
                borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: '#047857',
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
              }}><Pencil size={11} /> Editar</button>
            )}
          </div>
        )}
        {!loading && !editing && (
          <p style={{ fontSize: 10.5, color: '#64748b', marginTop: 8, fontFamily: monoFont, letterSpacing: '0.02em' }}>
            {editable
              ? `Entrada manual · ${String(mes).padStart(2, '0')}/${ano}`
              : semDados ? (notaSemDados || 'Sem dados no período') : `Origem: ${fonteAprovacoes(company.slug)} (balcão)`}
          </p>
        )}
      </div>

      {/* Esperado (prorateado) + Meta editável */}
      {!loading && (
        <div style={{
          marginTop: 14, padding: '10px 14px', borderRadius: 10,
          background: `linear-gradient(135deg, ${corTotal}10 0%, ${corTotal}03 100%)`,
          border: `1px solid ${corTotal}25`,
        }}>
          {editingMeta ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ fontSize: 9.5, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.10em' }}>Meta mensal (R$)</p>
              <input
                type="number" min="0" step="0.01" autoFocus value={metaDraft}
                onChange={e => setMetaDraft(e.target.value)} placeholder="ex: 60000"
                style={{ width: '100%', padding: '7px 9px', borderRadius: 7, border: '1px solid #cbd5e1', background: '#f4f6fb', color: '#b45309', fontSize: 15, fontWeight: 800, fontFamily: monoFont, outline: 'none' }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={saveMeta} disabled={savingMeta} style={{ flex: 1, padding: '6px', borderRadius: 6, border: 'none', background: 'linear-gradient(180deg, #10b981, #059669)', color: '#fff', fontSize: 11, fontWeight: 800, cursor: savingMeta ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>{savingMeta ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={11} />} Salvar</button>
                <button onClick={() => setEditingMeta(false)} disabled={savingMeta} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.08)', background: 'rgba(15,23,42,0.04)', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Cancelar</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 9.5, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.10em' }}>Esperado</p>
                <p style={{ fontSize: 13, fontWeight: 800, color: '#b45309', fontFamily: monoFont }}>{fmtBRL(espValor)}</p>
              </div>
              <div>
                <p style={{ fontSize: 9.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.10em' }}>Meta</p>
                <p style={{ fontSize: 13, fontWeight: 800, color: '#475569', fontFamily: monoFont }}>{meta ? fmtBRL(meta) : '—'}</p>
              </div>
              {META_EDIT_ENABLED && (
              <button onClick={startEditMeta} title="Editar meta mensal" style={{
                marginLeft: 'auto', background: 'rgba(13,148,136,0.10)', border: '1px solid rgba(13,148,136,0.25)',
                borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: '#0f766e',
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
              }}><Pencil size={11} /> Meta</button>
              )}
            </div>
          )}
        </div>
      )}

      <VerRegraBtn onClick={onShowRule} />
    </div>
  );
}

// =================== REGRA MODAL ===================

function tagStyle(bg, color, border) {
  return {
    padding: '3px 9px', borderRadius: 999,
    background: bg, color, border: `1px solid ${border}`,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
    fontFamily: monoFont,
  };
}

function RegraModal({ metrica, company, distintos, aprovConfig, abaCfg, abaKey, loading, onClose, onChanged, gdFactor, onSaveGdFactor }) {
  const Icone = metrica.Icone;
  const isAprov = metrica.type === 'aprovacoes';
  const isGd = metrica.type === 'gdta';
  const [editing, setEditing] = useState(false);
  const [thrDraft, setThrDraft] = useState('');
  const [opDraft, setOpDraft] = useState('gte');
  const [minDraft, setMinDraft] = useState('');
  const [metaAprDraft, setMetaAprDraft] = useState('');
  const [metaRecDraft, setMetaRecDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingMeta, setEditingMeta] = useState(null); // 'aprovacao' | 'receita' | null
  const [gdEditing, setGdEditing] = useState(false);
  const [gdFactorDraft, setGdFactorDraft] = useState('');
  const startGdEdit = () => { setGdFactorDraft(String(gdFactor ?? 50)); setGdEditing(true); };
  const saveGdEdit = async () => {
    const val = parseFloat(String(gdFactorDraft).replace(',', '.'));
    if (!isFinite(val) || val < 0 || val > 500) { alert('Valor inválido (0-500)'); return; }
    setSaving(true);
    try {
      await onSaveGdFactor(company.slug, val);
      setGdEditing(false);
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const startEdit = () => {
    setThrDraft(String(abaCfg.threshold));
    setOpDraft(abaCfg.comparison || 'gte');
    setMinDraft(String(abaCfg.min_value || 0));
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);
  const saveEdit = async () => {
    const val = parseFloat(String(thrDraft).replace(',', '.'));
    if (!isFinite(val) || val < 0) { alert('Threshold inválido'); return; }
    const minVal = parseFloat(String(minDraft).replace(',', '.'));
    const minFinal = isFinite(minVal) && minVal >= 0 ? minVal : 0;
    setSaving(true);
    try {
      await setAprovacaoThreshold(company.slug, abaKey, val, opDraft, 'admin', minFinal, abaCfg.meta_aprovacao, abaCfg.meta_receita);
      setEditing(false);
      onChanged && onChanged();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };
  const startEditMeta = (which) => {
    if (which === 'aprovacao') setMetaAprDraft(String(abaCfg.meta_aprovacao || 0));
    if (which === 'receita') setMetaRecDraft(String(abaCfg.meta_receita || 0));
    setEditingMeta(which);
  };
  const saveEditMeta = async () => {
    setSaving(true);
    try {
      let metaApr = abaCfg.meta_aprovacao;
      let metaRec = abaCfg.meta_receita;
      if (editingMeta === 'aprovacao') {
        const v = parseFloat(String(metaAprDraft).replace(',', '.'));
        if (!isFinite(v) || v < 0 || v > 100) { alert('Meta aprovação inválida (0-100%)'); setSaving(false); return; }
        metaApr = v;
      }
      if (editingMeta === 'receita') {
        const v = parseFloat(String(metaRecDraft).replace(',', '.'));
        if (!isFinite(v) || v < 0) { alert('Meta receita inválida'); setSaving(false); return; }
        metaRec = v;
      }
      await setAprovacaoThreshold(company.slug, abaKey, abaCfg.threshold, abaCfg.comparison || 'gte', 'admin', abaCfg.min_value || 0, metaApr, metaRec);
      setEditingMeta(null);
      onChanged && onChanged();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };
  const resetEdit = async () => {
    if (!window.confirm('Voltar pro threshold padrão do sistema?')) return;
    setSaving(true);
    try {
      await deleteAprovacaoThreshold(company.slug, abaKey);
      setEditing(false);
      onChanged && onChanged();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 16, border: '1px solid #e2e8f0',
        maxWidth: 560, width: '100%', maxHeight: '85vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(15,23,42,0.10)',
      }}>
        <div style={{
          padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14,
          borderBottom: '1px solid #e2e8f0',
          background: `linear-gradient(135deg, ${metrica.accent}10 0%, transparent 100%)`,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11,
            background: `linear-gradient(135deg, ${metrica.accent}25 0%, ${metrica.accent}08 100%)`,
            border: `1px solid ${metrica.accent}40`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icone size={20} color={metrica.accent} strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.015em' }}>
              {metrica.titulo}
            </h2>
            <p style={{ fontSize: 11.5, color: '#475569', marginTop: 2 }}>Regra aplicada · loja {company.name}</p>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{
            background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)',
            borderRadius: 8, padding: 6, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: '#475569',
          }}><X size={16} /></button>
        </div>

        <div style={{ overflowY: 'auto', padding: '18px 22px 22px' }}>
          {/* Descrição */}
          {metrica.descricao && (
            <div style={{
              padding: '12px 14px', borderRadius: 10,
              background: `linear-gradient(135deg, ${metrica.accent}10 0%, ${metrica.accent}03 100%)`,
              border: `1px solid ${metrica.accent}30`,
              marginBottom: 16,
            }}>
              <p style={{ fontSize: 10, fontWeight: 800, color: metrica.accent, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6 }}>O que é</p>
              <p style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.55 }}>{metrica.descricao}</p>
            </div>
          )}

          {loading && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:24, color:'#475569' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 12 }}>Carregando regras…</span>
            </div>
          )}

          {!loading && isAprov && abaCfg && (
            <div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ flex: 1.4, minWidth: 200, padding: '10px 12px', background: editing ? 'rgba(13,148,136,0.06)' : 'rgba(15,23,42,0.02)', border: `1px solid ${editing ? 'rgba(13,148,136,0.4)' : abaCfg.is_overridden ? 'rgba(251,191,36,0.35)' : 'rgba(15,23,42,0.06)'}`, borderRadius: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <p style={{ fontSize: 9.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                      Threshold {abaCfg.is_overridden && <span style={{ color: '#b45309', marginLeft: 6 }}>· editado</span>}
                    </p>
                    {(abaCfg.use_value_rule || aprovConfig?.approval_field !== 'os_aprovada') && !editing && (
                      <button onClick={startEdit} title="Editar threshold" style={{
                        background: 'rgba(13,148,136,0.12)', border: '1px solid rgba(13,148,136,0.3)',
                        borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: '#0f766e',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}><Pencil size={10} /></button>
                    )}
                  </div>
                  {!editing ? (
                    <>
                      <p style={{ fontSize: 15, fontWeight: 800, color: '#c2410c', fontFamily: monoFont, marginTop: 2 }}>
                        {abaCfg.comparison === 'gt' ? '>' : '≥'} {fmtBRL(abaCfg.threshold)}
                      </p>
                      {abaCfg.is_overridden && (
                        <p style={{ fontSize: 9, color: '#64748b', fontFamily: monoFont, marginTop: 2 }}>
                          padrão: {abaCfg.default_comparison === 'gt' ? '>' : '≥'} {fmtBRL(abaCfg.default_threshold)}
                        </p>
                      )}
                    </>
                  ) : (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => setOpDraft('gte')} style={{
                          padding: '4px 8px', borderRadius: 6, border: `1px solid ${opDraft === 'gte' ? '#10b981' : 'rgba(15,23,42,0.08)'}`,
                          background: opDraft === 'gte' ? 'rgba(16,185,129,0.18)' : 'rgba(15,23,42,0.02)',
                          color: opDraft === 'gte' ? '#15803d' : '#475569',
                          fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: monoFont,
                        }}>≥ (inclusivo)</button>
                        <button onClick={() => setOpDraft('gt')} style={{
                          padding: '4px 8px', borderRadius: 6, border: `1px solid ${opDraft === 'gt' ? '#f472b6' : 'rgba(15,23,42,0.08)'}`,
                          background: opDraft === 'gt' ? 'rgba(244,114,182,0.18)' : 'rgba(15,23,42,0.02)',
                          color: opDraft === 'gt' ? '#be185d' : '#475569',
                          fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: monoFont,
                        }}> &gt; (acima de)</button>
                      </div>
                      <label style={{ fontSize: 9, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.10em', marginTop: 4 }}>Aprova quando valor</label>
                      <input
                        type="number"
                        step="0.01"
                        value={thrDraft}
                        onChange={e => setThrDraft(e.target.value)}
                        autoFocus
                        placeholder="ex: 250.00"
                        style={{
                          padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1',
                          background: '#f4f6fb', color: '#c2410c', fontSize: 14, fontWeight: 800,
                          fontFamily: monoFont, outline: 'none', width: '100%',
                        }}
                      />
                      <label style={{ fontSize: 9, fontWeight: 800, color: '#be185d', textTransform: 'uppercase', letterSpacing: '0.10em', marginTop: 4 }}>
                        Mínimo p/ considerar (0 = desativado)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={minDraft}
                        onChange={e => setMinDraft(e.target.value)}
                        placeholder="ex: 100.00"
                        style={{
                          padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1',
                          background: '#f4f6fb', color: '#be185d', fontSize: 14, fontWeight: 800,
                          fontFamily: monoFont, outline: 'none', width: '100%',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={saveEdit} disabled={saving} style={{
                          flex: 1, padding: '5px 8px', borderRadius: 6, border: 'none',
                          background: 'linear-gradient(180deg, #10b981, #059669)',
                          color: '#fff', fontSize: 11, fontWeight: 800, cursor: saving ? 'wait' : 'pointer',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                        }}>{saving ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={11} />} Salvar</button>
                        <button onClick={cancelEdit} disabled={saving} style={{
                          padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.08)',
                          background: 'rgba(15,23,42,0.04)', color: '#475569',
                          fontSize: 11, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
                        }}>Cancelar</button>
                        {abaCfg.is_overridden && (
                          <button onClick={resetEdit} disabled={saving} title="Voltar ao padrão" style={{
                            padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(251,191,36,0.3)',
                            background: 'rgba(251,191,36,0.10)', color: '#b45309',
                            fontSize: 11, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                          }}><RotateCcw size={11} /></button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{
                  flex: 1, minWidth: 120, padding: '10px 12px',
                  background: editingMeta === 'aprovacao' ? 'rgba(13,148,136,0.06)' : 'rgba(15,23,42,0.02)',
                  border: `1px solid ${editingMeta === 'aprovacao' ? 'rgba(13,148,136,0.4)' : (abaCfg.meta_aprovacao !== abaCfg.default_meta_aprovacao ? 'rgba(251,191,36,0.35)' : 'rgba(15,23,42,0.06)')}`,
                  borderRadius: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <p style={{ fontSize: 9.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                      Meta Aprov. {abaCfg.meta_aprovacao !== abaCfg.default_meta_aprovacao && <span style={{ color: '#b45309', marginLeft: 4 }}>· editado</span>}
                    </p>
                    {META_EDIT_ENABLED && editingMeta !== 'aprovacao' && (
                      <button onClick={() => startEditMeta('aprovacao')} title="Editar % meta aprovação" style={{
                        background: 'rgba(13,148,136,0.12)', border: '1px solid rgba(13,148,136,0.3)',
                        borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: '#0f766e',
                        display: 'inline-flex', alignItems: 'center',
                      }}><Pencil size={10} /></button>
                    )}
                  </div>
                  {editingMeta === 'aprovacao' ? (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="number" step="1" min="0" max="100"
                        value={metaAprDraft}
                        onChange={e => setMetaAprDraft(e.target.value)}
                        autoFocus
                        placeholder="ex: 80"
                        style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f4f6fb', color: '#15803d', fontSize: 15, fontWeight: 800, fontFamily: monoFont, outline: 'none', width: '100%', textAlign: 'right' }}
                      />
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={saveEditMeta} disabled={saving} style={{ flex: 1, padding: '5px', borderRadius: 6, border: 'none', background: 'linear-gradient(180deg, #10b981, #059669)', color: '#fff', fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          {saving ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={11} />} Salvar
                        </button>
                        <button onClick={() => setEditingMeta(null)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.08)', background: 'rgba(15,23,42,0.04)', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>×</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: 15, fontWeight: 800, color: '#15803d', fontFamily: monoFont, marginTop: 2 }}>{abaCfg.meta_aprovacao}%</p>
                      {abaCfg.meta_aprovacao !== abaCfg.default_meta_aprovacao && (
                        <p style={{ fontSize: 9, color: '#64748b', fontFamily: monoFont, marginTop: 1 }}>padrão: {abaCfg.default_meta_aprovacao}%</p>
                      )}
                    </>
                  )}
                </div>
                <div style={{
                  flex: 1, minWidth: 120, padding: '10px 12px',
                  background: editingMeta === 'receita' ? 'rgba(13,148,136,0.06)' : 'rgba(15,23,42,0.02)',
                  border: `1px solid ${editingMeta === 'receita' ? 'rgba(13,148,136,0.4)' : (abaCfg.meta_receita !== abaCfg.default_meta_receita ? 'rgba(251,191,36,0.35)' : 'rgba(15,23,42,0.06)')}`,
                  borderRadius: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <p style={{ fontSize: 9.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                      Meta Receita {abaCfg.meta_receita !== abaCfg.default_meta_receita && <span style={{ color: '#b45309', marginLeft: 4 }}>· editado</span>}
                    </p>
                    {META_EDIT_ENABLED && editingMeta !== 'receita' && (
                      <button onClick={() => startEditMeta('receita')} title="Editar R$ meta receita" style={{
                        background: 'rgba(13,148,136,0.12)', border: '1px solid rgba(13,148,136,0.3)',
                        borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: '#0f766e',
                        display: 'inline-flex', alignItems: 'center',
                      }}><Pencil size={10} /></button>
                    )}
                  </div>
                  {editingMeta === 'receita' ? (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="number" step="100" min="0"
                        value={metaRecDraft}
                        onChange={e => setMetaRecDraft(e.target.value)}
                        autoFocus
                        placeholder="ex: 35000"
                        style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f4f6fb', color: '#0f766e', fontSize: 14, fontWeight: 800, fontFamily: monoFont, outline: 'none', width: '100%', textAlign: 'right' }}
                      />
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={saveEditMeta} disabled={saving} style={{ flex: 1, padding: '5px', borderRadius: 6, border: 'none', background: 'linear-gradient(180deg, #10b981, #059669)', color: '#fff', fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          {saving ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={11} />} Salvar
                        </button>
                        <button onClick={() => setEditingMeta(null)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.08)', background: 'rgba(15,23,42,0.04)', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>×</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: 15, fontWeight: 800, color: '#0f766e', fontFamily: monoFont, marginTop: 2 }}>{fmtBRL(abaCfg.meta_receita)}</p>
                      {abaCfg.meta_receita !== abaCfg.default_meta_receita && (
                        <p style={{ fontSize: 9, color: '#64748b', fontFamily: monoFont, marginTop: 1 }}>padrão: {fmtBRL(abaCfg.default_meta_receita)}</p>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div>
                <p style={{ fontSize: 10, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
                  Status considerados {aprovConfig?.filter_field === 'aparelho' ? '(Aparelho)' : '(Tipo de Serviço)'}
                </p>
                <p style={{ fontSize: 11, color: '#15803d', fontWeight: 700, marginBottom: 6 }}>✓ Incluídos no filtro</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {(abaCfg.filtros || []).map(f => (
                    <span key={f} style={tagStyle('#10b98122', '#15803d', '#10b98140')}>{f}</span>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button
                    onClick={(abaCfg.use_value_rule || (aprovConfig?.approval_field !== 'os_aprovada' && aprovConfig?.approval_field !== 'status_encerrada')) ? startEdit : undefined}
                    style={{
                      background: 'transparent', border: 'none', padding: 0,
                      textAlign: 'left', color: '#64748b', fontSize: 11,
                      cursor: (abaCfg.use_value_rule || (aprovConfig?.approval_field !== 'os_aprovada' && aprovConfig?.approval_field !== 'status_encerrada')) ? 'pointer' : 'default',
                      display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                    }}
                    title={(abaCfg.use_value_rule || (aprovConfig?.approval_field !== 'os_aprovada' && aprovConfig?.approval_field !== 'status_encerrada')) ? 'Clique pra editar' : undefined}
                  >
                    <span>Critério de aprovação: {(aprovConfig?.approval_field === 'os_aprovada' && !abaCfg.use_value_rule)
                      ? <code style={{ color: '#b45309', fontFamily: monoFont }}>OS Aprovada == Sim</code>
                      : (aprovConfig?.approval_field === 'status_encerrada' && !abaCfg.use_value_rule)
                        ? <code style={{ color: '#b45309', fontFamily: monoFont }}>Status == EC-ENCERRADA E valor {abaCfg.comparison === 'gte' ? '≥' : '>'} {fmtBRL(abaCfg.threshold)}</code>
                        : <code style={{ color: '#b45309', fontFamily: monoFont }}>Valor pago {abaCfg.comparison === 'gt' ? '>' : '≥'} {fmtBRL(abaCfg.threshold)}</code>}</span>
                    {(abaCfg.use_value_rule || (aprovConfig?.approval_field !== 'os_aprovada' && aprovConfig?.approval_field !== 'status_encerrada')) && (
                      <Pencil size={10} color="#5eead4" />
                    )}
                  </button>
                  <button
                    onClick={(abaCfg.use_value_rule || (aprovConfig?.approval_field !== 'os_aprovada' && aprovConfig?.approval_field !== 'status_encerrada')) ? startEdit : undefined}
                    style={{
                      background: 'rgba(244,114,182,0.06)', border: '1px solid rgba(244,114,182,0.20)',
                      padding: '6px 10px', borderRadius: 8,
                      textAlign: 'left', color: '#475569', fontSize: 11,
                      cursor: (abaCfg.use_value_rule || (aprovConfig?.approval_field !== 'os_aprovada' && aprovConfig?.approval_field !== 'status_encerrada')) ? 'pointer' : 'default',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: '100%',
                    }}
                    title="Clique pra editar"
                  >
                    <span>
                      Mínimo considerado: <code style={{ color: '#be185d', fontFamily: monoFont, fontWeight: 700 }}>{abaCfg.min_value > 0 ? `valor ≥ ${fmtBRL(abaCfg.min_value)}` : 'desativado'}</code>
                      <span style={{ color: '#94a3b8', marginLeft: 6 }}>{abaCfg.min_value > 0 ? '· abaixo é ignorado (nem reprovado)' : '· considera todos os valores'}</span>
                    </span>
                    {(abaCfg.use_value_rule || (aprovConfig?.approval_field !== 'os_aprovada' && aprovConfig?.approval_field !== 'status_encerrada')) && (
                      <Pencil size={11} color="#f472b6" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!loading && isGd && (
            <div style={{
              padding: '12px 14px', borderRadius: 10, marginBottom: 14,
              background: gdEditing ? 'rgba(13,148,136,0.06)' : 'rgba(251,191,36,0.06)',
              border: `1px solid ${gdEditing ? 'rgba(13,148,136,0.4)' : 'rgba(251,191,36,0.30)'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <p style={{ fontSize: 9.5, color: '#b45309', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                  Meta de projeção GD
                </p>
                {!gdEditing && (
                  <button onClick={startGdEdit} title="Editar % da meta" style={{
                    background: 'rgba(13,148,136,0.12)', border: '1px solid rgba(13,148,136,0.3)',
                    borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: '#0f766e',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}><Pencil size={10} /></button>
                )}
              </div>
              {!gdEditing ? (
                <>
                  <p style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.5 }}>
                    Meta = <code style={{ color: '#b45309', fontFamily: monoFont, fontWeight: 800 }}>{gdFactor ?? 50}%</code> das OS abertas no Balcão (CI) no mesmo período.
                  </p>
                  <p style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                    Status: ≥100% Dentro · 80-99% Em atenção · &lt;80% Abaixo
                  </p>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ fontSize: 10, fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.10em' }}>
                    % das OS de Balcão CI ({company.name})
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={gdFactorDraft}
                    onChange={e => setGdFactorDraft(e.target.value)}
                    autoFocus
                    placeholder="ex: 50"
                    style={{
                      padding: '8px 10px', borderRadius: 7, border: '1px solid #cbd5e1',
                      background: '#f4f6fb', color: '#b45309', fontSize: 16, fontWeight: 800,
                      fontFamily: monoFont, outline: 'none', width: '100%',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={saveGdEdit} disabled={saving} style={{
                      flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none',
                      background: 'linear-gradient(180deg, #10b981, #059669)',
                      color: '#fff', fontSize: 11, fontWeight: 800, cursor: saving ? 'wait' : 'pointer',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}>{saving ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={11} />} Salvar</button>
                    <button onClick={() => setGdEditing(false)} disabled={saving} style={{
                      padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.08)',
                      background: 'rgba(15,23,42,0.04)', color: '#475569',
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    }}>Cancelar</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && !isAprov && (metrica.filtros || []).length > 0 && (
            <div>
              {/* Exclusão SR no Balcão CI — vale pra todas as lojas */}
              {metrica.key === 'balcao' && (
                <div style={{
                  padding: '10px 12px', borderRadius: 10, marginBottom: 14,
                  background: 'linear-gradient(90deg, rgba(251,191,36,0.12) 0%, rgba(251,191,36,0.03) 100%)',
                  border: '1px solid rgba(251,191,36,0.35)',
                }}>
                  <p style={{ fontSize: 10, fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>Regra global</p>
                  <p style={{ fontSize: 12, color: '#b45309' }}>
                    <code style={{ background: 'rgba(15,23,42,0.10)', padding: '1px 5px', borderRadius: 4, color: '#b45309', fontFamily: monoFont }}>SR</code> (Stock Repair) <strong>não conta</strong> no Balcão (CI) em nenhuma loja — apurado separadamente no card "Stock Repair".
                  </p>
                </div>
              )}
              {(metrica.filtros || []).map((f, idx) => {
                // Balcão CI: SR é excluído da regra em todas as lojas
                let valoresUsadosEff = f.valoresUsados || [];
                if (metrica.key === 'balcao' && f.campo === 'tipo_servico') {
                  valoresUsadosEff = valoresUsadosEff.filter(v => v !== 'SR');
                }
                const todos = distintos?.[f.campo] || [];
                const usadosSet = new Set(valoresUsadosEff.map(v => String(v)));
                const usados = todos.filter(v => usadosSet.has(String(v)));
                const naoUsados = todos.filter(v => !usadosSet.has(String(v)));
                const usadosFantasma = valoresUsadosEff.filter(v => v && !todos.map(String).includes(String(v)));
                return (
                  <div key={idx} style={{ marginBottom: 18 }}>
                    <p style={{ fontSize: 10, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>{f.label}</p>
                    {(usados.length > 0 || usadosFantasma.length > 0) && (
                      <div style={{ marginBottom: 8 }}>
                        <p style={{ fontSize: 11, color: '#15803d', fontWeight: 700, marginBottom: 6 }}>✓ Incluídos no filtro</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {usados.map(v => <span key={v} style={tagStyle('#10b98122', '#15803d', '#10b98140')}>{v}</span>)}
                          {usadosFantasma.map(v => <span key={`f-${v}`} style={{ ...tagStyle('#facc1522', '#b45309', '#facc1540'), fontStyle: 'italic' }}>{v} (sem dados)</span>)}
                        </div>
                      </div>
                    )}
                    {naoUsados.length > 0 && (
                      <div>
                        <p style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>✗ Existem na base mas NÃO entram nesta métrica</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {naoUsados.map(v => <span key={v} style={tagStyle('rgba(15,23,42,0.04)', '#475569', 'rgba(15,23,42,0.08)')}>{v}</span>)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !isAprov && (metrica.filtros || []).length === 0 && (
            <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
              Esta métrica não aplica filtro por valor de coluna — usa todos os registros da fonte.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// =================== FILTER BAR ===================

function FilterBar({ dataInicio, dataFim, setDataInicio, setDataFim, setMes, atalhos, filtroExpandido, setFiltroExpandido, diasNoPeriodo, loading, onOpenSettings, onOpenCustomSlides, onOpenRoteiros, onOpenHeadcount, onOpenMapeamento, onOpenBacklog, updates, canEdit = true }) {
  const btn = (active) => ({
    padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
    background: active ? 'linear-gradient(180deg, #0f766e, #0f766e)' : 'rgba(15,23,42,0.04)',
    color: active ? '#fff' : '#475569',
    boxShadow: active ? '0 4px 12px rgba(15,118,110,0.35)' : 'none',
    transition: 'all 0.15s',
  });
  const input = {
    padding: '9px 12px', borderRadius: 10, border: '1px solid #cbd5e1',
    background: '#f4f6fb', color: '#0f172a', fontSize: 13, outline: 'none',
  };
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 10,
      background: 'linear-gradient(180deg, #f4f6fb 0%, rgba(255,255,255,0.92) 100%)',
      backdropFilter: 'blur(10px)',
      marginBottom: 24, paddingBottom: 4,
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 14, padding: '14px 18px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Calendar size={14} color="#64748b" />
          <span style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Período</span>
          <button onClick={() => setMes(0)} style={btn(atalhos.mesAtual)}>Mês Atual</button>
          <button onClick={() => setMes(-1)} style={btn(atalhos.mesPassado)}>Mês Anterior</button>
          <button onClick={() => setMes(-2)} style={btn(!atalhos.mesAtual && !atalhos.mesPassado && !filtroExpandido)}>2 meses</button>
          <button onClick={() => setFiltroExpandido(!filtroExpandido)} style={{ ...btn(filtroExpandido) }}>
            {filtroExpandido ? 'Fechar' : 'Filtro livre'}
          </button>
          {canEdit && (<>
          <button
            onClick={onOpenMapeamento}
            title="Editar filtros, status e thresholds das aprovações"
            aria-label="Mapeamento"
            style={{
              marginLeft: 'auto',
              padding: '8px 12px', borderRadius: 10,
              border: '1px solid rgba(244,114,182,0.30)',
              background: 'rgba(244,114,182,0.10)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 700, color: '#be185d',
            }}
          >
            <SlidersHorizontal size={13} />
            <span>Mapeamento</span>
          </button>
          <button
            onClick={onOpenBacklog}
            title="Configurar filtros e status do Backlog"
            aria-label="Mapeamento Backlog"
            style={{
              padding: '8px 12px', borderRadius: 10,
              border: '1px solid rgba(52,211,153,0.30)',
              background: 'rgba(52,211,153,0.10)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 700, color: '#15803d',
            }}
          >
            <SlidersHorizontal size={13} />
            <span>Backlog</span>
          </button>
          <button
            onClick={onOpenSettings}
            title="Configurar métricas visíveis"
            aria-label="Configurações"
            style={{
              padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.08)',
              background: 'rgba(15,23,42,0.04)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 700, color: '#334155',
            }}
          >
            <Settings size={14} />
            <span>Configurar</span>
          </button>
          <button
            onClick={onOpenCustomSlides}
            title="Criar slides de texto/observações para a apresentação"
            aria-label="Slides personalizados"
            style={{
              padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.08)',
              background: 'rgba(15,23,42,0.04)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 700, color: '#334155',
            }}
          >
            <Presentation size={14} />
            <span>Slides</span>
          </button>
          <button
            onClick={onOpenRoteiros}
            title="Editar os roteiros gerados pela IA (fora da apresentação)"
            aria-label="Roteiros"
            style={{
              padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.08)',
              background: 'rgba(15,23,42,0.04)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 700, color: '#334155',
            }}
          >
            <BookOpen size={14} />
            <span>Roteiros</span>
          </button>
          </>)}
        </div>
        {filtroExpandido && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>De</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={input} />
            <label style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>Até</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={input} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(15,23,42,0.035)', border: '1px solid rgba(15,23,42,0.06)',
          }}>
            {loading
              ? <Loader2 size={11} color="#fbbf24" style={{ animation: 'spin 1s linear infinite' }} />
              : <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b98188' }} />}
            <span style={{ fontSize: 11, color: '#475569', fontWeight: 600, fontFamily: monoFont }}>
              {fmtDateBR(dataInicio)} → {fmtDateBR(dataFim)} · {diasNoPeriodo}d
            </span>
          </div>
          {updates && updates.length > 0 && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              padding: '4px 10px', borderRadius: 999,
              background: 'rgba(15,23,42,0.035)', border: '1px solid rgba(15,23,42,0.06)',
            }}>
              <RefreshCw size={10} color="#64748b" />
              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Atualizado
              </span>
              {updates.map((u, i) => (
                <span
                  key={u.slug}
                  title={u.tooltip}
                  style={{ fontSize: 10.5, fontWeight: 600, fontFamily: monoFont, cursor: 'default', whiteSpace: 'nowrap' }}
                >
                  {i > 0 && <span style={{ color: '#cbd5e1', marginRight: 8 }}>·</span>}
                  <span style={{ color: '#475569' }}>{u.name}</span>{' '}
                  <span style={{ color: u.stale ? '#b45309' : '#475569' }}>{u.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =================== SETTINGS MODAL ===================

function SettingsModal({ hidden, order, onToggle, onMove, onSaveAll, saving, onClose }) {
  // Ordena METRICAS conforme `order` (keys faltantes vão pro fim na ordem original)
  const byKey = new Map(METRICAS.map(m => [m.key, m]));
  const usadas = new Set();
  const ordenadas = [];
  (order || []).forEach(k => {
    if (byKey.has(k) && !usadas.has(k)) {
      ordenadas.push(byKey.get(k));
      usadas.add(k);
    }
  });
  METRICAS.forEach(m => { if (!usadas.has(m.key)) ordenadas.push(m); });

  const grupoCor = {
    'Warrom': '#0d9488',
    'Aprovações': '#f472b6',
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 16, border: '1px solid #e2e8f0',
        maxWidth: 560, width: '100%', maxHeight: '85vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(15,23,42,0.10)',
      }}>
        <div style={{
          padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14,
          borderBottom: '1px solid #e2e8f0',
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11,
            background: 'linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(99,102,241,0.08) 100%)',
            border: '1px solid rgba(99,102,241,0.4)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Settings size={20} color="#a5b4fc" strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Configurar Métricas</h2>
            <p style={{ fontSize: 11.5, color: '#475569', marginTop: 2 }}>
              Ocultar/exibir seções globalmente · vale para todos os usuários
            </p>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{
            background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)',
            borderRadius: 8, padding: 6, cursor: 'pointer', color: '#475569',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}><X size={16} /></button>
        </div>

        <div style={{ overflowY: 'auto', padding: '14px 22px 18px' }}>
          <p style={{ fontSize: 10.5, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
            Arraste pelas setas pra reordenar livremente. As métricas aparecem na mesma ordem da Sales Day 2.0.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ordenadas.map((m, idx) => {
              const isHidden = hidden.includes(m.key);
              const Icone = m.Icone;
              const isFirst = idx === 0;
              const isLast = idx === ordenadas.length - 1;
              const gCor = grupoCor[m.grupo] || '#475569';
              return (
                <div
                  key={m.key}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 8px 8px 10px', borderRadius: 10,
                    background: isHidden ? 'rgba(15,23,42,0.02)' : `${m.accent}10`,
                    border: `1px solid ${isHidden ? 'rgba(15,23,42,0.06)' : m.accent + '30'}`,
                    opacity: isHidden ? 0.6 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  <GripVertical size={14} color="#475569" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button
                      onClick={() => onMove(m.key, -1)}
                      disabled={isFirst}
                      aria-label="Subir"
                      title="Subir"
                      style={{
                        background: isFirst ? 'transparent' : 'rgba(15,23,42,0.06)',
                        border: '1px solid rgba(15,23,42,0.08)', borderRadius: 5,
                        padding: 2, cursor: isFirst ? 'not-allowed' : 'pointer',
                        color: isFirst ? '#cbd5e1' : '#a5b4fc',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    ><ChevronUp size={11} /></button>
                    <button
                      onClick={() => onMove(m.key, +1)}
                      disabled={isLast}
                      aria-label="Descer"
                      title="Descer"
                      style={{
                        background: isLast ? 'transparent' : 'rgba(15,23,42,0.06)',
                        border: '1px solid rgba(15,23,42,0.08)', borderRadius: 5,
                        padding: 2, cursor: isLast ? 'not-allowed' : 'pointer',
                        color: isLast ? '#cbd5e1' : '#a5b4fc',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    ><ChevronDown size={11} /></button>
                  </div>
                  <button
                    onClick={() => onToggle(m.key)}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                      padding: '6px 10px', borderRadius: 8,
                      background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                    }}
                    title="Clique para ocultar/exibir"
                  >
                    <Icone size={16} color={isHidden ? '#475569' : m.accent} />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: isHidden ? '#475569' : '#0f172a' }}>
                      {m.titulo}
                    </span>
                    <span style={{
                      padding: '2px 7px', borderRadius: 999, fontSize: 9, fontWeight: 800,
                      color: gCor, background: `${gCor}15`, border: `1px solid ${gCor}30`,
                      textTransform: 'uppercase', letterSpacing: '0.1em',
                    }}>{m.grupo}</span>
                    {isHidden ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        <EyeOff size={12} /> Oculto
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: m.accent, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        <Eye size={12} /> Visível
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{
          padding: '14px 22px', borderTop: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
        }}>
          <button onClick={onClose} style={{
            padding: '9px 16px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.08)',
            background: 'rgba(15,23,42,0.04)', color: '#334155',
            fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
          }}>Cancelar</button>
          <button onClick={onSaveAll} disabled={saving} style={{
            padding: '9px 20px', borderRadius: 10, border: 'none',
            background: 'linear-gradient(180deg, #0f766e, #0f766e)',
            color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
            boxShadow: '0 4px 12px rgba(15,118,110,0.35)',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            {saving && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =================== OS LIST MODAL ===================

function OSListModal({ metrica, company, data, loading, filtro, setFiltro, onClose }) {
  const lista = (data?.os_list || []).filter(o => {
    if (filtro === 'aprovadas') return o.is_approved;
    if (filtro === 'reprovadas') return !o.is_approved;
    return true;
  });
  const totalReceita = lista.reduce((s, o) => s + (o.valor || 0), 0);
  const tabBtn = (active, accent) => ({
    padding: '6px 12px', borderRadius: 8, border: '1px solid',
    borderColor: active ? accent : 'rgba(15,23,42,0.08)',
    background: active ? `${accent}22` : 'rgba(15,23,42,0.02)',
    color: active ? accent : '#475569',
    fontSize: 11.5, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.02em',
  });
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 60,
      background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        border: `1px solid ${metrica.accent}40`, borderRadius: 16,
        width: '100%', maxWidth: 1080, maxHeight: '88vh',
        boxShadow: `0 24px 60px ${metrica.accent}25, 0 8px 32px rgba(15,23,42,0.10)`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 22px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: `${metrica.accent}22`, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <metrica.Icone size={18} color={metrica.accent} />
            </div>
            <div>
              <p style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{metrica.titulo} · {company.label}</p>
              <p style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                {data?.aba_titulo || ''} · {data?.total || 0} OS · {data?.aprovadas || 0} aprovadas
              </p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{
            background: 'rgba(15,23,42,0.08)', border: 'none', borderRadius: 8,
            padding: 6, cursor: 'pointer', color: '#334155',
            display: 'inline-flex', alignItems: 'center',
          }}><X size={16} /></button>
        </div>

        {/* Filtros */}
        <div style={{ padding: '12px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setFiltro('todas')} style={tabBtn(filtro === 'todas', '#0d9488')}>Todas ({data?.os_list?.length || 0})</button>
          <button onClick={() => setFiltro('aprovadas')} style={tabBtn(filtro === 'aprovadas', '#10b981')}>Aprovadas ({(data?.os_list || []).filter(o => o.is_approved).length})</button>
          <button onClick={() => setFiltro('reprovadas')} style={tabBtn(filtro === 'reprovadas', '#f87171')}>Não aprovadas ({(data?.os_list || []).filter(o => !o.is_approved).length})</button>
          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#475569', fontFamily: monoFont }}>
            Receita visível: <span style={{ color: '#b45309', fontWeight: 800 }}>{fmtBRL(totalReceita)}</span>
          </span>
        </div>

        {/* Conteúdo */}
        <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
          {loading && (
            <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#475569' }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Carregando OS…
            </div>
          )}
          {!loading && data?.error && (
            <p style={{ padding: 40, color: '#dc2626', textAlign: 'center' }}>{data.error}</p>
          )}
          {!loading && !data?.error && lista.length === 0 && (
            <p style={{ padding: 40, color: '#475569', textAlign: 'center' }}>Nenhuma OS encontrada nesse filtro.</p>
          )}
          {!loading && !data?.error && lista.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f4f6fb', zIndex: 2 }}>
                <tr>
                  {['OS', 'Data', 'Tipo / Aparelho', 'Valor', 'Aprovada?', 'Status'].map((h, i) => (
                    <th key={i} style={{
                      padding: '10px 14px', textAlign: i === 3 ? 'right' : 'left',
                      borderBottom: '1px solid #e2e8f0', color: '#64748b',
                      fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lista.map((o, idx) => (
                  <tr key={idx} style={{
                    background: idx % 2 ? 'rgba(15,23,42,0.02)' : 'transparent',
                    borderBottom: '1px solid rgba(226,232,240,0.8)',
                  }}>
                    <td style={{ padding: '8px 14px', fontFamily: monoFont, color: '#0f172a', fontWeight: 700 }}>{o.os}</td>
                    <td style={{ padding: '8px 14px', fontFamily: monoFont, color: '#475569' }}>{fmtDateBR(o.data)}</td>
                    <td style={{ padding: '8px 14px', color: '#334155', maxWidth: 320 }}>
                      <div style={{ fontWeight: 600 }}>{o.tipo_servico || '—'}</div>
                      {o.aparelho && <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>{o.aparelho}</div>}
                    </td>
                    <td style={{ padding: '8px 14px', fontFamily: monoFont, color: '#b45309', textAlign: 'right', fontWeight: 700 }}>{fmtBRL(o.valor)}</td>
                    <td style={{ padding: '8px 14px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                        fontSize: 10, fontWeight: 800, fontFamily: monoFont,
                        background: o.is_approved ? 'rgba(16,185,129,0.15)' : 'rgba(248,113,113,0.12)',
                        color: o.is_approved ? '#15803d' : '#dc2626',
                        border: `1px solid ${o.is_approved ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.25)'}`,
                      }}>{o.is_approved ? 'SIM' : 'NÃO'}</span>
                    </td>
                    <td style={{ padding: '8px 14px', fontSize: 11, color: '#475569' }}>{o.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {!loading && data && !data.error && lista.length > 0 && (
          <div style={{
            padding: '10px 22px', borderTop: '1px solid #e2e8f0',
            fontSize: 10.5, color: '#64748b', fontFamily: monoFont,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
          }}>
            <span>Critério: {lista[0]?.criterio_aprovacao}</span>
            <span>{lista.length} OS exibidas</span>
          </div>
        )}
      </div>
    </div>
  );
}

// =================== ROTEIROS (edição FORA da apresentação) ===================
// Painel de edição dos roteiros gerados pela IA. NÃO é o modo apresentador —
// na apresentação, o roteiro vive na janela separada (/apresentador).
function RoteirosModal({ slides, roteiros, generating, onGenerate, onSave, onRefresh, onClose }) {
  const SAMSUNG = "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif";
  const [openKey, setOpenKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { onRefresh?.(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const abrir = (key) => { setOpenKey(k => k === key ? null : key); setEditing(false); setErr(''); };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)', border: '1px solid rgba(100,116,139,0.40)', borderRadius: 16, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: SAMSUNG }}>
        <div style={{ padding: '14px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <BookOpen size={18} color="#0f766e" />
            <p style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', margin: 0 }}>Roteiros da apresentação</p>
          </span>
          <button onClick={onClose} aria-label="Fechar" style={{ background: 'rgba(15,23,42,0.08)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#334155' }}><X size={16} /></button>
        </div>
        <p style={{ padding: '10px 22px 4px', fontSize: 11.5, color: '#475569', margin: 0 }}>
          Edite ou regenere os textos AQUI, antes da reunião. Na apresentação, use o botão <b>Iniciar modo apresentador</b> — o roteiro abre em outra janela, invisível para o público.
        </p>
        <div style={{ flex: 1, overflow: 'auto', padding: '10px 22px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {slides.map((m, i) => {
            const rot = roteiros?.[m.key];
            const gen = (generating || []).includes(m.key);
            const aberto = openKey === m.key;
            return (
              <div key={m.key} style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 12, overflow: 'hidden' }}>
                <button onClick={() => abrir(m.key)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: SAMSUNG }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', fontFamily: monoFont, minWidth: 24 }}>{i + 1}</span>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: '#1F2A44', flex: 1 }}>{m.titulo}</span>
                  {gen && <span style={{ fontSize: 11, fontWeight: 700, color: '#0f766e', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> gerando…</span>}
                  {!gen && rot?.texto && <span style={{ fontSize: 11, fontWeight: 700, color: rot.edited ? '#B26A00' : '#008577' }}>{rot.edited ? 'editado' : 'gerado'}</span>}
                  {!gen && !rot?.texto && <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>sem roteiro</span>}
                  {aberto ? <ChevronUp size={15} color="#64748b" /> : <ChevronDown size={15} color="#64748b" />}
                </button>
                {aberto && (
                  <div style={{ padding: '0 14px 12px' }}>
                    {editing ? (
                      <textarea value={draft} onChange={e => setDraft(e.target.value)} autoFocus
                        style={{ width: '100%', minHeight: 130, boxSizing: 'border-box', padding: '9px 11px', borderRadius: 9, border: '1px solid #cbd5e1', fontSize: 13, lineHeight: 1.55, color: '#1F2A44', fontFamily: 'inherit', resize: 'vertical', outline: 'none' }} />
                    ) : (
                      <p style={{ fontSize: 13, color: rot?.texto ? '#1F2A44' : '#94a3b8', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: '2px 0 8px' }}>{rot?.texto || 'Roteiro ainda não gerado para este slide.'}</p>
                    )}
                    {err && <p style={{ fontSize: 11.5, color: '#D63A2F', margin: '4px 0' }}>{err}</p>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!editing && <button onClick={() => { setDraft(rot?.texto || ''); setEditing(true); setErr(''); }} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}><Pencil size={12} style={{ verticalAlign: '-2px', marginRight: 5 }} />Editar</button>}
                      {editing && (<>
                        <button disabled={busy} onClick={async () => { setBusy(true); setErr(''); try { await onSave(m.key, draft); setEditing(false); } catch (e) { setErr('Falha ao salvar: ' + (e.response?.data?.detail || e.message)); } setBusy(false); }}
                          style={{ padding: '7px 13px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', opacity: busy ? 0.7 : 1 }}>{busy ? 'Salvando…' : 'Salvar'}</button>
                        <button onClick={() => { setEditing(false); setErr(''); }} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>Cancelar</button>
                      </>)}
                      {!editing && (
                        <button disabled={busy || gen} onClick={async () => { setBusy(true); setErr(''); try { await onGenerate(m); } catch (e) { setErr('Falha ao gerar: ' + (e.response?.data?.detail || e.message)); } setBusy(false); }}
                          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(15,118,110,0.35)', background: 'rgba(15,118,110,0.08)', color: '#0f766e', fontSize: 11.5, fontWeight: 800, cursor: (busy || gen) ? 'default' : 'pointer', opacity: (busy || gen) ? 0.6 : 1 }}>
                          <RefreshCw size={12} style={{ verticalAlign: '-2px', marginRight: 5, ...(busy ? { animation: 'spin 1s linear infinite' } : {}) }} />{busy ? 'Gerando…' : 'Gerar novamente'}</button>
                      )}
                      {!editing && rot?.texto && (
                        <button onClick={() => { try { navigator.clipboard?.writeText(rot.texto); } catch { } }} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}><Copy size={12} style={{ verticalAlign: '-2px', marginRight: 5 }} />Copiar</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =================== SLIDES PERSONALIZADOS (gerenciador) ===================
// Cria/edita/exclui/posiciona slides de texto do Modo Apresentação (global, superadmin).
function CustomSlidesModal({ initialSlides, anchors, saving, onSave, onClose }) {
  const newId = () => `s${Date.now()}${Math.floor(Math.random() * 1000)}`;
  const [imgErr, setImgErr] = useState('');
  const [list, setList] = useState(() => (initialSlides || []).map(s => ({
    id: s.id || newId(),
    tipo: s.tipo === 'imagem' ? 'imagem' : 'texto',
    titulo: s.titulo || '',
    subtitulo: s.subtitulo || '',
    bulletsText: (s.bullets || []).join('\n'),
    imagem: s.imagem || '',
    after_key: s.after_key || '',
    ativo: s.ativo !== false,
  })));

  const upd = (id, patch) => setList(l => l.map(s => s.id === id ? { ...s, ...patch } : s));
  const add = () => setList(l => [...l, { id: newId(), tipo: 'texto', titulo: '', subtitulo: '', bulletsText: '', imagem: '', after_key: '', ativo: true }]);
  const del = (id) => setList(l => l.filter(s => s.id !== id));

  // Reduz a imagem no navegador (máx 1600px, JPEG) antes de guardar como data-URI.
  const onPickImage = (id, file) => {
    setImgErr('');
    if (!file) return;
    if (!file.type.startsWith('image/')) { setImgErr('Selecione um arquivo de imagem.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const MAX = 1600;
          let { width, height } = img;
          if (!width || !height) { setImgErr('Imagem inválida.'); return; }
          if (width > MAX || height > MAX) {
            const r = Math.min(MAX / width, MAX / height);
            width = Math.round(width * r); height = Math.round(height * r);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { setImgErr('Não foi possível processar a imagem neste navegador.'); return; }
          ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height); // fundo branco (JPEG não tem transparência)
          ctx.drawImage(img, 0, 0, width, height);
          const dataUri = canvas.toDataURL('image/jpeg', 0.82);
          if (dataUri.length > 5_800_000) { setImgErr('Imagem muito grande mesmo após reduzir. Tente uma menor.'); return; }
          upd(id, { imagem: dataUri, tipo: 'imagem' });
        } catch (err) {
          setImgErr('Falha ao processar a imagem.');
        }
      };
      img.onerror = () => setImgErr('Não consegui ler essa imagem.');
      img.src = reader.result;
    };
    reader.onerror = () => setImgErr('Falha ao ler o arquivo.');
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    // Mantém o slide se tiver título OU (for imagem com imagem) — não perde título digitado.
    const payload = list
      .filter(s => (s.titulo.trim() || (s.tipo === 'imagem' && s.imagem)))
      .map(s => ({
        id: s.id, tipo: s.tipo,
        titulo: s.titulo.trim(), subtitulo: s.subtitulo.trim(),
        bullets: s.tipo === 'texto' ? s.bulletsText.split('\n').map(b => b.trim()).filter(Boolean) : [],
        imagem: s.tipo === 'imagem' ? s.imagem : '',
        after_key: s.after_key || null, ativo: !!s.ativo,
      }));
    // Guarda de tamanho: evita blob gigante (imagens são pesadas em base64).
    if (payload.length > 40) { setImgErr('Máximo de 40 slides.'); return; }
    const totalBytes = payload.reduce((a, s) => a + (s.imagem || '').length + s.titulo.length + s.subtitulo.length + s.bullets.reduce((x, b) => x + b.length, 0), 0);
    if (totalBytes > 24_000_000) { setImgErr('Conteúdo total muito grande (muitas imagens). Remova ou reduza algumas.'); return; }
    setImgErr('');
    onSave(payload);
  };

  const SAMSUNG = "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif";
  const inp = { width: '100%', padding: '9px 11px', borderRadius: 9, border: '1px solid #d9dde3', background: '#fff', fontSize: 14, fontWeight: 600, fontFamily: SAMSUNG, color: '#0f172a', outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)', border: '1px solid rgba(100,116,139,0.40)', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Presentation size={18} color="#1428A0" />
            <p style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', margin: 0 }}>Slides personalizados</p>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{ background: 'rgba(15,23,42,0.08)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#334155' }}><X size={16} /></button>
        </div>

        <div style={{ padding: '10px 22px 4px', fontSize: 11.5, color: '#475569' }}>
          Slides de <b>texto</b> ou <b>imagem</b> que aparecem no <b>Modo Apresentação</b> (todas as lojas veem). Escolha o tipo e onde cada um entra.
        </div>
        {imgErr && (
          <div style={{ margin: '6px 22px 0', padding: '8px 12px', borderRadius: 9, background: 'rgba(214,58,47,0.08)', border: '1px solid rgba(214,58,47,0.22)', color: '#D63A2F', fontSize: 12.5, fontWeight: 600 }}>{imgErr}</div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: '10px 22px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {list.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Nenhum slide ainda. Clique em “+ Adicionar slide”.</div>
          )}
          {list.map((s, i) => (
            <div key={s.id} style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 12, padding: 14, opacity: s.ativo ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', fontFamily: monoFont }}>#{i + 1}</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => upd(s.id, { ativo: !s.ativo })} title={s.ativo ? 'Ativo (clique p/ ocultar)' : 'Oculto (clique p/ ativar)'} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 8, border: '1px solid #e2e8f0', background: s.ativo ? 'rgba(0,195,178,0.10)' : 'rgba(15,23,42,0.04)', color: s.ativo ? '#008577' : '#94a3b8', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                  {s.ativo ? <Eye size={13} /> : <EyeOff size={13} />}{s.ativo ? 'Ativo' : 'Oculto'}
                </button>
                <button onClick={() => del(s.id)} title="Excluir slide" style={{ display: 'inline-flex', alignItems: 'center', padding: 6, borderRadius: 8, border: '1px solid rgba(214,58,47,0.25)', background: 'rgba(214,58,47,0.06)', color: '#D63A2F', cursor: 'pointer' }}><Trash2 size={14} /></button>
              </div>
              {/* Tipo: Texto ou Imagem */}
              <div style={{ display: 'inline-flex', gap: 4, padding: 3, background: 'rgba(15,23,42,0.05)', borderRadius: 10, marginBottom: 10 }}>
                {[{ k: 'texto', t: 'Texto' }, { k: 'imagem', t: 'Imagem' }].map(o => (
                  <button key={o.k} onClick={() => upd(s.id, { tipo: o.k })} style={{ padding: '5px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 800, fontFamily: SAMSUNG, background: s.tipo === o.k ? '#1428A0' : 'transparent', color: s.tipo === o.k ? '#fff' : '#64748b' }}>{o.t}</button>
                ))}
              </div>
              <div style={{ marginBottom: 9 }}>
                <p style={lbl}>{s.tipo === 'imagem' ? 'Título (opcional)' : 'Título'}</p>
                <input style={inp} value={s.titulo} onChange={e => upd(s.id, { titulo: e.target.value })} placeholder={s.tipo === 'imagem' ? 'Ex: Ranking Samsung (opcional)' : 'Ex: Plano de ação'} />
              </div>
              <div style={{ marginBottom: 9 }}>
                <p style={lbl}>Subtítulo (opcional)</p>
                <input style={inp} value={s.subtitulo} onChange={e => upd(s.id, { subtitulo: e.target.value })} placeholder="Ex: Junho/2026" />
              </div>
              {s.tipo === 'texto' ? (
                <div style={{ marginBottom: 9 }}>
                  <p style={lbl}>Tópicos (um por linha)</p>
                  <textarea style={{ ...inp, minHeight: 82, resize: 'vertical', fontWeight: 500, lineHeight: 1.4 }} value={s.bulletsText} onChange={e => upd(s.id, { bulletsText: e.target.value })} placeholder={'Reduzir backlog em 20%\nBater meta de MX\nRevisar aprovações da Linha Branca'} />
                </div>
              ) : (
                <div style={{ marginBottom: 9 }}>
                  <p style={lbl}>Imagem</p>
                  {s.imagem && (
                    <img src={s.imagem} alt="prévia" style={{ display: 'block', maxWidth: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 12, border: '1px solid #e8edf3', marginBottom: 8, background: '#fff' }} />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 9, border: '1px solid #d9dde3', background: '#fff', color: '#1428A0', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
                      <Plus size={14} /> {s.imagem ? 'Trocar imagem' : 'Escolher imagem'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { onPickImage(s.id, e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                    {s.imagem && (
                      <button onClick={() => upd(s.id, { imagem: '' })} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 10px', borderRadius: 9, border: '1px solid rgba(214,58,47,0.25)', background: 'rgba(214,58,47,0.06)', color: '#D63A2F', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}><Trash2 size={13} /> Remover</button>
                    )}
                  </div>
                  <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>A imagem fica centralizada, com bordas arredondadas. É reduzida automaticamente.</p>
                </div>
              )}
              <div>
                <p style={lbl}>Aparecer depois de</p>
                <select style={{ ...inp, cursor: 'pointer' }} value={s.after_key} onChange={e => upd(s.id, { after_key: e.target.value })}>
                  <option value="">No início da apresentação</option>
                  {(anchors || []).map(a => <option key={a.key} value={a.key}>{a.titulo}</option>)}
                </select>
              </div>
            </div>
          ))}
          <button onClick={add} style={{ padding: '10px 14px', borderRadius: 10, border: '1px dashed #94a3b8', background: 'rgba(20,40,160,0.03)', color: '#1428A0', fontSize: 13, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Plus size={15} /> Adicionar slide
          </button>
        </div>

        <div style={{ padding: '12px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(15,23,42,0.08)', background: 'rgba(15,23,42,0.04)', color: '#334155', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1428A0', color: '#fff', fontSize: 12, fontWeight: 800, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
}

// =================== HEADCOUNT MODAL ===================

function HeadcountModal({ year, month, entries, companies, onSaved, onClose }) {
  const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  const [forms, setForms] = useState(() => {
    const initial = {};
    companies.forEach(c => {
      const e = entries[c.slug] || {};
      initial[c.slug] = {
        recepcionistas: e.recepcionistas ?? 0,
        tecnicos_mx: e.tecnicos_mx ?? 0,
        tecnicos_lb: e.tecnicos_lb ?? 0,
        tecnicos_lm: e.tecnicos_lm ?? 0,
        tecnicos_lm_lb: e.tecnicos_lm_lb ?? 0,
        adm_in_home: e.adm_in_home ?? 0,
      };
    });
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const set = (slug, field, val) => setForms(f => ({ ...f, [slug]: { ...f[slug], [field]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      for (const c of companies) {
        const f = forms[c.slug];
        await saveHeadcount({
          loja_slug: c.slug, year, month,
          recepcionistas: parseFloat(String(f.recepcionistas).replace(',', '.')) || 0,
          tecnicos_mx: parseFloat(String(f.tecnicos_mx).replace(',', '.')) || 0,
          tecnicos_lb: parseFloat(String(f.tecnicos_lb).replace(',', '.')) || 0,
          tecnicos_lm: parseFloat(String(f.tecnicos_lm).replace(',', '.')) || 0,
          tecnicos_lm_lb: parseFloat(String(f.tecnicos_lm_lb).replace(',', '.')) || 0,
          adm_in_home: parseFloat(String(f.adm_in_home).replace(',', '.')) || 0,
        }, 'admin');
      }
      onSaved();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 7,
    border: '1px solid #cbd5e1', background: '#f4f6fb', color: '#0f172a',
    fontSize: 13, fontWeight: 700, fontFamily: monoFont, textAlign: 'center', outline: 'none',
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 90,
      background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        border: '1px solid rgba(100,116,139,0.40)', borderRadius: 16,
        width: '100%', maxWidth: 760, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Users size={18} color="#64748b" />
            <p style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Equipe — {MESES[month - 1]}/{year}</p>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{
            background: 'rgba(15,23,42,0.08)', border: 'none', borderRadius: 8,
            padding: 6, cursor: 'pointer', color: '#334155',
          }}><X size={16} /></button>
        </div>

        <div style={{ padding: '14px 22px 4px', fontSize: 11.5, color: '#475569' }}>
          Preencha por loja. Produtividade = total / quantidade. <b>Técnicos Híbridos (LM e LB)</b> = quantos técnicos de In Home atendem as duas linhas (não conte também em LM/LB).
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '8px 22px 18px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 6px', textAlign: 'left', color: '#64748b', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', borderBottom: '1px solid #e2e8f0' }}>Loja</th>
                <th style={{ padding: '8px 6px', textAlign: 'center', color: '#334155', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', borderBottom: '1px solid #e2e8f0' }}>
                  <Users size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />Recepcionistas
                </th>
                <th style={{ padding: '8px 6px', textAlign: 'center', color: '#be185d', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', borderBottom: '1px solid #e2e8f0' }}>
                  <UserCheck size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />Técnicos MX
                </th>
                <th style={{ padding: '8px 6px', textAlign: 'center', color: '#15803d', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', borderBottom: '1px solid #e2e8f0' }}>
                  <UserCheck size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />Técnicos LB
                </th>
                <th style={{ padding: '8px 6px', textAlign: 'center', color: '#b45309', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', borderBottom: '1px solid #e2e8f0' }}>
                  <UserCheck size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />Técnicos LM
                </th>
                <th style={{ padding: '8px 6px', textAlign: 'center', color: '#7c3aed', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', borderBottom: '1px solid #e2e8f0' }}>
                  <UserCheck size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />Técnicos Híbridos (LM e LB)
                </th>
                <th style={{ padding: '8px 6px', textAlign: 'center', color: '#0e7490', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', borderBottom: '1px solid #e2e8f0' }}>
                  <Users size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />ADMs In Home
                </th>
              </tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.slug}>
                  <td style={{ padding: '10px 6px', color: '#0f172a', fontWeight: 700, borderBottom: '1px solid rgba(226,232,240,0.8)' }}>{c.name || c.label}</td>
                  <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(226,232,240,0.8)', width: 120 }}>
                    <input type="number" min="0" step="0.5" value={forms[c.slug]?.recepcionistas ?? 0} onChange={e => set(c.slug, 'recepcionistas', e.target.value)} style={inputStyle} placeholder="ex: 7 ou 7,5" />
                  </td>
                  <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(226,232,240,0.8)', width: 120 }}>
                    <input type="number" min="0" step="0.5" value={forms[c.slug]?.tecnicos_mx ?? 0} onChange={e => set(c.slug, 'tecnicos_mx', e.target.value)} style={inputStyle} placeholder="ex: 3 ou 3,5" />
                  </td>
                  <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(226,232,240,0.8)', width: 120 }}>
                    <input type="number" min="0" step="0.5" value={forms[c.slug]?.tecnicos_lb ?? 0} onChange={e => set(c.slug, 'tecnicos_lb', e.target.value)} style={inputStyle} placeholder="ex: 2 ou 2,5" />
                  </td>
                  <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(226,232,240,0.8)', width: 120 }}>
                    <input type="number" min="0" step="0.5" value={forms[c.slug]?.tecnicos_lm ?? 0} onChange={e => set(c.slug, 'tecnicos_lm', e.target.value)} style={inputStyle} placeholder="ex: 2 ou 2,5" />
                  </td>
                  <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(226,232,240,0.8)', width: 120 }}>
                    <input type="number" min="0" step="0.5" value={forms[c.slug]?.tecnicos_lm_lb ?? 0} onChange={e => set(c.slug, 'tecnicos_lm_lb', e.target.value)} style={inputStyle} placeholder="fazem os dois" />
                  </td>
                  <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(226,232,240,0.8)', width: 120 }}>
                    <input type="number" min="0" step="0.5" value={forms[c.slug]?.adm_in_home ?? 0} onChange={e => set(c.slug, 'adm_in_home', e.target.value)} style={inputStyle} placeholder="ADMs do In Home" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '12px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(15,23,42,0.08)',
            background: 'rgba(15,23,42,0.04)', color: '#334155',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>Cancelar</button>
          <button onClick={save} disabled={saving} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: 'linear-gradient(180deg, #64748b, #475569)', color: '#fff',
            fontSize: 12, fontWeight: 800, cursor: saving ? 'wait' : 'pointer',
            boxShadow: '0 4px 12px rgba(71,85,105,0.35)',
          }}>
            {saving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={13} />}
            {saving ? 'Salvando…' : 'Salvar tudo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =================== SLIDE DE COMPARAÇÃO (Modo Apresentação) ===================
// Um insight por slide. NÃO recalcula nada: recebe um `model` já montado a partir
// dos MESMOS summaries/extras dos cards (ver buildPresentModel). Aqui é só layout:
// barras horizontais ranqueadas (número gigante), selo do líder, linha de insight
// e uma tabela de comparação secundária. Animações suaves (fade+translate na
// entrada via key; barras crescem via CSS transition). Respeita prefers-reduced-motion.

// Formata um valor de submétrica conforme seu tipo (reusa os mesmos helpers dos cards).
// Decimal SEMPRE em pt-BR (vírgula): 96.6 → "96,6".
const d1BR = (v) => (v == null || !isFinite(v)) ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function fmtSec(v, fmt) {
  if (v === null || v === undefined || !isFinite(v)) return '—';
  if (fmt === 'brl') return fmtBRL(v);
  if (fmt === 'ms') return fmtMS(v);
  if (fmt === 'pct1') return `${d1BR(v)}%`;
  if (fmt === 'pct0') return `${v.toFixed(0)}%`;
  if (fmt === 'dec1') return d1BR(v);
  return fmtNum(v); // 'num' (inteiro pt-BR)
}

// Farol de status vs alvo (regra dos 10%): verde ≥ alvo · amarelo até 10% · vermelho pior.
function statusFarol(v, target, lowerBetter = false) {
  if (v == null || !isFinite(v) || target == null || !isFinite(target) || target <= 0) return null;
  if (lowerBetter) return v <= target ? 'green' : (v <= target * 1.10 ? 'amber' : 'red');
  return v >= target ? 'green' : (v >= target * 0.90 ? 'amber' : 'red');
}
const TONE = {
  green:   { brand: '#00C3B2', txt: '#008577', bg: 'rgba(0,195,178,0.08)',   border: 'rgba(0,195,178,0.18)' },
  amber:   { brand: '#FFB546', txt: '#B26A00', bg: 'rgba(255,181,70,0.08)',  border: 'rgba(255,181,70,0.22)' },
  red:     { brand: '#EF4444', txt: '#B91C1C', bg: 'rgba(239,68,68,0.07)',   border: 'rgba(239,68,68,0.20)' },
  neutral: { brand: '#1428A0', txt: '#1428A0', bg: 'rgba(20,40,160,0.05)',   border: 'rgba(20,40,160,0.14)' },
};

// ===== Cards com farol HONESTO =====
// Verde SÓ quando a meta/esperado foi de fato atingido. Sem meta → tom neutro
// (comparativo). Ninguém na meta → "Mais próxima do esperado" (amarelo).
// Diferença pequena entre as 2 melhores → "Resultados semelhantes" (sem ranking forte).
function cardsDesempenho(stores, { value, fmt, lowerBetter = false, target = null,
  kickerSemMeta = 'Melhor resultado', kickerAtencao = 'Merece atenção', atencaoNota = null }) {
  const arr = stores.map(s => ({ s, v: value(s), t: typeof target === 'function' ? target(s) : target }))
    .filter(x => x.v != null && isFinite(x.v));
  if (!arr.length) return [];
  arr.sort((a, b) => lowerBetter ? a.v - b.v : b.v - a.v);
  const best = arr[0], worst = arr[arr.length - 1];
  const cards = [];
  const temMeta = arr.some(x => x.t != null && isFinite(x.t) && x.t > 0);
  // Resultados muito próximos (≤5% relativo entre 1º e 2º) → sem ranking forte.
  const parecidos = arr.length >= 2 && best.v !== 0 && Math.abs(best.v - arr[1].v) / Math.abs(best.v) <= 0.05;
  if (temMeta) {
    const st = statusFarol(best.v, best.t, lowerBetter);
    if (st === 'green') cards.push({ kicker: lowerBetter ? 'Dentro da meta' : 'Meta atingida', tone: 'green', store: best.s, value: best.v, fmt, sub: parecidos ? 'Resultados semelhantes entre as líderes' : null });
    else cards.push({ kicker: 'Mais próxima do esperado', tone: 'amber', store: best.s, value: best.v, fmt, sub: 'Nenhuma unidade atingiu o esperado' });
    if (worst.s !== best.s) {
      const stW = statusFarol(worst.v, worst.t, lowerBetter);
      cards.push({ kicker: kickerAtencao, tone: stW === 'red' ? 'red' : 'amber', store: worst.s, value: worst.v, fmt, sub: atencaoNota });
    }
  } else {
    if (parecidos) cards.push({ kicker: 'Resultados semelhantes', tone: 'neutral', store: best.s, value: best.v, fmt, sub: `${best.s.name} e ${arr[1].s.name} praticamente empatadas` });
    else cards.push({ kicker: kickerSemMeta, tone: 'neutral', store: best.s, value: best.v, fmt });
    if (worst.s !== best.s && !parecidos) cards.push({ kicker: kickerAtencao, tone: 'amber', store: worst.s, value: worst.v, fmt, sub: atencaoNota });
  }
  return cards;
}

// Formatação "grossa" usada DURANTE a contagem: sem casas decimais / centavos
// (evita a vírgula tremendo enquanto sobe). O valor exato (com vírgula) só
// aparece quando a animação termina.
function fmtSecCoarse(v, fmt) {
  if (v === null || v === undefined || !isFinite(v)) return '—';
  if (fmt === 'brl') return fmtBRL(v).replace(/,\d{2}$/, ''); // "R$ 31.304"
  if (fmt === 'ms') return fmtMS(v);
  // floor (não round) durante a subida: nunca passa do alvo nem muda nº de dígitos no fim.
  if (fmt === 'pct1' || fmt === 'pct0') return `${Math.floor(v)}%`;
  return fmtNum(Math.floor(v));
}

// Count-up suave (0 → valor) via requestAnimationFrame. Devolve {val, done}.
// duration longa = sobe devagar (suave, não "bugado").
// PDF dos slides (14/10/2026): com o contexto ligado os slides saem ESTÁTICOS — número já no
// valor final e barra já no tamanho final (sem a contagem de 2,4 s que a apresentação faz).
const SlidesEstaticosContext = React.createContext(false);

function useCountUp(target, duration = 2400) {
  const estatico = React.useContext(SlidesEstaticosContext);
  const [state, setState] = useState(() => (estatico ? { val: target, done: true } : { val: 0, done: false }));
  useEffect(() => {
    if (estatico) { setState({ val: target, done: true }); return; }
    if (typeof target !== 'number' || !isFinite(target)) { setState({ val: target, done: true }); return; }
    if (target === 0) { setState({ val: target, done: true }); return; }
    setState({ val: 0, done: false });
    let raf; const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 4); // easeOutQuart (desacelera bem suave no fim)
      if (t < 1) { setState({ val: target * eased, done: false }); raf = requestAnimationFrame(tick); }
      else setState({ val: target, done: true });
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, estatico]);
  return state;
}

// Número que conta de 0 até o valor — SUAVE, sem tremor:
//  - tabular-nums: todos os dígitos têm a mesma largura (sem jitter ao trocar).
//  - sizer invisível com o valor FINAL: reserva a largura definitiva, então o
//    layout (grid/barra) nunca reflui durante a contagem.
//  - durante a subida mostra sem decimais (fmtSecCoarse); no fim, exato (fmtSec).
function CountNumber({ value, fmt, style }) {
  const { val, done } = useCountUp(value);
  const finalStr = fmtSec(value, fmt);
  const shown = done ? finalStr : fmtSecCoarse(val, fmt);
  return (
    <span style={{ ...style, position: 'relative', display: 'inline-block', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1' }}>
      <span style={{ visibility: 'hidden' }}>{finalStr}</span>
      <span style={{ position: 'absolute', right: 0, top: 0 }}>{shown}</span>
    </span>
  );
}
function PresentationSlide({ model }) {
  // Barras crescem de 0 → valor ao montar (re-monta por slide via key). No PDF já nascem prontas.
  const estatico = React.useContext(SlidesEstaticosContext);
  const [grown, setGrown] = useState(estatico);
  useEffect(() => {
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setGrown(true)));
    return () => cancelAnimationFrame(r);
  }, []);

  if (!model || !model.stores || model.stores.length === 0) {
    return (
      <div style={{ margin: 'auto', textAlign: 'center', color: '#94a3b8', fontSize: 32.5, fontWeight: 700 }}>
        Sem dados para comparar nesta métrica.
      </div>
    );
  }

  const { titulo, subtitulo, accent, primaryLabel, primaryFmt, lowerIsBetterPrimary,
          stores, secundarios } = model;

  const ranked = [...stores].sort((a, b) => {
    const va = a.primaryValue ?? null, vb = b.primaryValue ?? null;
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return lowerIsBetterPrimary ? va - vb : vb - va;
  });
  const leader = ranked.find(s => s.primaryValue != null) || ranked[0];
  const worst = [...ranked].reverse().find(s => s.primaryValue != null && s.slug !== leader?.slug);
  const maxAbs = Math.max(1, ...stores.map(s => Math.abs(s.primaryValue ?? 0)));

  // "Merece atenção" baseado num indicador de DESEMPENHO (não em volume): quando o
  // modelo define attentionRowLabel, usa o PIOR daquela linha; senão, pior do primário.
  const attRow = model.attentionRowLabel
    ? (secundarios || []).find(r => r.label === model.attentionRowLabel)
    : null;
  let attStore = null, attValue = null, attFmt = primaryFmt;
  if (attRow) {
    const arr = stores.map(s => ({ s, v: attRow.valores[s.slug] })).filter(x => x.v != null && isFinite(x.v));
    arr.sort((a, b) => attRow.lowerBetter ? b.v - a.v : a.v - b.v); // pior primeiro
    if (arr[0]) { attStore = arr[0].s; attValue = arr[0].v; attFmt = attRow.fmt; }
  } else if (worst && worst.primaryValue != null) {
    attStore = worst; attValue = worst.primaryValue;
  }

  // "Melhor desempenho": quando há métrica de DESEMPENHO (attentionRowLabel), usa o
  // MELHOR dela — NÃO o líder de volume. Ex: Balcão → melhor backlog %, não mais entradas.
  let melStore = null, melValue = null, melFmt = primaryFmt;
  if (attRow) {
    const arr = stores.map(s => ({ s, v: attRow.valores[s.slug] })).filter(x => x.v != null && isFinite(x.v));
    arr.sort((a, b) => attRow.lowerBetter ? a.v - b.v : b.v - a.v); // melhor primeiro
    if (arr[0]) { melStore = arr[0].s; melValue = arr[0].v; melFmt = attRow.fmt; }
  } else if (leader && leader.primaryValue != null) {
    melStore = leader; melValue = leader.primaryValue;
  }

  // 3º card ("destaque"): prioriza vs Mês Anterior / Atingimento / Conversão;
  // senão, a 1ª linha de DESEMPENHO (colorize). Nunca volumetria, e NUNCA a mesma
  // linha do card "Merece atenção" (evita card duplicado).
  const growthRow = (secundarios || []).find(r => /vs |atingimento|cresc|convers/i.test(r.label) && r !== attRow)
    || (secundarios || []).find(r => r.colorize && r !== attRow);
  let growthBest = null;
  if (growthRow) {
    const arr = stores.map(s => ({ s, v: growthRow.valores[s.slug] }))
      .filter(x => x.v != null && isFinite(x.v));
    arr.sort((a, b) => growthRow.lowerBetter ? a.v - b.v : b.v - a.v);
    growthBest = arr[0] || null;
  }

  // Paleta Samsung p/ os cards de destaque (Teal / Saffron / Clover).
  const HL = {
    melhor:  { brand: '#00C3B2', txt: '#008577', bg: 'rgba(0,195,178,0.08)',  border: 'rgba(0,195,178,0.18)' },
    atencao: { brand: '#FFB546', txt: '#B26A00', bg: 'rgba(255,181,70,0.08)', border: 'rgba(255,181,70,0.22)' },
    cresc:   { brand: '#97D653', txt: '#558B2F', bg: 'rgba(151,214,83,0.10)', border: 'rgba(151,214,83,0.24)' },
  };
  let highlights = [];
  if (melStore && melValue != null) {
    highlights.push({ icon: Crown, kicker: 'Melhor desempenho', c: HL.melhor, store: melStore,
      valueNode: <CountNumber value={melValue} fmt={melFmt} style={{}} /> });
  }
  if (attStore && attValue != null) {
    highlights.push({ icon: AlertTriangle, kicker: model.attentionKicker || 'Merece atenção', c: HL.atencao, store: attStore,
      valueNode: <CountNumber value={attValue} fmt={attFmt} style={{}} /> });
  }
  if (growthBest && growthRow) {
    highlights.push({ icon: TrendingUp, kicker: growthRow.label, c: HL.cresc, store: growthBest.s,
      valueNode: <CountNumber value={growthBest.v} fmt={growthRow.fmt} style={{}} /> });
  }
  // ===== Cards com FAROL HONESTO (model.cards, quando o slide define) =====
  // Verde = meta atingida de verdade · Amarelo = mais próxima do esperado ·
  // Vermelho = desvio relevante · Neutro = comparativo sem meta cadastrada.
  if (model.cards) {
    const ICON_BY_TONE = { green: Crown, amber: AlertTriangle, red: AlertTriangle, neutral: TrendingUp };
    highlights = model.cards.slice(0, 3).map(c => ({
      icon: ICON_BY_TONE[c.tone] || TrendingUp,
      kicker: c.kicker,
      c: TONE[c.tone] || TONE.neutral,
      store: c.store,
      sub: c.sub,
      valueNode: <CountNumber value={c.value} fmt={c.fmt} style={{}} />,
    }));
  }

  // Fonte Samsung (mesma ordem de prioridade pedida).
  const SAMSUNG = "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif";
  // Entrada: fade + slide-up 350ms (sem gate de reduced-motion — animação é o ponto da apresentação).
  const fadeUp = (delay) => ({ animation: `psUp 0.35s cubic-bezier(0.22,1,0.36,1) both`, animationDelay: `${delay}ms` });

  return (
    <div style={{ width: '100%', maxWidth: 1860, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 26, padding: '8px 4px 24px', fontFamily: SAMSUNG }}>
      <style>{`
        @keyframes psUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .ps-bar { transition: width 2.2s cubic-bezier(0.22,1,0.36,1); }
        .ps-hl { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .ps-hl:hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(20,40,160,0.10); }
      `}</style>

      {/* HEADER */}
      <div style={fadeUp(0)}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {model.bloco && (
                <span style={{ fontSize: 17, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.16em', background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 6, padding: '3px 10px' }}>{model.bloco}</span>
              )}
            </div>
            <h1 style={{ fontSize: 70, fontWeight: 700, color: '#1428A0', letterSpacing: '-0.03em', lineHeight: 0.98, margin: model.bloco ? '8px 0 0' : 0 }}>{titulo}</h1>
            <p style={{ fontSize: 25.5, fontWeight: 400, color: '#1428A0', opacity: 0.6, margin: '9px 0 0' }}>{subtitulo}</p>
            {model.headerChips && model.headerChips.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {model.headerChips.map((chip, ci) => (
                  <span key={ci} style={{ fontSize: 19.5, fontWeight: 700, color: '#475569', background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.09)', borderRadius: 999, padding: '4px 12px' }}>{chip}</span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '8px 16px', borderRadius: 999, background: 'rgba(20,40,160,0.07)', border: '1px solid rgba(20,40,160,0.16)' }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#1428A0', textTransform: 'uppercase', letterSpacing: '0.14em' }}>{primaryLabel}</span>
              {lowerIsBetterPrimary && <span style={{ fontSize: 19, fontWeight: 400, color: '#1428A0', opacity: 0.6 }}>· menor é melhor</span>}
            </div>
            {model.metaNota && (
              <span style={{ fontSize: 18, fontWeight: 600, color: '#94a3b8', maxWidth: 420, textAlign: 'right' }}>{model.metaNota}</span>
            )}
          </div>
        </div>
      </div>

      {/* NOTA DE CONTEXTO (rota da FIX MSC, backlog abs × %, canal abaixo do ritmo, taxas >100%) */}
      {model.nota && (
        <div style={{ ...fadeUp(40), display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', borderRadius: 12, background: 'rgba(255,181,70,0.09)', border: '1px solid rgba(255,181,70,0.30)' }}>
          <Info size={17} color="#B26A00" style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: '#7c4a03', lineHeight: 1.45 }}>{model.nota}</span>
        </div>
      )}

      {/* CONSOLIDADO DO CANAL (vendas): o resultado do canal vem ANTES das unidades */}
      {model.consolidado && (
        <div style={{ ...fadeUp(50), display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', padding: '18px 22px', borderRadius: 16, background: model.consolidado.alerta ? 'rgba(239,68,68,0.06)' : (model.consolidado.ritmo >= 100 ? 'rgba(0,195,178,0.07)' : 'rgba(20,40,160,0.05)'), border: `1px solid ${model.consolidado.alerta ? 'rgba(239,68,68,0.25)' : (model.consolidado.ritmo >= 100 ? 'rgba(0,195,178,0.22)' : 'rgba(20,40,160,0.14)')}` }}>
          <div>
            <p style={{ fontSize: 19, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>{model.consolidado.titulo}</p>
            <p style={{ fontSize: 34.5, fontWeight: 800, color: model.consolidado.alerta ? '#B91C1C' : (model.consolidado.ritmo >= 100 ? '#008577' : '#1428A0'), margin: '6px 0 0' }}>{model.consolidado.frase}</p>
          </div>
          <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
            <div><p style={{ fontSize: 16, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Realizado</p><p style={{ fontSize: 28, fontWeight: 800, color: model.consolidado.ritmo >= 100 ? '#008577' : (model.consolidado.ritmo >= 90 ? '#B45309' : '#B91C1C'), margin: '2px 0 0', fontFamily: monoFont }}>{fmtBRL(model.consolidado.realizado)}</p></div>
            <div><p style={{ fontSize: 16, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Esperado até hoje</p><p style={{ fontSize: 28, fontWeight: 800, color: '#1F2A44', margin: '2px 0 0', fontFamily: monoFont }}>{fmtBRL(model.consolidado.esperado)}</p></div>
          </div>
        </div>
      )}

      {/* HERÓI — MODELO 1 (Recepção): NÍVEL DE SERVIÇO (TME/TMA), não volume */}
      {model.template === 'servico' && (
        <div style={{ ...fadeUp(60), display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 18 }}>
          {[{ key: 'tme', label: 'TME — Tempo Médio de Espera', prevKey: 'tmePrev' }, { key: 'tma', label: 'TMA — Tempo Médio de Atendimento', prevKey: 'tmaPrev' }].map(panel => {
            const ordenado = [...stores].filter(s => s[panel.key] != null && isFinite(s[panel.key])).sort((a, b) => a[panel.key] - b[panel.key]);
            const semDado = stores.filter(s => s[panel.key] == null || !isFinite(s[panel.key]));
            return (
              <div key={panel.key} style={{ background: 'linear-gradient(180deg,#ffffff,#f8fafc)', border: '1px solid #e8edf3', borderRadius: 16, padding: '18px 22px', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
                <p style={{ fontSize: 19.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.10em', margin: '0 0 14px' }}>{panel.label} <span style={{ color: '#A0AEC0', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>· menor é melhor</span></p>
                {[...ordenado, ...semDado].map((s, i) => {
                  const v = s[panel.key];
                  const prev = s[panel.prevKey];
                  const deltaPct = (v != null && prev > 0) ? ((v - prev) / prev) * 100 : null;
                  const melhorou = deltaPct != null && deltaPct < 0;
                  return (
                    <div key={s.slug} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #eef2f6' }}>
                      <span style={{ width: 11, height: 11, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 29, fontWeight: 700, color: '#1F2A44', minWidth: 92 }}>{s.name}</span>
                      {i === 0 && v != null && <Crown size={16} color={s.color} fill={s.color} />}
                      <span style={{ flex: 1 }} />
                      {deltaPct != null && (
                        <span style={{ fontSize: 19.5, fontWeight: 700, color: melhorou ? '#008577' : '#B91C1C' }}>
                          {melhorou ? '▼' : '▲'} {d1BR(Math.abs(deltaPct))}% vs mês ant.
                        </span>
                      )}
                      <span style={{ fontSize: 40.5, fontWeight: 800, color: v == null ? '#A0AEC0' : (i === 0 ? '#008577' : '#1F2A44'), fontFamily: monoFont, letterSpacing: '-0.02em' }}>{v == null ? '—' : fmtMS(v)}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* RECEPÇÃO: VOLUME em destaque — embasa decisão de equipe (contratar/reduzir) */}
      {model.template === 'servico' && model.volumePanel && (
        <div style={{ ...fadeUp(120), background: 'linear-gradient(180deg,#ffffff,#f8fafc)', border: '1px solid #e8edf3', borderRadius: 16, padding: '16px 22px', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
          <p style={{ fontSize: 19.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.10em', margin: '0 0 12px' }}>{model.volumePanel.titulo} <span style={{ color: '#A0AEC0', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>· comparado ao mês anterior</span></p>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, model.volumePanel.itens.length)}, 1fr)`, gap: 18 }}>
            {model.volumePanel.itens.map(it => (
              <div key={it.slug}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 21, fontWeight: 700, color: '#64748b' }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: it.color }} />{it.name}
                </span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 3 }}>
                  <span style={{ fontSize: 46, fontWeight: 800, color: it.qtd == null ? '#A0AEC0' : '#1F2A44', fontFamily: monoFont, letterSpacing: '-0.02em' }}>{it.qtd == null ? '—' : fmtNum(it.qtd)}</span>
                  {it.delta != null && isFinite(it.delta) && (
                    <span style={{ fontSize: 22.5, fontWeight: 800, color: it.delta >= 0 ? '#008577' : '#B91C1C' }}>
                      {it.delta >= 0 ? '▲' : '▼'} {d1BR(Math.abs(it.delta))}%
                    </span>
                  )}
                </div>
                {it.porRecep != null && isFinite(it.porRecep) && (
                  <p style={{ fontSize: 18, color: '#94a3b8', margin: '2px 0 0' }}>{d1BR(it.porRecep)} senhas por recepcionista</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BARRAS GIGANTES (o herói) */}
      {model.template !== 'servico' && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {ranked.map((s, i) => {
          const v = s.primaryValue ?? null;
          const widthPct = v === null ? 0 : Math.max(3, (Math.abs(v) / maxAbs) * 100);
          const isLeader = leader && s.slug === leader.slug && v !== null;
          // Barra verde/âmbar/vermelho vs a META/ESPERADO. Verde ≥ alvo · âmbar até 10% abaixo · vermelho pior.
          //  - primaryVsTarget[slug]: alvo POR LOJA (aprovações, Store+/SMB → esperado da loja).
          //  - barStatusAll: status ÚNICO p/ todas as barras (E-commerce: ritmo do SITE, meta geral).
          const STATUS = {
            green: ['#00C3B2', '#009E90', '#008577'],
            amber: ['#FACC15', '#EAB308', '#A16207'],
            red: ['#EF4444', '#C81E1E', '#DC2626'],
          };
          const pTgt = model.primaryVsTarget ? model.primaryVsTarget[s.slug] : null;
          let barFill = s.color, barDeep = `${s.color}d9`, numColor = s.color;
          let st = null;
          if (model.barStatusAll && STATUS[model.barStatusAll]) {
            st = model.barStatusAll;
          } else if (pTgt != null && isFinite(pTgt) && pTgt > 0 && v != null) {
            st = v >= pTgt ? 'green' : (v >= pTgt * 0.90 ? 'amber' : 'red');
          } else if (model.primaryStatus && STATUS[model.primaryStatus[s.slug]] && v != null) {
            // Farol COMPARATIVO entre lojas (sem meta): verde = melhor do grupo.
            st = model.primaryStatus[s.slug];
          }
          if (st) { [barFill, barDeep, numColor] = STATUS[st]; }
          return (
            <div key={s.slug} style={fadeUp(60 + i * 70)}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, max-content) minmax(0, 1fr) max-content', alignItems: 'center', gap: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: s.color, boxShadow: `0 0 12px ${s.color}66`, flexShrink: 0 }} />
                  <span style={{ fontSize: 33.5, fontWeight: 700, color: '#1F2A44', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{s.name}</span>
                  {isLeader && <Crown size={19} color={s.color} fill={s.color} style={{ flexShrink: 0 }} />}
                </div>
                <div style={{ height: 56, borderRadius: 14, background: '#E8ECF2', overflow: 'hidden', position: 'relative' }}>
                  <div className="ps-bar" style={{
                    height: '100%', width: grown ? `${widthPct}%` : '0%', borderRadius: 14,
                    background: `linear-gradient(90deg, ${barFill} 0%, ${barDeep} 100%)`,
                    boxShadow: isLeader ? `0 2px 14px ${barFill}3d` : 'none',
                    opacity: v === null ? 0.25 : 1,
                  }} />
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {v === null
                    ? <span style={{ fontSize: 59.5, fontWeight: 700, color: '#A0AEC0', fontFamily: SAMSUNG }}>—</span>
                    : <CountNumber value={v} fmt={primaryFmt} style={{ fontSize: 61.5, fontWeight: 700, color: numColor, fontFamily: SAMSUNG, letterSpacing: '-0.03em', lineHeight: 1 }} />}
                  {v !== null && model.primarySub && model.primarySub[s.slug] && (
                    <div style={{ fontSize: 25, fontWeight: 700, color: '#475569', marginTop: 5, fontFamily: monoFont }}>{model.primarySub[s.slug]}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* MODELO 3 — VOLUME (entradas → reparos) em destaque + backlog ABSOLUTO.
          O backlog proporcional NÃO se repete aqui: já é a barra do topo. */}
      {model.fluxo && (
        <div style={{ ...fadeUp(140), display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 18 }}>
          <div style={{ background: 'linear-gradient(180deg,#ffffff,#f8fafc)', border: '2px solid rgba(20,40,160,0.22)', borderRadius: 16, padding: '16px 22px', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
            <p style={{ fontSize: 19.5, fontWeight: 800, color: '#1428A0', textTransform: 'uppercase', letterSpacing: '0.10em', margin: 0 }}>Entradas e reparos no período</p>
            <p style={{ fontSize: 17, color: '#94a3b8', margin: '3px 0 12px' }}>quanto entrou e quanto foi resolvido</p>
            <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
              {(model.fluxo.volume || []).map(it => (
                <div key={it.slug} style={{ minWidth: 120 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 19.5, fontWeight: 700, color: '#64748b' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: it.color }} />{it.name}
                  </span>
                  <p style={{ fontSize: 34.5, fontWeight: 800, color: it.entradas == null ? '#A0AEC0' : '#1F2A44', fontFamily: monoFont, margin: '3px 0 0' }}>
                    {it.entradas == null ? '—' : fmtNum(it.entradas)} <span style={{ fontSize: 19, fontWeight: 700, color: '#94a3b8' }}>entradas</span>
                  </p>
                  <p style={{ fontSize: 30, fontWeight: 800, color: it.reparos == null ? '#A0AEC0' : '#008577', fontFamily: monoFont, margin: '2px 0 0' }}>
                    {it.reparos == null ? '—' : fmtNum(it.reparos)} <span style={{ fontSize: 19, fontWeight: 700, color: '#94a3b8' }}>reparos</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: 'linear-gradient(180deg,#ffffff,#f8fafc)', border: '1px solid #e8edf3', borderRadius: 16, padding: '16px 22px', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
            <p style={{ fontSize: 19.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.10em', margin: 0 }}>Backlog atual (OS)</p>
            <p style={{ fontSize: 17, color: '#94a3b8', margin: '3px 0 12px' }}>quantidade absoluta — ler junto com o proporcional (barra acima)</p>
            <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
              {stores.map(s => {
                const v = model.fluxo.backlogAbs?.[s.slug];
                return (
                  <div key={s.slug} style={{ minWidth: 96 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 19.5, fontWeight: 700, color: '#64748b' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />{s.name}
                    </span>
                    <p style={{ fontSize: 34.5, fontWeight: 800, color: (v == null || !isFinite(v)) ? '#A0AEC0' : '#1F2A44', fontFamily: monoFont, margin: '3px 0 0' }}>{fmtSec(v, 'num')}</p>
                    {(() => {
                      const pv = model.fluxo.prev?.[s.slug];
                      if (!pv || v == null || !isFinite(v)) return null;
                      const d = v - pv.total;
                      const cor = d < 0 ? '#008577' : (d > 0 ? '#B91C1C' : '#64748b');
                      const seta = d < 0 ? '▼' : (d > 0 ? '▲' : '=');
                      const [, mes, dia] = pv.data.split('-');
                      return (
                        <p style={{ fontSize: 17, fontWeight: 700, color: cor, margin: '3px 0 0' }}>
                          {seta} {d === 0 ? 'estável' : fmtNum(Math.abs(d))} vs {dia}/{mes} <span style={{ color: '#94a3b8', fontWeight: 600 }}>({fmtNum(pv.total)})</span>
                        </p>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
            {!stores.some(st => model.fluxo.prev?.[st.slug]) && (
              <p style={{ fontSize: 16, color: '#94a3b8', margin: '12px 0 0' }}>comparação com a semana anterior aparece aqui a partir do próximo snapshot semanal (histórico iniciado em 21/07)</p>
            )}
          </div>
        </div>
      )}

      {/* MODELO 2 — APROVAÇÃO em destaque: taxa vs meta + aprovadas/reprovadas (some 3 linhas da tabela) */}
      {model.aprovPanel && (
        <div style={{ ...fadeUp(150), background: 'linear-gradient(180deg,#ffffff,#f8fafc)', border: '2px solid rgba(20,40,160,0.22)', borderRadius: 16, padding: '16px 22px', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
          <p style={{ fontSize: 19.5, fontWeight: 800, color: '#1428A0', textTransform: 'uppercase', letterSpacing: '0.10em', margin: 0 }}>Taxa de aprovação</p>
          <p style={{ fontSize: 17, color: '#94a3b8', margin: '3px 0 12px' }}>colorida vs a meta de cada unidade · aprovadas e não reparadas embaixo</p>
          <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap' }}>
            {model.aprovPanel.itens.map(it => {
              const st = statusFarol(it.taxa, it.meta, false);
              const cor = st === 'green' ? '#008577' : st === 'amber' ? '#A16207' : st === 'red' ? '#C81E1E' : '#1F2A44';
              return (
                <div key={it.slug} style={{ minWidth: 150 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 19.5, fontWeight: 700, color: '#64748b' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: it.color }} />{it.name}
                  </span>
                  <p style={{ fontSize: 40.5, fontWeight: 800, color: (it.taxa == null || !isFinite(it.taxa)) ? '#A0AEC0' : cor, fontFamily: monoFont, margin: '3px 0 0', letterSpacing: '-0.02em' }}>
                    {fmtSec(it.taxa, 'pct1')} <span style={{ fontSize: 19, fontWeight: 700, color: '#94a3b8' }}>{it.meta != null ? `meta ${fmtSec(it.meta, 'pct0')}` : ''}</span>
                  </p>
                  <p style={{ fontSize: 20, fontWeight: 700, margin: '3px 0 0', fontFamily: monoFont }}>
                    <span style={{ color: '#008577' }}>{it.aprovadas == null ? '—' : fmtNum(it.aprovadas)} aprovadas</span>
                    <span style={{ color: '#cbd5e1' }}> · </span>
                    <span style={{ color: '#C81E1E' }}>{it.reprovadas == null ? '—' : fmtNum(it.reprovadas)} não reparadas</span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MODELO 2 — BLOCO FINANCEIRO separado do técnico */}
      {model.financeiro && (
        <div style={{ ...fadeUp(160), background: 'linear-gradient(180deg,#ffffff,#f8fafc)', border: '1px solid #e8edf3', borderRadius: 16, padding: '16px 22px', boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
          <p style={{ fontSize: 19.5, fontWeight: 800, color: '#1428A0', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 10px' }}>{model.financeiro.titulo}</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: monoFont }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 18, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.10em', borderBottom: '2px solid #e2e8f0', fontFamily: SAMSUNG }}>Indicador</th>
                {stores.map(s => (
                  <th key={s.slug} style={{ textAlign: 'right', padding: '8px 12px', fontSize: 20, fontWeight: 800, color: s.color, borderBottom: '2px solid #e2e8f0', fontFamily: SAMSUNG }}>{s.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.financeiro.linhas.map((ln, li) => (
                <tr key={li} style={{ background: li % 2 ? 'rgba(20,40,160,0.02)' : 'transparent' }}>
                  <td style={{ padding: '9px 12px', fontSize: 22, fontWeight: 600, color: '#1F2A44', fontFamily: SAMSUNG, borderBottom: '1px solid #eef2f6' }}>
                    {ln.label}{ln.hint && <span style={{ fontSize: 16, color: '#94a3b8', marginLeft: 6 }}>{ln.hint}</span>}
                  </td>
                  {stores.map(s => {
                    const v = ln.valores?.[s.slug];
                    const tgt = ln.farolVs?.[s.slug];
                    const st = (tgt != null && v != null) ? statusFarol(v, tgt, false) : null;
                    const cor = st === 'green' ? '#008577' : st === 'amber' ? '#A16207' : st === 'red' ? '#C81E1E' : '#1F2A44';
                    return (
                      <td key={s.slug} style={{ padding: '9px 12px', textAlign: 'right', fontSize: 23, fontWeight: 700, color: (v == null || !isFinite(v)) ? '#A0AEC0' : cor, borderBottom: '1px solid #eef2f6' }}>{fmtSec(v, ln.fmt)}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* RESUMO EXECUTIVO (cards de destaque) */}
      {highlights.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${highlights.length}, 1fr)`, gap: 16 }}>
          {highlights.map((h, i) => {
            const Ic = h.icon;
            return (
              <div key={i} className="ps-hl" style={{
                ...fadeUp(220 + i * 90),
                padding: '18px 22px', borderRadius: 16, background: h.c.bg,
                border: `1px solid ${h.c.border}`, boxShadow: '0 2px 10px rgba(20,40,160,0.04)',
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 34, height: 34, borderRadius: 10, background: `${h.c.brand}26`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Ic size={18} color={h.c.txt} />
                  </span>
                  <span style={{ fontSize: 19.5, fontWeight: 700, color: h.c.txt, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.85 }}>{h.kicker}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                    <span style={{ width: 11, height: 11, borderRadius: '50%', background: h.store.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 29.5, fontWeight: 700, color: '#1428A0' }}>{h.store.name}</span>
                  </span>
                  <span style={{ fontSize: 41.5, fontWeight: 700, color: h.store.color, fontFamily: SAMSUNG, letterSpacing: '-0.03em' }}>{h.valueNode}</span>
                </div>
                {h.sub && (
                  <span style={{ fontSize: 19.5, fontWeight: 600, color: h.c.txt, opacity: 0.9 }}>{h.sub}</span>
                )}
                {model.storeDesc && model.storeDesc[h.store.slug] && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {model.storeDesc[h.store.slug].map((line, k) => (
                      <span key={k} style={{ fontSize: 19.5, fontWeight: 600, color: h.c.txt, opacity: 0.9 }}>{line}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* TABELA COMPARATIVA */}
      {secundarios && secundarios.length > 0 && (
        <div style={fadeUp(360)}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontFamily: monoFont }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '13px 18px', fontSize: 20, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', sans-serif", borderBottom: '2px solid #e2e8f0' }}>Métrica</th>
                {stores.map(s => (
                  <th key={s.slug} style={{ textAlign: 'right', padding: '13px 18px', fontSize: 24, fontWeight: 800, color: s.color, fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', sans-serif", borderBottom: '2px solid #e2e8f0' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      {s.name}
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: s.color }} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {secundarios.filter(row => !row.oculto).map((row, ri) => {
                // Cor SÓ em indicadores de desempenho (row.colorize). Equipe, volumetria,
                // backlog absoluto, meta e esperado ficam neutros (sem verde/vermelho).
                // Dois modos de cor:
                //  - vsTarget: cada loja vs o PRÓPRIO alvo (verde ≥ alvo, vermelho abaixo). Justo p/ valores
                //    absolutos (Receita vs Esperado) e "atingiu a meta" (% vs 100 / crescimento vs 0).
                //  - senão: melhor/pior ENTRE as lojas (cross-store) — p/ taxas normalizadas.
                const target = row.colorize ? row.vsTarget : null;
                const isTargetMode = target != null;
                const tgtFor = (slug) => (typeof target === 'object' ? (target ? target[slug] : null) : target);
                const nums = stores.map(s => row.valores[s.slug]).filter(v => v != null && isFinite(v));
                const spread = row.colorize && !isTargetMode && nums.length >= 2 && Math.min(...nums) !== Math.max(...nums);
                const best = spread ? (row.lowerBetter ? Math.min(...nums) : Math.max(...nums)) : null;
                const worstV = spread ? (row.lowerBetter ? Math.max(...nums) : Math.min(...nums)) : null;
                return (
                  <tr key={ri} style={{ background: row.info ? 'rgba(99,102,241,0.07)' : (ri % 2 ? 'rgba(20,40,160,0.022)' : 'transparent') }}>
                    <td style={{ textAlign: 'left', padding: '14px 18px', fontSize: 25, fontWeight: 500, color: row.info ? '#64748b' : '#1F2A44', fontFamily: SAMSUNG, borderBottom: '1px solid #eef2f6', borderLeft: `3px solid ${row.info ? '#6366f1' : 'transparent'}` }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontStyle: row.info ? 'italic' : 'normal' }}>
                        {row.label}
                        {row.tooltip && (
                          <span title={row.tooltip} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                            <Info size={14} color="#6366f1" />
                          </span>
                        )}
                        {row.hint && <span style={{ fontSize: 17, color: '#94a3b8', fontWeight: 400 }}>{row.hint}</span>}
                        {row.lowerBetter && <span style={{ fontSize: 17, color: '#A0AEC0', fontWeight: 400 }}>↓ melhor</span>}
                      </span>
                      {row.badge && (
                        <span style={{ display: 'block', marginTop: 5, width: 'fit-content', fontSize: 16, fontWeight: 700, color: '#4f46e5', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 999, padding: '2px 10px', letterSpacing: '0.01em', fontStyle: 'normal' }}>{row.badge}</span>
                      )}
                    </td>
                    {stores.map(s => {
                      const v = row.valores[s.slug];
                      const isNum = v != null && isFinite(v);
                      let isBest = false, isWorst = false, isAmber = false;
                      if (row.colorize && isNum) {
                        if (isTargetMode) {
                          const tv = tgtFor(s.slug);
                          if (tv != null && isFinite(tv)) {
                            // ≥ alvo = verde · até 10% do alvo = âmbar · pior que isso = vermelho.
                            if (row.lowerBetter) {
                              if (v <= tv) isBest = true;
                              else if (v <= tv * 1.10) isAmber = true;
                              else isWorst = true;
                            } else {
                              if (v >= tv) isBest = true;
                              else if (v >= tv * 0.90) isAmber = true;
                              else isWorst = true;
                            }
                          }
                        } else if (spread) {
                          isBest = v === best; isWorst = v === worstV;
                        }
                      }
                      return (
                        <td key={s.slug} style={{
                          textAlign: 'right', padding: '14px 18px', fontSize: 29, fontWeight: 700,
                          color: isBest ? '#008577' : isAmber ? '#A16207' : isWorst ? '#D63A2F' : (row.info ? '#94a3b8' : '#1F2A44'),
                          background: isBest ? 'rgba(0,195,178,0.10)' : isAmber ? 'rgba(250,204,21,0.20)' : isWorst ? 'rgba(255,67,55,0.08)' : 'transparent',
                          letterSpacing: '-0.02em', borderBottom: '1px solid #eef2f6',
                        }}>
                          {isNum ? fmtSec(v, row.fmt) : '—'}
                          {isNum && row.bases && row.bases[s.slug] != null && isFinite(row.bases[s.slug]) && (
                            <span style={{ display: 'block', fontSize: 17, fontWeight: 500, color: '#94a3b8', letterSpacing: 0, marginTop: 1 }}>
                              de {fmtNum(row.bases[s.slug])}
                            </span>
                          )}
                          {isNum && row.subvals && row.subvals[s.slug] && (
                            <span style={{ display: 'block', fontSize: 17, fontWeight: 500, color: '#94a3b8', letterSpacing: 0, marginTop: 1 }}>
                              {row.subvals[s.slug]}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ fontSize: 17, color: '#94a3b8', margin: '8px 4px 0', fontFamily: SAMSUNG }}>
            — = dado não disponível · 0 = valor confirmado como zero · linhas marcadas “contexto” não definem desempenho
          </p>
        </div>
      )}
    </div>
  );
}

// Slide PERSONALIZADO (texto/observações OU imagem). Renderizado no Modo Apresentação
// no lugar do PresentationSlide quando metrica.isCustom.
function CustomSlide({ slide }) {
  const SAMSUNG = "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif";
  const fade = (delay) => ({ animation: `slideUp 0.5s ${delay}ms cubic-bezier(0.16,1,0.3,1) both` });
  const keyframes = <style>{`@keyframes slideUp { from { opacity:0; transform: translateY(16px) } to { opacity:1; transform: none } }`}</style>;

  // IMAGEM: sempre centralizada, com bordas arredondadas.
  if (slide.tipo === 'imagem' && slide.imagem) {
    return (
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '4px 8px' }}>
        {keyframes}
        {slide.titulo && (
          <div style={{ ...fade(0), textAlign: 'center' }}>
            <h1 style={{ fontSize: 54, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', margin: 0, fontFamily: SAMSUNG }}>{slide.titulo}</h1>
            {slide.subtitulo && <p style={{ fontSize: 27, fontWeight: 500, color: '#64748b', margin: '5px 0 0', fontFamily: SAMSUNG }}>{slide.subtitulo}</p>}
          </div>
        )}
        <img src={slide.imagem} alt={slide.titulo || 'Slide'} style={{ ...fade(80), display: 'block', maxWidth: '100%', maxHeight: slide.titulo ? '66vh' : '78vh', objectFit: 'contain', borderRadius: 20, border: '1px solid #e8edf3', boxShadow: '0 14px 44px rgba(15,23,42,0.18)' }} />
      </div>
    );
  }

  // TEXTO: título + tópicos.
  const bullets = (slide.bullets || []).filter(b => String(b).trim());
  return (
    <div style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: '10px 8px' }}>
      {keyframes}
      <div style={{ ...fade(0), display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
        <span style={{ width: 8, height: 40, borderRadius: 6, background: slide.accent || '#1428A0', display: 'inline-block' }} />
        <h1 style={{ fontSize: 70, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.05, margin: 0, fontFamily: SAMSUNG }}>{slide.titulo}</h1>
      </div>
      {slide.subtitulo && (
        <p style={{ ...fade(90), fontSize: 29.5, fontWeight: 500, color: '#64748b', margin: '0 0 26px 22px', fontFamily: SAMSUNG }}>{slide.subtitulo}</p>
      )}
      <ul style={{ listStyle: 'none', margin: bullets.length ? '22px 0 0' : 0, padding: 0 }}>
        {bullets.map((b, i) => (
          <li key={i} style={{ ...fade(160 + i * 70), display: 'flex', alignItems: 'flex-start', gap: 16, padding: '14px 18px', marginBottom: 10, background: 'linear-gradient(180deg,#ffffff,#f8fafc)', border: '1px solid #e8edf3', borderRadius: 14, boxShadow: '0 2px 10px rgba(20,40,160,0.04)' }}>
            <span style={{ flexShrink: 0, width: 10, height: 10, borderRadius: '50%', background: slide.accent || '#1428A0', marginTop: 12 }} />
            <span style={{ fontSize: 36.5, fontWeight: 600, color: '#1F2A44', lineHeight: 1.35, fontFamily: SAMSUNG }}>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
// Slide FIXO de encerramento — sempre o ÚLTIMO da apresentação (pedido 24/09:
// a plateia nunca sabia quando a apresentação tinha terminado).
function FimSlide({ periodo }) {
  const SAMSUNG = "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif";
  const fade = (delay) => ({ animation: `slideUp 0.55s ${delay}ms cubic-bezier(0.16,1,0.3,1) both` });
  return (
    <div style={{ width: '100%', minHeight: '64vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 22, textAlign: 'center', fontFamily: SAMSUNG }}>
      <style>{`@keyframes slideUp { from { opacity:0; transform: translateY(16px) } to { opacity:1; transform: none } }`}</style>
      <div style={{ ...fade(0), width: 118, height: 118, borderRadius: '50%', background: 'linear-gradient(135deg,#10b981,#0f766e)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 18px 50px rgba(16,185,129,0.35)' }}>
        <Check size={64} color="#ffffff" strokeWidth={3} />
      </div>
      <h1 style={{ ...fade(120), fontSize: 96, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', margin: 0 }}>Obrigado!</h1>
      <p style={{ ...fade(220), fontSize: 34, fontWeight: 600, color: '#475569', margin: 0 }}>Fim da apresentação — espaço aberto para dúvidas e comentários.</p>
      {periodo && (
        <p style={{ ...fade(300), fontSize: 24, fontWeight: 700, color: '#94a3b8', margin: 0, fontFamily: monoFont }}>Período apresentado: {periodo}</p>
      )}
      <p style={{ ...fade(380), fontSize: 20, fontWeight: 800, color: '#cbd5e1', letterSpacing: '0.16em', margin: '16px 0 0' }}>UP&HELP · SALES DAY 2.0</p>
    </div>
  );
}

// =============== JANELA PÚBLICA da apresentação ===============
// É a janela COMPARTILHADA no Meet: SÓ o slide, navegação e "Sair".
// NADA de roteiro/IA aqui — o roteiro vive na janela do APRESENTADOR
// (rota /apresentador), sincronizada via BroadcastChannel.
function PresentationOverlay({ metricas, idx, setIdx, onClose, buildModel }) {
  const total = metricas.length;
  const cur = Math.min(Math.max(0, idx), Math.max(0, total - 1));
  const metrica = metricas[cur];

  // onClose estável via ref — o efeito de fullscreen NÃO pode depender de
  // onClose (recriado a cada navegação), senão o cleanup chamaria exitFullscreen
  // a cada clique e fecharia a apresentação.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // ===== SINCRONIZAÇÃO com o Painel do Apresentador (2ª janela) =====
  // Estado → broadcast a cada navegação; 'nav'/'goto' do painel → muda o slide aqui.
  const chRef = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { metricas, cur, total, buildModel };
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('sd2_presenter');
    chRef.current = ch;
    const sendState = () => {
      const { metricas: ms, cur: c, total: t, buildModel: bm } = stateRef.current;
      const m = ms[c];
      let destaques = [];
      try {
        if (m && !m.isCustom && !m.isFim) {
          const model = bm(m);
          destaques = (model?.cards || []).slice(0, 3).map(cd => ({
            kicker: cd.kicker, tone: cd.tone, loja: cd.store?.name || '', valor: fmtSec(cd.value, cd.fmt), sub: cd.sub || null,
          }));
        }
      } catch { /* destaque é opcional */ }
      ch.postMessage({
        type: 'state', idx: c, total: t,
        // o slide de encerramento entra como "custom" pro painel (sem roteiro/destaques)
        slides: ms.map(x => ({ key: x.key, titulo: x.titulo, subtitulo: x.subtitulo || '', isCustom: !!x.isCustom || !!x.isFim })),
        destaques,
      });
    };
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.type === 'hello') sendState();
      else if (d.type === 'nav') setIdx((i) => Math.max(0, Math.min(stateRef.current.total - 1, i + (d.delta || 0))));
      else if (d.type === 'goto' && typeof d.idx === 'number') setIdx(Math.max(0, Math.min(stateRef.current.total - 1, d.idx)));
      else if (d.type === 'gerar' && d.key) {
        // O painel pediu "Gerar novamente": o payload do slide é montado AQUI (fonte dos dados).
        const m = stateRef.current.metricas.find(x => x.key === d.key);
        if (!m || m.isCustom || m.isFim) { ch.postMessage({ type: 'gerar-erro', key: d.key, erro: 'Slide sem dados' }); return; }
        try {
          const payload = modelToPayload(stateRef.current.buildModel(m));
          if (!payload) { ch.postMessage({ type: 'gerar-erro', key: d.key, erro: 'Slide sem dados' }); return; }
          generateRoteiro({ key: m.key, titulo: m.titulo, subtitulo: m.subtitulo || '', payload })
            .then(r => ch.postMessage({ type: 'gerado', key: d.key, texto: r.data.texto }))
            .catch(e => ch.postMessage({ type: 'gerar-erro', key: d.key, erro: e.response?.data?.detail || e.message }));
        } catch (e) { ch.postMessage({ type: 'gerar-erro', key: d.key, erro: String(e && e.message) }); }
      }
    };
    ch.addEventListener('message', onMsg);
    sendState();
    return () => { ch.postMessage({ type: 'closed' }); ch.removeEventListener('message', onMsg); ch.close(); chRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Rebroadcast a cada navegação/mudança de slides.
  useEffect(() => {
    const ch = chRef.current;
    if (!ch) return;
    const { metricas: ms, cur: c, total: t, buildModel: bm } = stateRef.current;
    const m = ms[c];
    let destaques = [];
    try {
      if (m && !m.isCustom && !m.isFim) {
        const model = bm(m);
        destaques = (model?.cards || []).slice(0, 3).map(cd => ({
          kicker: cd.kicker, tone: cd.tone, loja: cd.store?.name || '', valor: fmtSec(cd.value, cd.fmt), sub: cd.sub || null,
        }));
      }
    } catch { /* opcional */ }
    ch.postMessage({
      type: 'state', idx: c, total: t,
      slides: ms.map(x => ({ key: x.key, titulo: x.titulo, subtitulo: x.subtitulo || '', isCustom: !!x.isCustom || !!x.isFim })),
      destaques,
    });
  }, [cur, total, metricas]);

  // Fullscreen real — roda UMA vez (mount/unmount).
  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    const onFs = () => { if (!document.fullscreenElement) onCloseRef.current(); };
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    };
  }, []);

  // Teclado (backup)
  useEffect(() => {
    const h = (e) => {
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Spacebar' || e.key === 'PageDown') {
        e.preventDefault(); setIdx((i) => Math.min(total - 1, i + 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault(); setIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Home') { e.preventDefault(); setIdx(0); }
      else if (e.key === 'End') { e.preventDefault(); setIdx(total - 1); }
      else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [total, setIdx, onClose]);

  if (!metrica) return null;

  const handleClick = (e) => {
    if (e.clientX < window.innerWidth / 2) setIdx((i) => Math.max(0, i - 1));
    else setIdx((i) => Math.min(total - 1, i + 1));
  };

  const ACCENT = '#0f766e';
  const navBtn = (disabled) => ({
    pointerEvents: 'auto', width: 44, height: 44, borderRadius: 12, border: '1px solid #e2e8f0',
    background: 'rgba(255,255,255,0.92)', color: disabled ? '#cbd5e1' : '#475569',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'default' : 'pointer', boxShadow: '0 3px 10px rgba(15,23,42,0.08)',
  });

  return (
    <div
      onClick={handleClick}
      style={{ position: 'fixed', inset: 0, zIndex: 100000, background: '#F8F9FB', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif" }}
    >
      <style>{`
        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
      `}</style>

      {/* Barra de progresso fininha no topo */}
      <div style={{ position: 'fixed', top: 0, left: 0, height: 3, width: `${((cur + 1) / total) * 100}%`, background: ACCENT, zIndex: 4, transition: 'width 0.25s ease' }} />

      {/* Topo sutil: título + contador (esq) · Sair (dir). SEM recursos de apresentador. */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 28px', pointerEvents: 'none' }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
          {metrica.titulo}
          <span style={{ color: '#94a3b8', fontWeight: 700, marginLeft: 12, fontFamily: monoFont, fontSize: 14 }}>{cur + 1} / {total}</span>
        </span>
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          <button style={{ ...navBtn(cur === 0), width: 38, height: 38 }} disabled={cur === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} title="Anterior (←)"><ChevronLeft size={19} /></button>
          <button style={{ ...navBtn(cur >= total - 1), width: 38, height: 38 }} disabled={cur >= total - 1} onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} title="Próximo (→)"><ChevronRight size={19} /></button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            style={{ pointerEvents: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#ffffff', color: '#64748b', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}
            title="Sair (Esc)"
          >
            <X size={14} /> Sair
          </button>
        </span>
      </div>

      {/* Palco: o slide ocupa 100% da área — nada abre por cima dele. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="present-stage" style={{ pointerEvents: 'none', margin: 'auto', width: '100%', maxWidth: 1880, padding: '20px 40px 44px' }}>
          {metrica.isFim
            ? <FimSlide key="fim" periodo={metrica.periodo} />
            : metrica.isCustom
              ? <CustomSlide key={metrica.key} slide={metrica} />
              : <PresentationSlide key={metrica.key} model={buildModel(metrica)} />}
        </div>
      </div>

    </div>
  );
}

// ===== PDF dos slides (pedido 14/10/2026: reunião sem chamada, enviar os slides em PDF) =====
// Monta TODOS os slides da apresentação (a mesma lista do "Apresentar": visíveis + personalizados
// ativos + encerramento; os ocultos não entram), um por página de 1920×1080, estáticos, e abre a
// impressão do navegador — o usuário escolhe "Salvar como PDF". Texto sai vetorial (nítido).
function SlidesPdf({ metricas, buildModel, nomeArquivo, onDone }) {
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    const tituloAntes = document.title;
    document.title = nomeArquivo;   // o Chrome usa o título como nome sugerido do arquivo
    let cancelado = false;
    const fim = () => { if (!cancelado) onDoneRef.current(); };
    window.addEventListener('afterprint', fim);
    (async () => {
      try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch { /* segue */ }
      // espera as imagens dos slides personalizados (até 6 s)
      const t0 = Date.now();
      while (Date.now() - t0 < 6000) {
        const imgs = [...document.querySelectorAll('.sd2-pdf-deck img')];
        if (imgs.every(im => im.complete)) break;
        await new Promise(r => setTimeout(r, 200));
      }
      // Slide mais alto que a página (ex.: In Home Completo com 6 linhas na tabela): na tela ele
      // rola, no papel cortaria. Reduz o slide até caber inteiro (no máximo até 60%).
      document.querySelectorAll('.sd2-pdf-pagina').forEach(pg => {
        const area = pg.querySelector('.sd2-pdf-area');
        const st = area && area.querySelector('.present-stage');
        if (!st) return;
        st.style.zoom = '1';
        const precisa = st.scrollHeight, cabe = area.clientHeight;
        if (precisa > cabe) st.style.zoom = String(Math.max(0.6, (cabe - 6) / precisa));
      });
      await new Promise(r => setTimeout(r, 600));
      if (!cancelado) window.print();
    })();
    return () => { cancelado = true; window.removeEventListener('afterprint', fim); document.title = tituloAntes; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const total = metricas.length;
  const ACCENT = '#0f766e';
  return createPortal(
    <SlidesEstaticosContext.Provider value={true}>
      <div className="sd2-pdf-deck" style={{ position: 'fixed', inset: 0, zIndex: 100005, background: '#e2e8f0', overflow: 'auto' }}>
        <style>{`
          @page { size: 1920px 1080px; margin: 0; }
          .sd2-pdf-deck *, .sd2-pdf-deck *::before, .sd2-pdf-deck *::after { transition: none !important; animation: none !important; }
          .sd2-pdf-pagina, .sd2-pdf-pagina * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          @media print {
            html, body { margin: 0 !important; padding: 0 !important; background: #F8F9FB !important; }
            #root { display: none !important; }
            .sd2-pdf-deck { position: static !important; overflow: visible !important; background: #F8F9FB !important; }
            .sd2-pdf-aviso { display: none !important; }
            .sd2-pdf-pagina { margin: 0 !important; box-shadow: none !important; break-after: page; page-break-after: always; }
            .sd2-pdf-pagina:last-child { break-after: auto; page-break-after: auto; }
          }
        `}</style>
        <div className="sd2-pdf-aviso" style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 20px', background: '#0f172a', color: '#ffffff', fontSize: 14, fontWeight: 700, fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', sans-serif" }}>
          <span>Gerando o PDF com {total} slides — na janela de impressão escolha <b>Salvar como PDF</b>.</span>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <button onClick={() => window.print()} style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>Abrir impressão de novo</button>
            <button onClick={() => onDoneRef.current()} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #475569', background: 'transparent', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Fechar</button>
          </span>
        </div>
        {metricas.map((metrica, i) => (
          <div key={metrica.key} className="sd2-pdf-pagina" style={{ position: 'relative', width: 1920, height: 1080, margin: '16px auto', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#F8F9FB', boxShadow: '0 6px 24px rgba(15,23,42,0.18)', fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'SamsungOne UI', sans-serif" }}>
            <div style={{ position: 'absolute', top: 0, left: 0, height: 3, width: `${((i + 1) / total) * 100}%`, background: ACCENT }} />
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', padding: '14px 28px' }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
                {metrica.titulo}
                <span style={{ color: '#94a3b8', fontWeight: 700, marginLeft: 12, fontFamily: monoFont, fontSize: 14 }}>{i + 1} / {total}</span>
              </span>
            </div>
            <div className="sd2-pdf-area" style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
              <div className="present-stage" style={{ margin: 'auto', width: '100%', maxWidth: 1880, padding: '20px 40px 44px' }}>
                {metrica.isFim
                  ? <FimSlide periodo={metrica.periodo} />
                  : metrica.isCustom
                    ? <CustomSlide slide={metrica} />
                    : <PresentationSlide model={buildModel(metrica)} />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </SlidesEstaticosContext.Provider>,
    document.body
  );
}

// =================== MAIN ===================

function SalesDay2Dashboard() {
  const { user } = useAuth();
  const canEdit = user?.role === 'superadmin';   // lojas = somente leitura (sem editar dados)
  const [results, setResults] = useState({});
  const [resultsPrev, setResultsPrev] = useState({});
  const [salesDay, setSalesDayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoAberto, setInfoAberto] = useState(null);

  // Modo Apresentação (slide por métrica, fontes ampliadas)
  const [presenting, setPresenting] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const [gerandoPdf, setGerandoPdf] = useState(false);   // PDF dos slides (14/10/2026)

  // Modal "Ver regra"
  const [regraOpen, setRegraOpen] = useState(null); // { metrica, company }
  const [distintosCache, setDistintosCache] = useState({});
  const [aprovConfigs, setAprovConfigs] = useState({});
  const [distintosLoading, setDistintosLoading] = useState(false);

  // Modal "Lista de OS" (aprovações)
  const [osListOpen, setOsListOpen] = useState(null); // { metrica, company }
  const [osListData, setOsListData] = useState(null);
  const [osListLoading, setOsListLoading] = useState(false);
  const [osListFiltro, setOsListFiltro] = useState('todas'); // 'todas' | 'aprovadas' | 'reprovadas'

  // Headcount manual
  const [headcount, setHeadcountData] = useState({});
  const [headcountOpen, setHeadcountOpen] = useState(false);

  // Vendas Digitais (WooCommerce B2C/B2B + Store+ + SMB) — só p/ o slide de apresentação.
  // { [slug]: { b2c, b2cCount, b2bv, b2bCount, storePlus, smb } }
  const [b2bSales, setB2bSales] = useState(null);
  const [siteMeta, setSiteMeta] = useState({ b2c: 0, b2b: 0 });  // meta GERAL do site (E-commerce)

  // Venda de Acessórios (total por loja no período) — { [slug]: {total, semDados} }
  const [acessorios, setAcessoriosData] = useState({});
  const [acessoriosLoading, setAcessoriosLoading] = useState(false);
  const [seminovos, setSeminovosData] = useState({});
  const [seminovosLoading, setSeminovosLoading] = useState(false);
  const [acessListOpen, setAcessListOpen] = useState(null);   // company
  const [acessListData, setAcessListData] = useState(null);
  const [acessListLoading, setAcessListLoading] = useState(false);

  // Mapeamento (filtros / status / regras)
  const [mapeamentoOpen, setMapeamentoOpen] = useState(false);
  // Mapeamento do Backlog (filtros por aba + status excluídos)
  const [backlogMapOpen, setBacklogMapOpen] = useState(false);

  // GD projection factors (% do Balcão CI por loja) + metas GD FIXAS (valor manual por loja)
  const [gdFactors, setGdFactors] = useState({});
  const [gdMetasFixas, setGdMetasFixas] = useState({});
  const refreshGdFactors = useCallback(async () => {
    try { const r = await getGdProjection(); setGdFactors(r.data.factors || {}); setGdMetasFixas(r.data.metas_fixas || {}); }
    catch { setGdFactors({}); setGdMetasFixas({}); }
  }, []);
  useEffect(() => { refreshGdFactors(); }, [refreshGdFactors]);

  // Carrega configs de aprovações
  useEffect(() => {
    (async () => {
      try { const r = await getAprovacoesConfigs(); setAprovConfigs(r.data || {}); }
      catch { setAprovConfigs({}); }
    })();
  }, []);

  const abrirRegra = async (metrica, company) => {
    setRegraOpen({ metrica, company });
    if (metrica.type !== 'aprovacoes' && !distintosCache[company.slug]) {
      setDistintosLoading(true);
      try {
        const res = await getDistintos(company.ascCode);
        setDistintosCache(prev => ({ ...prev, [company.slug]: res.data }));
      } catch { setDistintosCache(prev => ({ ...prev, [company.slug]: {} })); }
      setDistintosLoading(false);
    }
  };

  const abrirOSList = async (metrica, company) => {
    setOsListOpen({ metrica, company });
    setOsListData(null);
    setOsListFiltro('todas');
    setOsListLoading(true);
    try {
      const aprovConfig = aprovConfigs[company.slug];
      let abaUsada = null;
      for (const ak of metrica.abaKeys || []) {
        if (aprovConfig?.abas?.[ak]) { abaUsada = ak; break; }
      }
      if (!abaUsada) { setOsListData({ error: 'Aba não encontrada para esta empresa', os_list: [] }); setOsListLoading(false); return; }
      const res = await getAprovacoesOSList(company.slug, company.ascCode, abaUsada, dataInicio, dataFim, false);
      setOsListData(res.data);
    } catch (e) {
      setOsListData({ error: e.message || 'Erro ao carregar', os_list: [] });
    }
    setOsListLoading(false);
  };

  const hoje = new Date();
  const ontem = new Date(hoje); ontem.setDate(ontem.getDate() - 1);
  const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  const [dataInicio, setDataInicio] = useState(fmtDate(inicioMes));
  const [dataFim, setDataFim] = useState(fmtDate(ontem));
  const [filtroExpandido, setFiltroExpandido] = useState(false);
  const [lastUpdates, setLastUpdates] = useState(null);

  // Settings persistidas
  const [hidden, setHidden] = useState([]);
  const [order, setOrder] = useState([]);
  const [hiddenLoading, setHiddenLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hiddenDraft, setHiddenDraft] = useState([]);
  const [orderDraft, setOrderDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  // Slides personalizados (texto) do Modo Apresentação
  const [customSlides, setCustomSlides] = useState([]);
  const [customSlidesOpen, setCustomSlidesOpen] = useState(false);
  const [roteirosOpen, setRoteirosOpen] = useState(false);
  // Roteiro da apresentação (IA) — textos salvos por slide + slides em geração
  const [roteiros, setRoteiros] = useState({});
  const [roteiroGenerating, setRoteiroGenerating] = useState([]);
  const [dataFreshTick, setDataFreshTick] = useState(0);  // conta chegadas de dados FRESCOS (não cache)
  const lastRoteiroSyncRef = useRef('');
  const roteiroPollRef = useRef(null);
  const refreshRoteiros = useCallback(() => {
    getRoteiros().then(r => {
      setRoteiros(r.data.roteiros || {});
      setRoteiroGenerating(r.data.generating || []);
    }).catch(() => {});
  }, []);
  // Enquanto houver slides gerando, atualiza os textos a cada 15s (para sozinho).
  const startRoteiroPolling = useCallback(() => {
    if (roteiroPollRef.current) return;
    let ticks = 0;
    roteiroPollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const r = await getRoteiros();
        setRoteiros(r.data.roteiros || {});
        const gen = r.data.generating || [];
        setRoteiroGenerating(gen);
        if (!gen.length || ticks >= 20) { clearInterval(roteiroPollRef.current); roteiroPollRef.current = null; }
      } catch {
        if (ticks >= 20) { clearInterval(roteiroPollRef.current); roteiroPollRef.current = null; }
      }
    }, 15000);
  }, []);
  useEffect(() => () => { if (roteiroPollRef.current) clearInterval(roteiroPollRef.current); }, []);
  const [savingCustom, setSavingCustom] = useState(false);
  const handleSaveCustomSlides = async (slides) => {
    setSavingCustom(true);
    try {
      const res = await saveCustomSlides(slides);
      setCustomSlides(res.data.slides || []);
      setCustomSlidesOpen(false);
    } catch (e) {
      alert('Falha ao salvar slides: ' + (e.response?.data?.detail || e.message));
    }
    setSavingCustom(false);
  };

  const companies = COMPANIES.filter(c => !c.isSalesDay && !c.isWarromComp && !c.isSalesDay2);

  // Carrega visibility + ordem + slides personalizados salvos
  useEffect(() => {
    (async () => {
      try {
        const [vRes, oRes, csRes] = await Promise.all([getVisibility(), getMetricOrder(), getCustomSlides()]);
        setHidden(vRes.data.hidden || []);
        setOrder(oRes.data.order || []);
        setCustomSlides(csRes.data.slides || []);
      } catch { setHidden([]); setOrder([]); setCustomSlides([]); }
      refreshRoteiros();  // roteiros salvos (IA) — leitura barata, nunca gera nada
      setHiddenLoading(false);
    })();
  }, []);

  // Calcula período equivalente do mês anterior (mesmo intervalo de dias)
  const prevPeriod = useMemo(() => {
    if (!dataInicio || !dataFim) return null;
    const di = new Date(dataInicio + 'T00:00:00');
    const df = new Date(dataFim + 'T00:00:00');
    const pi = new Date(di.getFullYear(), di.getMonth() - 1, di.getDate());
    const pf = new Date(df.getFullYear(), df.getMonth() - 1, df.getDate());
    return { inicio: fmtDate(pi), fim: fmtDate(pf) };
  }, [dataInicio, dataFim]);

  // Carrega dados (Warrom dashboards + SalesDay aprovações + período anterior pra delta de Recepção)
  const loadData = useCallback(async () => {
    const cacheKey = `sd2cache:v1:${dataInicio}:${dataFim}`;
    // Stale-while-revalidate: ao dar F5, mostra na hora o último dado salvo
    // (sem spinner) e atualiza em segundo plano. O fetch fresco SEMPRE roda e
    // sobrescreve — então o número final é o real; o cache só evita a espera.
    let hadCache = false;
    try {
      const raw = window.localStorage.getItem(cacheKey);
      if (raw) {
        const c = JSON.parse(raw);
        if (c && c.map) {
          setResults(c.map);
          setResultsPrev(c.mapPrev || {});
          setSalesDayData(c.salesDay ?? null);
          setLoading(false);
          hadCache = true;
        }
      }
    } catch { /* cache indisponível — segue com fetch normal */ }
    if (!hadCache) setLoading(true);
    setError(null);
    // Última atualização dos uploads por loja (não bloqueia o carregamento principal)
    getLastUpdates().then(r => setLastUpdates(r.data.lojas || [])).catch(() => {});
    try {
      const [warromResults, salesDayRes, warromResultsPrev] = await Promise.all([
        Promise.all(companies.map(async c => {
          try { const r = await getDashboard(c.ascCode, dataInicio, dataFim); return [c.slug, r.data]; }
          catch { return [c.slug, null]; }
        })),
        getSalesDay(dataInicio, dataFim).then(r => r.data).catch(() => null),
        prevPeriod ? Promise.all(companies.map(async c => {
          try { const r = await getDashboard(c.ascCode, prevPeriod.inicio, prevPeriod.fim); return [c.slug, r.data]; }
          catch { return [c.slug, null]; }
        })) : Promise.resolve([]),
      ]);
      const map = {}; warromResults.forEach(([s, d]) => { map[s] = d; });
      const mapPrev = {}; warromResultsPrev.forEach(([s, d]) => { mapPrev[s] = d; });
      setResults(map);
      setResultsPrev(mapPrev);
      setSalesDayData(salesDayRes);
      setDataFreshTick(t => t + 1);  // dados FRESCOS chegaram (cache não conta) → libera o sync do roteiro
      try { window.localStorage.setItem(cacheKey, JSON.stringify({ map, mapPrev, salesDay: salesDayRes })); } catch { /* quota cheia — ignora */ }
    } catch (e) { if (!hadCache) setError(e.message); }
    setLoading(false);
  }, [dataInicio, dataFim, prevPeriod]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  const setMes = (offset) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() + offset, 1);
    const fim = offset === 0 ? ontem : new Date(d.getFullYear(), d.getMonth() + 1, 0);
    setDataInicio(fmtDate(d)); setDataFim(fmtDate(fim));
  };

  const dtIni = new Date(dataInicio + 'T00:00:00');
  const dtFim = new Date(dataFim + 'T00:00:00');
  const diasNoPeriodo = Math.round((dtFim - dtIni) / 86400000) + 1;
  const diasNoMes = new Date(dtIni.getFullYear(), dtIni.getMonth() + 1, 0).getDate();
  // Esperado padronizado por DIAS ÚTEIS (seg–sex, sem feriados) — 21/09. Por LOJA,
  // porque cada cidade tem feriados municipais/estaduais próprios (FERIADOS_LOCAIS).
  const fatorEsperadoLoja = (slug) => businessMonthFactor(dataFim, slug);

  // Carregar headcount conforme o mês do dataInicio
  const headcountYear = dtIni.getFullYear();
  const headcountMonth = dtIni.getMonth() + 1;
  const refreshHeadcount = useCallback(async () => {
    try {
      const r = await getHeadcount(headcountYear, headcountMonth);
      const map = {};
      (r.data.entries || []).forEach(e => { map[e.loja_slug] = e; });
      setHeadcountData(map);
    } catch { setHeadcountData({}); }
  }, [headcountYear, headcountMonth]);
  useEffect(() => { refreshHeadcount(); }, [refreshHeadcount]);

  // Venda de Acessórios: total por loja no período (busca em paralelo p/ as 4 lojas)
  const refreshAcessorios = useCallback(async () => {
    if (!dataInicio || !dataFim) return;
    setAcessoriosLoading(true);
    try {
      const pairs = await Promise.all(companies.map(async c => {
        try { const r = await getAcessorios(c.ascCode, dataInicio, dataFim); return [c.slug, r.data]; }
        catch { return [c.slug, null]; }
      }));
      const map = {};
      pairs.forEach(([s, d]) => { map[s] = d; });
      setAcessoriosData(map);
    } catch { setAcessoriosData({}); }
    setAcessoriosLoading(false);
  }, [dataInicio, dataFim]); // eslint-disable-line
  useEffect(() => { refreshAcessorios(); }, [refreshAcessorios]);

  // Venda de Seminovos: total por loja no período (mesmo desenho do acessórios)
  const refreshSeminovos = useCallback(async () => {
    if (!dataInicio || !dataFim) return;
    setSeminovosLoading(true);
    try {
      const pairs = await Promise.all(companies.map(async c => {
        try { const r = await getSeminovos(c.ascCode, dataInicio, dataFim); return [c.slug, r.data]; }
        catch { return [c.slug, null]; }
      }));
      const map = {};
      pairs.forEach(([se, d]) => { map[se] = d; });
      setSeminovosData(map);
    } catch { setSeminovosData({}); }
    setSeminovosLoading(false);
  }, [dataInicio, dataFim]); // eslint-disable-line
  useEffect(() => { refreshSeminovos(); }, [refreshSeminovos]);

  // Vendas Digitais (WooCommerce período + Store+/SMB do mês) — só p/ o slide.
  const refreshB2bSales = useCallback(async () => {
    if (!dataInicio || !dataFim) return;
    const UNIT2SLUG = { JMV: 'jmv', FIX_CSP: 'fix-csp', VIP: 'vip', FIX_MSC: 'fix-msc' };
    try {
      const y = Number(dataInicio.slice(0, 4));
      const m = Number(dataInicio.slice(5, 7));
      const [anaRes, spRes, smRes] = await Promise.all([
        getB2BAnalytics(dataInicio, dataFim).catch(() => null),
        getStorePlusSMB(y, m).catch(() => null),
        getSiteMeta().catch(() => null),
      ]);
      const acc = {};
      const ensure = (slug) => (acc[slug] = acc[slug] || { b2c: 0, b2cCount: 0, b2bv: 0, b2bCount: 0, storePlus: 0, smb: 0, metaStorePlus: 0, metaSmb: 0 });
      const ana = anaRes?.data || {};
      (ana.b2c?.por_unidade || []).forEach(u => { const s = UNIT2SLUG[u.unit]; if (s) { const x = ensure(s); x.b2c += u.total || 0; x.b2cCount += u.count || 0; } });
      (ana.b2b?.por_unidade || []).forEach(u => { const s = UNIT2SLUG[u.unit]; if (s) { const x = ensure(s); x.b2bv += u.total || 0; x.b2bCount += u.count || 0; } });
      (spRes?.data?.entries || []).forEach(e => { const s = UNIT2SLUG[e.unit]; if (s) { const x = ensure(s); x.storePlus += e.store_plus_value || 0; x.smb += e.smb_value || 0; x.metaStorePlus += e.meta_store_plus || 0; x.metaSmb += e.meta_smb || 0; } });
      setSiteMeta(smRes?.data?.metas || { b2c: 0, b2b: 0 });  // meta GERAL do site (não por loja)
      setB2bSales(Object.keys(acc).length ? acc : null);
    } catch { setB2bSales(null); }
  }, [dataInicio, dataFim]); // eslint-disable-line
  useEffect(() => { refreshB2bSales(); }, [refreshB2bSales]);

  const abrirAcessList = async (company) => {
    setAcessListOpen(company);
    setAcessListData(null);
    setAcessListLoading(true);
    try {
      const r = await getAcessoriosItens(company.ascCode, dataInicio, dataFim);
      setAcessListData(r.data);
    } catch (e) {
      setAcessListData({ itens: [], total: 0, error: e.message });
    }
    setAcessListLoading(false);
  };

  const mesAtualAtivo = dataInicio === fmtDate(inicioMes) && dataFim === fmtDate(ontem);
  const mpIni = fmtDate(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
  const mpFim = fmtDate(new Date(hoje.getFullYear(), hoje.getMonth(), 0));
  const mesPassadoAtivo = dataInicio === mpIni && dataFim === mpFim;

  // Constrói a ordem efetiva (saved order + keys faltantes na ordem original)
  const buildEffectiveOrder = (savedOrder) => {
    const set = new Set();
    const eff = [];
    (savedOrder || []).forEach(k => {
      if (METRICAS.find(m => m.key === k) && !set.has(k)) { eff.push(k); set.add(k); }
    });
    // Métricas novas (não presentes na ordem salva) entram logo após seu
    // vizinho natural no array METRICAS, não no fim da página.
    METRICAS.forEach((m, idx) => {
      if (set.has(m.key)) return;
      let insertAt = eff.length;
      for (let j = idx - 1; j >= 0; j--) {
        const pos = eff.indexOf(METRICAS[j].key);
        if (pos !== -1) { insertAt = pos + 1; break; }
      }
      eff.splice(insertAt, 0, m.key);
      set.add(m.key);
    });
    return eff;
  };

  const openSettings = () => {
    setHiddenDraft([...hidden]);
    setOrderDraft(buildEffectiveOrder(order));
    setSettingsOpen(true);
  };
  const toggleDraft = (key) => {
    setHiddenDraft(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const moveDraft = (key, delta) => {
    setOrderDraft(prev => {
      const i = prev.indexOf(key);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const saveSettings = async () => {
    setSaving(true);
    try {
      const [vRes, oRes] = await Promise.all([
        setVisibility(hiddenDraft),
        setMetricOrder(orderDraft),
      ]);
      setHidden(vRes.data.hidden || []);
      setOrder(oRes.data.order || []);
      setSettingsOpen(false);
    } catch (e) {
      alert('Falha ao salvar: ' + e.message);
    }
    setSaving(false);
  };

  // Helper para obter summary da métrica
  const getSummaryForCompany = (metrica, company) => {
    if (metrica.type === 'acessorios') return null; // dados vêm de `acessorios` (fetch dedicado)
    if (metrica.type === 'seminovos') return null;   // dados vêm de `seminovos` (fetch dedicado)
    if (metrica.type === 'produtividade') return null; // slide consolidado: monta dos abas + headcount direto
    if (metrica.type === 'aprovacoes') {
      const empresa = salesDay?.empresas?.[company.slug];
      if (!empresa || empresa.semDados) return null;
      for (const ak of metrica.abaKeys) {
        if (empresa.abas?.[ak]) return empresa.abas[ak];
      }
      return null;
    }
    const data = results[company.slug];
    if (!data) return null;
    if (metrica.type === 'recepcao') return summarizeRecepcao(data.recepcao_diario);
    if (metrica.type === 'gdta') return summarizeGDTA(data.gd_ta);
    if (metrica.type === 'section') return summarizeSection(data[metrica.dataKey]);
    return summarizeProjeto(data[metrica.dataKey]);
  };

  // ======= MODELO DO SLIDE DE COMPARAÇÃO (Modo Apresentação) =======
  // FIDELIDADE: extrai TUDO dos MESMOS objetos que os cards usam
  // (getSummaryForCompany + os mesmos extras computados em renderMetricaSection).
  // Aplica as MESMAS fórmulas dos cards (gd: linhas 508-524 · aprovações: 634 ·
  // acessórios: 736) sobre esses summary+extras. Não recalcula a partir dos brutos.
  const buildPresentModel = (metrica) => {
    // Mesma filtragem por loja oculta que renderMetricaSection (HIDDEN_TABS).
    const cards = companies
      .filter(c => !(HIDDEN_TABS[c.slug] || []).includes(metrica.key))
      .map(c => ({ company: c, summary: getSummaryForCompany(metrica, c) }));

    // Paleta Samsung por loja (SÓ na apresentação — não altera o dashboard operacional).
    const SAMSUNG_STORE = { 'jmv': '#1428A0', 'fix-csp': '#0077C8', 'vip': '#00C3B2', 'fix-msc': '#00B5E3' };
    const themeColor = (slug) => SAMSUNG_STORE[slug] || COMPANY_THEME[slug]?.color || '#475569';
    const base = {
      titulo: metrica.titulo,
      subtitulo: metrica.subtitulo,
      accent: metrica.accent,
    };

    // --- helper p/ a linha de insight (líder vs vice) ---
    const buildInsight = (storesForInsight, { lowerIsBetter, fmtName }) => {
      const valid = storesForInsight.filter(s => s.primaryValue !== null && s.primaryValue !== undefined && isFinite(s.primaryValue));
      if (valid.length < 1) return '';
      const ord = [...valid].sort((a, b) => lowerIsBetter ? a.primaryValue - b.primaryValue : b.primaryValue - a.primaryValue);
      const lead = ord[0];
      const leadFmt = fmtName(lead.primaryValue);
      if (ord.length < 2) return `${lead.name} lidera com ${leadFmt}.`;
      const second = ord[1];
      if (second.primaryValue === 0 || !isFinite(second.primaryValue)) {
        return `${lead.name} lidera com ${leadFmt}.`;
      }
      const diffPct = Math.abs((lead.primaryValue - second.primaryValue) / second.primaryValue) * 100;
      const verbo = lowerIsBetter ? 'abaixo da' : 'acima da';
      return `${lead.name} lidera com ${leadFmt} — ${diffPct.toFixed(0)}% ${verbo} ${second.name}.`;
    };

    // Vendas Digitais — canais SEPARADOS, 1 slide cada:
    // WooCommerce B2C, WooCommerce B2B, Store+, SMB. metrica.channel.
    if (metrica.type === 'digital') {
      const dataB2b = b2bSales || {};
      const ch = metrica.channel;
      const list = companies
        .filter(c => dataB2b[c.slug])
        .map(c => {
          const d = dataB2b[c.slug];
          const row = { slug: c.slug, name: c.name, color: themeColor(c.slug) };
          // 0 real (canal ativo com venda zero) ≠ '—' (sem dado) — d existe = dado carregado.
          if (ch === 'woo_b2c') {
            const v = d.b2c || 0, cnt = d.b2cCount || 0;
            row.primaryValue = v; row.pedidos = cnt; row.ticket = cnt > 0 ? v / cnt : null;
          } else if (ch === 'woo_b2b') {
            const v = d.b2bv || 0, cnt = d.b2bCount || 0;
            row.primaryValue = v; row.pedidos = cnt; row.ticket = cnt > 0 ? v / cnt : null;
          } else if (ch === 'storeplus') {
            const meta = d.metaStorePlus || 0;
            row.realizado = d.storePlus || 0;
            row.meta = meta || null; row.atg = meta > 0 ? (row.realizado / meta) * 100 : null;
          } else { // smb
            const meta = d.metaSmb || 0;
            row.realizado = d.smb || 0;
            row.meta = meta || null; row.atg = meta > 0 ? (row.realizado / meta) * 100 : null;
          }
          return row;
        });
      // ===== MODELO 5 — VENDAS =====
      let primaryLabel, primaryFmt = 'brl', secundarios, attentionRowLabel, headerChips = null,
        primaryVsTarget = null, consolidado = null, cardsList = null, iaContexto = null, nota = null,
        primarySub = null;
      if (ch === 'woo_b2c' || ch === 'woo_b2b') {
        // E-commerce: PRIMEIRO o resultado consolidado do canal (meta do site é GERAL);
        // as barras mostram a CONTRIBUIÇÃO de cada unidade. Destaques independentes.
        const canal = ch === 'woo_b2c' ? 'B2C' : 'B2B';
        primaryLabel = `Contribuição por unidade — E-commerce ${canal}`;
        const totalReal = list.reduce((a, s) => a + (s.primaryValue || 0), 0);
        primarySub = Object.fromEntries(list.map(s => [s.slug, (totalReal > 0 && s.primaryValue != null) ? `${d1BR((s.primaryValue / totalReal) * 100)}% do canal` : null]));
        secundarios = [
          { label: 'Pedidos', fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(list.map(s => [s.slug, s.pedidos])) },
          { label: 'Ticket médio', fmt: 'brl', lowerBetter: false, colorize: true, valores: Object.fromEntries(list.map(s => [s.slug, s.ticket])) },
        ];
        const meta = (ch === 'woo_b2c' ? siteMeta.b2c : siteMeta.b2b) || 0;
        if (meta > 0) {
          // Meta do CANAL (site inteiro, não por loja) → régua NACIONAL, sem feriado local.
          const esperado = meta * businessMonthFactor(dataFim);
          const ritmo = esperado > 0 ? (totalReal / esperado) * 100 : null;
          consolidado = {
            titulo: `Canal ${canal} (site inteiro)`,
            ritmo, realizado: totalReal, esperado, meta,
            falta: Math.max(0, esperado - totalReal), excedente: Math.max(0, totalReal - esperado),
            frase: ritmo != null ? `${canal} está em ${d1BR(ritmo)}% do ritmo esperado até hoje.` : null,
          };
          headerChips = [`Meta do site (mês): ${fmtBRL(meta)}`, `Esperado até hoje: ${fmtBRL(esperado)}`];
        }
        // Destaques INDEPENDENTES (sem meta por loja → tons neutros).
        const cFat = cardsDesempenho(list, { value: s => s.primaryValue, fmt: 'brl', kickerSemMeta: 'Maior faturamento' })[0];
        const cPed = cardsDesempenho(list, { value: s => s.pedidos, fmt: 'num', kickerSemMeta: 'Mais pedidos' })[0];
        const cTic = cardsDesempenho(list, { value: s => s.ticket, fmt: 'brl', kickerSemMeta: 'Maior ticket médio' })[0];
        cardsList = [cFat, cPed, cTic].filter(Boolean).map(c => ({ ...c, tone: 'neutral' }));
        iaContexto = {
          indicador_principal: `Ritmo do CANAL ${canal} vs esperado até hoje (meta do site é geral, não por loja)`,
          direcao: 'quanto MAIOR o ritmo do canal, melhor',
          indicadores_secundarios: ['Contribuição por unidade', 'Pedidos', 'Ticket médio'],
          nao_usar_para_desempenho: ['maior faturamento por unidade como "melhor desempenho" (não há meta por unidade)'],
          observacoes: consolidado?.frase || 'Meta do site ainda não cadastrada.',
        };
      } else {
        const nome = ch === 'storeplus' ? 'Store+' : 'SMB';
        // Principal = ATINGIMENTO vs esperado até hoje (dias úteis) — não o valor absoluto.
        primaryLabel = `Atingimento vs esperado até hoje — ${nome}`;
        primaryFmt = 'pct1';
        list.forEach(s => {
          // Fator por LOJA: desconta os feriados locais da cidade de cada uma.
          const fator = businessMonthFactor(dataFim, s.slug);
          s.espHoje = (s.meta != null && s.meta > 0) ? s.meta * fator : null;
          s.primaryValue = (s.espHoje > 0 && s.realizado != null) ? (s.realizado / s.espHoje) * 100 : null;
          s.falta = (s.espHoje > 0 && s.realizado != null) ? Math.max(0, s.espHoje - s.realizado) : null;
          s.proj = (s.realizado != null && fator > 0) ? s.realizado / fator : null;
        });
        primarySub = Object.fromEntries(list.map(s => [s.slug, (s.realizado != null && s.espHoje) ? `${fmtBRL(s.realizado)} de ${fmtBRL(s.espHoje)} esperados` : null]));
        secundarios = [
          { label: 'Realizado (mês)', oculto: true, fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(list.map(s => [s.slug, s.realizado])) },
          { label: 'Esperado até hoje', oculto: true, fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(list.map(s => [s.slug, s.espHoje])) },
          { label: 'Meta mensal', fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(list.map(s => [s.slug, s.meta])) },
          { label: 'Atingimento da meta (mês)', fmt: 'pct1', lowerBetter: false, colorize: true, vsTarget: 100, valores: Object.fromEntries(list.map(s => [s.slug, s.atg])) },
        ];
        attentionRowLabel = undefined;
        primaryVsTarget = Object.fromEntries(list.map(s => [s.slug, s.primaryValue != null ? 100 : null]));
        // Banner do GRUPO (pedido 18/08, mesmo formato do B2C): meta somada nos chips +
        // realizado vs esperado do grupo no banner.
        const metaGrupo = list.reduce((a, s) => a + (s.meta || 0), 0);
        const realGrupo = list.reduce((a, s) => a + (s.realizado || 0), 0);
        const espGrupo = list.reduce((a, s) => a + (s.espHoje || 0), 0);
        const ritmoGrupo = espGrupo > 0 ? (realGrupo / espGrupo) * 100 : null;
        if (metaGrupo > 0) headerChips = [`Meta do grupo (mês): ${fmtBRL(metaGrupo)}`, `Esperado até hoje: ${fmtBRL(espGrupo)}`];
        consolidado = ritmoGrupo != null ? {
          titulo: `Grupo ${nome} (todas as unidades)`,
          ritmo: ritmoGrupo, realizado: realGrupo, esperado: espGrupo,
          frase: `${nome} está em ${d1BR(ritmoGrupo)}% do ritmo esperado até hoje.`,
        } : null;
        cardsList = [];  // as barras coloridas vs esperado já contam a história
        iaContexto = {
          indicador_principal: 'Atingimento vs esperado até hoje (%) — comparação justa (metas diferentes por unidade)',
          direcao: 'quanto MAIOR, melhor (100% = no ritmo)',
          indicadores_secundarios: ['Realizado', 'Esperado até hoje', 'Atingimento da meta (mês)'],
          nao_usar_para_desempenho: ['valor absoluto vendido isolado por unidade (metas são diferentes)'],
          observacoes: consolidado ? `Grupo ${nome}: meta do mês ${fmtBRL(metaGrupo)}; vendido ${fmtBRL(realGrupo)} de ${fmtBRL(espGrupo)} esperados até hoje (${d1BR(ritmoGrupo)}%).` : '',
        };
      }
      return {
        ...base, template: 'vendas', bloco: 'Vendas',
        primaryLabel, primaryFmt, lowerIsBetterPrimary: false,
        stores: list, secundarios, attentionRowLabel,
        ...(headerChips ? { headerChips } : {}),
        ...(primaryVsTarget ? { primaryVsTarget } : {}),
        ...(consolidado ? { consolidado } : {}),
        ...(cardsList ? { cards: cardsList } : {}),
        ...(iaContexto ? { iaContexto } : {}),
        ...(nota ? { nota } : {}),
        ...(primarySub ? { primarySub } : {}),
        insight: buildInsight(list, { lowerIsBetter: false, fmtName: v => primaryFmt === 'pct1' ? `${d1BR(v)}% do esperado` : fmtBRL(v) }),
      };
    }

    // Taxa de Conversão do Qless: quantas senhas viraram Service (OS aberta).
    // Services = totalOS MX + totalOS LM Balcão. Conversão = Services / Senhas Qless.
    if (metrica.type === 'conversao') {
      // Services = ENTRADAS do Balcão CI (IW + OW), independente de aprovação —
      // mesma fonte do slide "Balcão (CI)". Mede quantas senhas viraram OS no balcão.
      const list = companies
        .filter(c => !(HIDDEN_TABS[c.slug] || []).includes('recepcao'))
        .map(c => {
          const senhas = summarizeRecepcao(results[c.slug]?.recepcao_diario)?.qtd ?? null;
          const bal = summarizeSection(results[c.slug]?.balcao_total);
          const services = bal ? (bal.eTotal ?? 0) : 0;   // IW + OW
          const taxa = (senhas && senhas > 0) ? (services / senhas) * 100 : null;
          return {
            slug: c.slug, name: c.name, color: themeColor(c.slug),
            primaryValue: taxa, senhas, services,
            eIw: bal?.eIw ?? null, eOw: bal?.eOw ?? null,
          };
        });
      return {
        ...base,
        primaryLabel: 'Taxa de Conversão (Services / Senhas)', primaryFmt: 'pct1', lowerIsBetterPrimary: false,
        stores: list,
        secundarios: [
          { label: 'Services (IW + OW)', hint: 'entradas do Balcão CI', fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(list.map(s => [s.slug, s.services])) },
          { label: 'Senhas Qless', fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(list.map(s => [s.slug, s.senhas])) },
        ],
        insight: buildInsight(list, { lowerIsBetter: false, fmtName: v => `${d1BR(v)}% de conversão` }),
      };
    }

    // ===== MODELO 1 — RECEPÇÃO / NÍVEL DE SERVIÇO =====
    // Desempenho = TME/TMA (menor melhor). VOLUME de senhas = CONTEXTO (varia com
    // cidade/demanda/repetição do cliente) e NÃO define a melhor unidade.
    if (metrica.type === 'recepcao') {
      const stores = cards.map(({ company, summary }) => {
        const hc = headcount[company.slug] || {};
        const recepcionistas = hc.recepcionistas || 0;
        const qtd = summary?.qtd ?? null;
        const prod = (recepcionistas > 0 && qtd != null) ? qtd / recepcionistas : null;
        const prevSummary = summarizeRecepcao(resultsPrev[company.slug]?.recepcao_diario);
        const avg = prevSummary?.qtd || 0;
        const delta = (qtd != null && avg) ? ((qtd - avg) / avg) * 100 : null;
        return {
          slug: company.slug, name: company.name, color: themeColor(company.slug),
          primaryValue: summary?.tme ?? null,   // ranking/roteiro pelo NÍVEL DE SERVIÇO
          qtd, recepcionistas: recepcionistas || null,
          prod, tme: summary?.tme ?? null, tma: summary?.tma ?? null,
          tmePrev: prevSummary?.tme ?? null, tmaPrev: prevSummary?.tma ?? null, delta,
        };
      });
      // Sem cards: os painéis TME/TMA já mostram melhor (coroa) e pior (fim da lista).
      return {
        ...base, template: 'servico', bloco: 'Operação',
        primaryLabel: 'Nível de serviço (TME / TMA)', primaryFmt: 'ms', lowerIsBetterPrimary: true,
        stores,
        metaNota: 'Metas de TME/TMA ainda não cadastradas — comparação apenas entre unidades, sem farol de meta.',
        // VOLUME em destaque (painel próprio): junto com a variação vs mês anterior,
        // é o que embasa decisão de equipe (contratar/reduzir) — pediu o Rafhael.
        volumePanel: {
          titulo: 'Volume de senhas no período',
          itens: stores.map(s => ({ slug: s.slug, name: s.name, color: s.color, qtd: s.qtd, delta: s.delta, porRecep: s.prod })),
        },
        secundarios: [
          // TME/TMA ficam nos painéis do topo; estas 2 linhas são SÓ para a IA (ocultas na tabela).
          { label: 'TME — tempo médio de espera', oculto: true, fmt: 'ms', lowerBetter: true, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.tme])) },
          { label: 'TMA — tempo médio de atendimento', oculto: true, fmt: 'ms', lowerBetter: true, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.tma])) },
          { label: 'Senhas no período', oculto: true, fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.qtd])) },
          { label: 'Variação de senhas vs mês anterior', oculto: true, fmt: 'pct1', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.delta])) },
          { label: 'Recepcionistas', fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.recepcionistas])) },
          { label: 'Senhas por recepcionista', hint: 'carga média', fmt: 'dec1', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.prod])) },
        ],
        cards: [],
        iaContexto: {
          indicador_principal: 'TME e TMA (tempo de espera e de atendimento)',
          direcao: 'quanto MENOR, melhor',
          indicadores_contexto: ['Senhas no período', 'Recepcionistas', 'Senhas por recepcionista'],
          nao_usar_para_desempenho: ['volume de senhas', 'senhas por recepcionista'],
          observacoes: 'O volume de senhas e a variação vs mês anterior são MUITO relevantes (dimensionamento de equipe) — PODE citar a variação de volume como fato, mas sem usá-la para dizer quem atende melhor.',
        },
        insight: buildInsight(stores, { lowerIsBetter: true, fmtName: v => `TME ${fmtMS(v)}` }),
      };
    }

    if (metrica.type === 'gdta') {
      // "Esperado pra hoje" proporcional aos DIAS ÚTEIS decorridos (lojas abrem só seg-sex).
      const stores = cards.map(({ company, summary }) => {
        // Fator por LOJA: dias úteis da cidade de cada uma (feriados locais).
        const fator = businessMonthFactor(dataFim, company.slug);
        const balcaoData = results[company.slug]?.balcao_total;
        const balcaoMX = balcaoData ? sumDaily(balcaoData.entradas_diario || [], 'iw', 'ow') : 0;
        const factorPct = gdFactors[company.slug] ?? 50;
        const metaFixa = gdMetasFixas[company.slug] || 0;
        const gd = summary?.gds ?? null;
        // Meta FIXA (manual) tem prioridade sobre o % do Balcão, quando definida (> 0).
        const metaProjetada = metaFixa > 0 ? Math.round(metaFixa) : Math.round((balcaoMX || 0) * (factorPct / 100));
        const esperadoHoje = Math.round(metaProjetada * fator);
        const atingimento = metaProjetada > 0 && gd != null ? (gd / metaProjetada) * 100 : null;
        const atingHoje = esperadoHoje > 0 && gd != null ? (gd / esperadoHoje) * 100 : null;
        return {
          slug: company.slug, name: company.name, color: themeColor(company.slug),
          // PRIMÁRIO agora é o % de atingimento da meta (comparação JUSTA — não o volume,
          // que sempre deixaria a JMV em 1º). GD realizados vira linha da tabela.
          primaryValue: atingimento,
          gd, esperadoHoje, metaProjetada, atingimento, atingHoje, balcaoMX, fator,
        };
      });
      // MODELO 2 (GD): o principal é o ATINGIMENTO DO ESPERADO ATÉ HOJE (ritmo por
      // dias úteis) — não o % da meta mensal (que sempre parece baixo no meio do mês).
      const gdStores = stores.map(s => ({
        ...s,
        primaryValue: s.atingHoje,
        falta: (s.esperadoHoje != null && s.gd != null) ? Math.max(0, s.esperadoHoje - s.gd) : null,
        projecao: (s.fator > 0 && s.gd != null) ? s.gd / s.fator : null,
      }));
      return {
        ...base, template: 'meta', bloco: 'Operação',
        primaryLabel: 'Atingimento do esperado até hoje', primaryFmt: 'pct1', lowerIsBetterPrimary: false,
        stores: gdStores,
        primaryVsTarget: Object.fromEntries(gdStores.map(s => [s.slug, s.atingHoje != null ? 100 : null])),
        // Memória na barra (igual Acessórios): quanto FEZ de quanto era ESPERADO.
        primarySub: Object.fromEntries(gdStores.map(s => [s.slug, (s.gd != null && s.esperadoHoje != null) ? `${fmtNum(s.gd)} GDs de ${fmtNum(s.esperadoHoje)} esperados` : null])),
        secundarios: [
          { label: 'Meta mensal', fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(gdStores.map(s => [s.slug, s.metaProjetada])) },
          { label: 'Atingimento da meta mensal', fmt: 'pct1', lowerBetter: false, colorize: true, vsTarget: 100, valores: Object.fromEntries(gdStores.map(s => [s.slug, s.atingimento])) },
          // Só p/ a IA (já estão na memória da barra).
          { label: 'GD válidos (COMPLETION = Y)', oculto: true, fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(gdStores.map(s => [s.slug, s.gd])) },
          { label: 'Esperado até hoje', oculto: true, fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(gdStores.map(s => [s.slug, s.esperadoHoje])) },
        ],
        cards: [],  // as barras coloridas já mostram quem está no ritmo e quem não está
        iaContexto: {
          indicador_principal: 'Atingimento do esperado até hoje (ritmo por dias úteis)',
          direcao: 'quanto MAIOR, melhor (100% = no ritmo da meta)',
          meta: '100% do esperado até hoje',
          nao_usar_para_desempenho: ['volume absoluto de GD realizados'],
          observacoes: 'As metas de GD são fixas por unidade.',
        },
        insight: buildInsight(gdStores, { lowerIsBetter: false, fmtName: v => `${d1BR(v)}% do esperado` }),
      };
    }

    if (metrica.type === 'section') {
      // Abas do snapshot de backlog que compõem cada slide (p/ "vs semana anterior").
      const BK_PREV_ABAS = { balcao: ['balcaoCI'], ih: ['ih', 'ii'], sr: ['sr'], ii: ['ii'], total: ['balcaoCI', 'ih', 'ii'], ih_completo: ['ih_completo'] };
      const stores = cards.map(({ company, summary }) => {
        const eTotal = summary?.eTotal ?? null;
        const backlog = summary?.backlog?.total ?? null;
        const bp = results[company.slug]?.backlog_prev;
        const prevAbas = BK_PREV_ABAS[metrica.key];
        // Retrato sem alguma das abas (ex.: anterior a 08/10 p/ o In Home Completo) = sem comparação, não 0.
        const prevBk = (bp?.abas && prevAbas && prevAbas.every(k => bp.abas[k] != null)) ? prevAbas.reduce((a, k) => a + bp.abas[k], 0) : null;
        // Backlog % = backlog / volumetria (entradas). Indicador de desempenho.
        const backlogPct = (backlog != null && eTotal > 0) ? (backlog / eTotal) * 100 : null;
        return {
          slug: company.slug, name: company.name, color: themeColor(company.slug),
          primaryValue: summary && (summary.eTotal || summary.rTotal || (summary.backlog?.total || 0)) ? summary.eTotal : null,
          eTotal, rTotal: summary?.rTotal ?? null,
          scont: summary?.scont ?? null,
          eIw: summary?.eIw ?? null, eOw: summary?.eOw ?? null,
          pctIw: (summary && summary.eIw > 0) ? (summary.rIw / summary.eIw) * 100 : null,
          pctOw: (summary && summary.eOw > 0) ? (summary.rOw / summary.eOw) * 100 : null,
          backlog, backlogPct,
          prevBk, prevData: bp?.data || null,
        };
      });
      // Produtividade (pedido 08/10/2026): saiu do slide In Home (que voltou a ter as
      // taxas de reparo IW/OW) e vive SÓ no slide "In Home Completo" (IH + II + RH + SR):
      // Services por ADM = entradas do período ÷ ADMs In Home (campo único, sem IW/OW);
      // Reparos por técnico = reparos ÷ técnicos LM + LB + híbridos (híbrido conta 1).
      const isCompleto = metrica.key === 'ih_completo';
      // SCONT (13/10/2026): OS fechada em X09 por falta de contato vem com "SCONT" na frente
      // do ASC Job. Nos slides In Home e In Home Completo entram "Sem contato (SCONT)" e
      // "OS realizadas" = reparos − SCONT; Reparos por técnico usa só as realizadas.
      const temScont = metrica.key === 'ih' || isCompleto;
      const sconts = temScont ? Object.fromEntries(stores.map(s => [s.slug, s.scont])) : null;
      const realizadas = temScont ? Object.fromEntries(stores.map(s =>
        [s.slug, (s.rTotal != null && s.scont != null) ? Math.max(0, s.rTotal - s.scont) : null])) : null;
      const linhasScont = temScont ? [
        { label: 'Sem contato (SCONT)', fmt: 'num', lowerBetter: true, colorize: false, valores: sconts },
        { label: 'OS realizadas', fmt: 'num', lowerBetter: false, colorize: false, valores: realizadas },
      ] : [];
      const _hcNum = (slug, f) => Number((headcount[slug] || {})[f] || 0);
      const tecsIH = isCompleto ? Object.fromEntries(stores.map(s => {
        const t = _hcNum(s.slug, 'tecnicos_lm') + _hcNum(s.slug, 'tecnicos_lb') + _hcNum(s.slug, 'tecnicos_lm_lb');
        return [s.slug, t || null];
      })) : null;
      const prodIH = isCompleto ? Object.fromEntries(stores.map(s => {
        const t = tecsIH[s.slug];
        const feitas = realizadas[s.slug] ?? s.rTotal;   // sem contato não teve visita
        return [s.slug, (t > 0 && feitas != null) ? feitas / t : null];
      })) : null;
      const adms = isCompleto ? Object.fromEntries(stores.map(s => [s.slug, _hcNum(s.slug, 'adm_in_home') || null])) : null;
      const prodAdm = isCompleto ? Object.fromEntries(stores.map(s =>
        [s.slug, (adms[s.slug] > 0 && s.eTotal != null) ? s.eTotal / adms[s.slug] : null])) : null;
      // MODELO 3 — FLUXO/BACKLOG: desempenho = backlog PROPORCIONAL + taxas de reparo.
      // Entradas (volume) viram CONTEXTO — quem recebe mais não é "melhor" por isso.
      const temBacklog = stores.some(s => s.backlogPct != null && isFinite(s.backlogPct));
      const fluxoStores = stores.map(s => ({ ...s, primaryValue: temBacklog ? s.backlogPct : s.pctIw }));
      const taxaAcima100 = stores.some(s => (s.pctIw != null && s.pctIw > 100) || (s.pctOw != null && s.pctOw > 100));
      // Farol COMPARATIVO entre lojas (não há meta de backlog): verde = menor backlog
      // proporcional do grupo · amarelo = até 10% acima da melhor · vermelho = acima disso.
      let primaryStatus = null;
      if (temBacklog) {
        const comPct = fluxoStores.filter(s => s.primaryValue != null && isFinite(s.primaryValue));
        if (comPct.length >= 2) {
          const melhor = Math.min(...comPct.map(s => s.primaryValue));
          primaryStatus = Object.fromEntries(comPct.map(s => [s.slug,
            s.primaryValue === melhor ? 'green' : (s.primaryValue <= melhor * 1.10 ? 'amber' : 'red')]));
        }
      }
      return {
        ...base, template: 'fluxo', bloco: 'Operação',
        primaryLabel: temBacklog ? 'Backlog proporcional às entradas' : 'Taxa de Reparo IW',
        primaryFmt: temBacklog ? 'pct1' : 'pct0', lowerIsBetterPrimary: temBacklog,
        stores: fluxoStores,
        // Base de cálculo embaixo do %: dá o MOTIVO do número (backlog ÷ entradas).
        primarySub: temBacklog
          ? Object.fromEntries(fluxoStores.map(s => [s.slug, (s.backlog != null && s.eTotal) ? `${fmtNum(s.backlog)} em backlog de ${fmtNum(s.eTotal)} entradas` : null]))
          : undefined,
        ...(primaryStatus ? { primaryStatus } : {}),
        fluxo: {
          // Bloco 1: backlog ABSOLUTO (o proporcional já é a barra do topo — não repetir).
          backlogAbs: Object.fromEntries(stores.map(s => [s.slug, s.backlog])),
          prev: Object.fromEntries(stores.map(s => [s.slug, s.prevBk != null ? { total: s.prevBk, data: s.prevData } : null])),
          // Bloco 2: VOLUME em destaque — entradas e reparos importam (dimensionamento/fluxo).
          volume: stores.map(s => ({ slug: s.slug, name: s.name, color: s.color, entradas: s.eTotal, reparos: s.rTotal })),
        },
        secundarios: [
          // In Home Completo: no lugar das taxas de reparo entram as produtividades (08/10).
          ...(isCompleto ? [
            { label: 'Services por ADM', fmt: 'dec1', lowerBetter: false, colorize: true, valores: prodAdm },
            { label: 'ADMs', fmt: 'dec1', lowerBetter: false, colorize: false, valores: adms },
            ...linhasScont,
            { label: 'Reparos por técnico In Home', fmt: 'dec1', lowerBetter: false, colorize: true, valores: prodIH },
            { label: 'Técnicos In Home', fmt: 'dec1', lowerBetter: false, colorize: false, valores: tecsIH },
          ] : [
            { label: 'Taxa Reparo IW', fmt: 'pct0', lowerBetter: false, colorize: true, bases: Object.fromEntries(stores.map(s => [s.slug, s.eIw])), valores: Object.fromEntries(stores.map(s => [s.slug, s.pctIw])) },
            { label: 'Taxa Reparo OW', fmt: 'pct0', lowerBetter: false, colorize: true, bases: Object.fromEntries(stores.map(s => [s.slug, s.eOw])), valores: Object.fromEntries(stores.map(s => [s.slug, s.pctOw])) },
            ...linhasScont,
          ]),
          // Linhas só p/ a IA (já aparecem nos blocos de destaque acima da tabela).
          { label: 'Backlog atual (OS)', oculto: true, fmt: 'num', lowerBetter: true, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.backlog])) },
          ...(stores.some(s => s.prevBk != null) ? [{ label: 'Backlog na semana anterior (OS)', oculto: true, fmt: 'num', lowerBetter: true, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.prevBk])) }] : []),
          { label: 'Entradas no período', oculto: true, fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.eTotal])) },
          { label: 'Reparos realizados', oculto: true, fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.rTotal])) },
        ],
        cards: [],  // as barras (backlog proporcional) já mostram melhor/pior
        iaContexto: {
          indicador_principal: temBacklog ? 'Backlog proporcional às entradas (%)' : 'Taxa de reparo IW',
          direcao: temBacklog ? 'quanto MENOR o backlog proporcional, melhor' : 'quanto MAIOR a taxa, melhor',
          cores_das_barras: 'comparativas ENTRE as lojas (verde = menor backlog proporcional do grupo) — não significam meta atingida',
          comparacao_semanal: 'se houver "Backlog na semana anterior", a variação (subiu/caiu) PODE ser citada como fato — queda é positiva',
          indicadores_secundarios: isCompleto
            ? ['Backlog atual (OS)', 'Services por ADM', 'Reparos por técnico In Home']
            : ['Taxa Reparo IW', 'Taxa Reparo OW', 'Backlog atual (OS)'],
          indicadores_contexto: ['Entradas no período', 'Reparos realizados', ...(temScont ? ['Sem contato (SCONT)', 'OS realizadas'] : []), ...(isCompleto ? ['ADMs', 'Técnicos In Home'] : [])],
          nao_usar_para_desempenho: ['volume de entradas', 'backlog absoluto isolado'],
          observacao_volume: 'Entradas e reparos são fatos importantes (fluxo/dimensionamento) — PODE citá-los, sem usá-los para dizer quem é melhor.',
          observacoes: 'Diferenciar SEMPRE backlog absoluto (OS) de backlog proporcional (%). ' + (taxaAcima100 && !isCompleto ? 'Taxas >100% podem incluir reparos de OS de períodos anteriores. ' : '') + (isCompleto ? 'In Home Completo = IH + II + RH + SR dos produtos de In Home (sem balcão, sem celular). Produtividades: Services por ADM = ENTRADAS do período ÷ ADMs da equipe; Reparos por técnico = OS realizadas ÷ técnicos (LM + LB + híbridos). Loja sem equipe informada aparece "—".' : '') + (temScont ? ' "Sem contato (SCONT)" = OS fechadas sem conseguir contato com o cliente (não houve atendimento); "OS realizadas" = reparos − sem contato. A marcação SCONT começou em 13/10/2026: zero antes dessa data não significa que não houve casos.' : ''),
        },
        insight: temBacklog
          ? buildInsight(fluxoStores, { lowerIsBetter: true, fmtName: v => `${d1BR(v)}% de backlog proporcional` })
          : buildInsight(fluxoStores, { lowerIsBetter: false, fmtName: v => `${v.toFixed(0)}% de taxa IW` }),
      };
    }

    if (metrica.type === 'aprovacoes') {
      // Produtividade/Técnicos agora vivem no slide consolidado 'produtividade'.
      const stores = cards.map(({ company, summary: aba }) => {
        const espReceita = aba?.metaReceita ? (aba.metaReceita * businessMonthFactor(dataFim, company.slug)) : 0;
        return {
          slug: company.slug, name: company.name, color: themeColor(company.slug),
          primaryValue: aba ? (aba.approvalRate || 0) : null,
          metaAprovacao: aba?.metaAprovacao ?? null,
          totalRevenue: aba?.totalRevenue ?? null,
          espReceita: aba ? espReceita : null,
          metaReceita: aba?.metaReceita ?? null,
          ticketMedio: (aba && aba.ticketMedio > 0) ? aba.ticketMedio : null,
          totalOS: aba?.totalOS ?? null,
          approvedOS: aba?.approvedOS ?? null,
          reprovadas: (aba && aba.totalOS != null && aba.approvedOS != null) ? (aba.totalOS - aba.approvedOS) : null,
        };
      });
      // MODELO 2 — META/APROVAÇÃO/RECEITA: bloco TÉCNICO (taxa vs meta) separado do
      // bloco FINANCEIRO (receita vs esperado). Destaques independentes — não existe
      // "uma vencedora" quando cada unidade se destaca em indicadores diferentes.
      const aprovStores = stores.map(s => ({
        ...s,
        taxaAprov: s.primaryValue,
        deltaPp: (s.primaryValue != null && s.metaAprovacao != null) ? s.primaryValue - s.metaAprovacao : null,
        atgReceita: (s.espReceita > 0 && s.totalRevenue != null) ? (s.totalRevenue / s.espReceita) * 100 : null,
        faltaReceita: (s.espReceita > 0 && s.totalRevenue != null) ? Math.max(0, s.espReceita - s.totalRevenue) : null,
        // Projeção pelo MESMO rateio do esperado (dias corridos do período/mês) — sem regra nova.
        projReceita: (s.totalRevenue != null && businessMonthFactor(dataFim, s.slug) > 0) ? s.totalRevenue / businessMonthFactor(dataFim, s.slug) : null,
      }));
      // BARRA = venda vs esperado até hoje (igual Acessórios). A taxa de aprovação vira
      // linha da tabela (colorida vs a META da própria loja). Sem cards — tudo já está na tela.
      const secundarios = [
        { label: 'Meta mensal (receita)', fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(aprovStores.map(s => [s.slug, s.metaReceita])) },
        { label: 'Ticket médio', fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(aprovStores.map(s => [s.slug, s.ticketMedio])) },
        // Só p/ a IA — já aparecem no painel de destaque de aprovação.
        { label: 'Taxa de aprovação', oculto: true, fmt: 'pct1', lowerBetter: false, colorize: false, valores: Object.fromEntries(aprovStores.map(s => [s.slug, s.taxaAprov])) },
        { label: 'Meta de aprovação', oculto: true, fmt: 'pct0', lowerBetter: false, colorize: false, valores: Object.fromEntries(aprovStores.map(s => [s.slug, s.metaAprovacao])) },
        { label: 'Services (OS)', oculto: true, fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(aprovStores.map(s => [s.slug, s.totalOS])) },
        { label: 'Aprovadas', oculto: true, fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(aprovStores.map(s => [s.slug, s.approvedOS])) },
        { label: 'Não reparadas', oculto: true, fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(aprovStores.map(s => [s.slug, s.reprovadas])) },
      ];
      // Painel de destaque (igual o de entradas/reparos do Balcão): taxa vs meta + aprovadas/reprovadas.
      const aprovPanel = {
        itens: aprovStores.map(s => ({
          slug: s.slug, name: s.name, color: s.color,
          taxa: s.taxaAprov, meta: s.metaAprovacao,
          aprovadas: s.approvedOS, reprovadas: s.reprovadas, services: s.totalOS,
        })),
      };
      const financeiro = null;
      const aprovCards = [];
      const aprovComVenda = aprovStores.map(s => ({ ...s, primaryValue: s.atgReceita }));
      return {
        template: 'meta', bloco: 'Operação',
        aprovPanel,
        cards: aprovCards,
        primarySub: Object.fromEntries(aprovComVenda.map(s => [s.slug, (s.totalRevenue != null && s.espReceita) ? `${fmtBRL(s.totalRevenue)} de ${fmtBRL(s.espReceita)} esperados` : null])),
        iaContexto: {
          indicador_principal: 'Venda (receita) vs ESPERADO até hoje (%) — 100% = no ritmo',
          direcao: 'quanto MAIOR, melhor (comparar sempre com o esperado de CADA unidade)',
          indicadores_secundarios: ['Taxa de aprovação vs meta da unidade', 'Ticket médio'],
          indicadores_contexto: ['Services (OS)', 'Aprovadas', 'Não reparadas', 'Meta mensal (receita)'],
          nao_usar_para_desempenho: ['volume de services', 'receita absoluta sem comparar com o esperado da própria unidade'],
          observacoes: 'Unidades diferentes podem se destacar em indicadores diferentes (receita ≠ taxa de aprovação ≠ ticket) — não eleger uma única vencedora.',
        },
        ...base,
        primaryLabel: 'Atingimento vs esperado até hoje', primaryFmt: 'pct1', lowerIsBetterPrimary: false,
        stores: aprovComVenda, secundarios,
        // Barra verde/âmbar/vermelho conforme o ritmo da RECEITA vs o esperado (100%).
        primaryVsTarget: Object.fromEntries(aprovComVenda.map(s => [s.slug, s.atgReceita != null ? 100 : null])),
        insight: buildInsight(aprovComVenda, { lowerIsBetter: false, fmtName: v => `${d1BR(v)}% do esperado` }),
      };
    }

    // Slides de Produtividade (OS por técnico). Quais segmentos entram vem de `metrica.segs`:
    //   - 'produtividade_mx' → ['mx']  (bancada — OS da aba de aprovações 'mx')
    //   - 'produtividade_lm' → ['lm']  (In Home LM/TV — OS do GSPN In Home) + 0,5 dos híbridos
    //   - 'produtividade_lb' → ['lb']  (In Home Linha Branca — OS do GSPN In Home) + 0,5 dos híbridos
    // FONTE DAS OS:
    //   mx → aba de aprovações 'mx' (totalOS).
    //   lm → prod_lm: IH+II+SR de TV/monitor/soundbar (VD/MON/AV) — definição das lojas.
    //   lb → prod_lb: IH+II+SR de lavadora/geladeira/ar (WM/REF/AC) — definição das lojas.
    // (ih_ce/ih_da continuam intactas p/ o slide In Home / paridade com a planilha.)
    // Combinado (metrica.combinedTecField): técnicos que fazem LM E LB → (OS LM + OS LB) ÷ esses técnicos.
    // Respeita HIDDEN_TABS: se a loja esconde o segmento, ele fica '—'.
    if (metrica.type === 'produtividade') {
      // OS por segmento (null = sem dado → não entra na conta).
      const osByKey = {
        // MX = NOVA métrica: toda OS de CELULAR do GSPN ACC (modelo começando com "SM").
        mx: (c) => { const s = summarizeSection(results[c.slug]?.mx_celular); return s ? (s.eTotal ?? 0) : null; },
        lm: (c) => { const s = summarizeSection(results[c.slug]?.prod_lm); return s ? (s.eTotal ?? 0) : null; },
        lb: (c) => { const s = summarizeSection(results[c.slug]?.prod_lb); return s ? (s.eTotal ?? 0) : null; },
      };
      const ALL_SEGMENTS = {
        mx: { label: 'MX',           tecField: 'tecnicos_mx', hideKey: 'aprov_mx' },
        lm: { label: 'LM In-Home',   tecField: 'tecnicos_lm', hideKey: 'aprov_lm_ih' },
        lb: { label: 'Linha Branca', tecField: 'tecnicos_lb', hideKey: 'aprov_lb' },
      };
      const segList = (metrica.segs || ['mx', 'lm', 'lb']).map(k => ({ key: k, ...ALL_SEGMENTS[k] }));
      const single = segList.length === 1;
      const hasCombined = !!metrica.combinedTecField;
      // Loja que esconde TODOS os segmentos deste slide sai fora (não vira coluna "—").
      // Ex.: FIX CSP no In Home (esconde LM In-Home e Linha Branca); FIX MSC no MX.
      const visibleCards = cards.filter(({ company }) => {
        const hf = HIDDEN_TABS[company.slug] || [];
        return segList.some(sg => !hf.includes(sg.hideKey));
      });
      const stores = visibleCards.map(({ company }) => {
        const hc = headcount[company.slug] || {};
        const hiddenFor = HIDDEN_TABS[company.slug] || [];
        // Técnicos HÍBRIDOS (fazem LM e LB): cada um vale 1 no total, rateado 0,5 p/ LM + 0,5 p/ LB.
        const hib = hasCombined ? (hc[metrica.combinedTecField] || 0) : 0;
        const hibShare = hib * 0.5;
        const seg = {};
        let totOS = 0, anyOS = false, denomEfetivo = 0;
        for (const sg of segList) {
          const isHidden = hiddenFor.includes(sg.hideKey);
          const os = isHidden ? null : osByKey[sg.key](company);
          const informado = hc[sg.tecField] || 0;
          // Técnicos EFETIVOS do segmento = informados + parcela dos híbridos (0,5 cada · só In Home).
          const share = (hasCombined && !isHidden) ? hibShare : 0;
          const efetivo = informado + share;
          const prod = (os != null && efetivo > 0) ? os / efetivo : null;
          seg[sg.key] = {
            prod,
            tecEfetivo: (!isHidden && efetivo > 0) ? efetivo : null,
            tecInformado: informado,
            hibShare: share,
            os: isHidden ? null : os,
          };
          // Denominador geral = soma dos técnicos EFETIVOS. No combinado dá (LM+LB+híbridos);
          // num slide de 1 linha só dá (linha + 0,5×híbridos). Nunca duplica os híbridos.
          if (os != null) { totOS += os; anyOS = true; denomEfetivo += efetivo; }
        }
        const prodGeral = (anyOS && denomEfetivo > 0) ? totOS / denomEfetivo : null;
        const combined = hasCombined ? { tec: hib > 0 ? hib : null, hibShare } : null;
        return { slug: company.slug, name: company.name, color: themeColor(company.slug), primaryValue: prodGeral, seg, combined, totOS: anyOS ? totOS : null, totTec: denomEfetivo > 0 ? denomEfetivo : null };
      });
      const bySeg = (k, field) => Object.fromEntries(stores.map(s => [s.slug, s.seg[k][field]]));
      const byCombined = (field) => Object.fromEntries(stores.map(s => [s.slug, s.combined ? s.combined[field] : null]));
      // ===== MODELO 4 — PRODUTIVIDADE E CAPACIDADE =====
      // INDICADOR PRINCIPAL: "OS por técnico EQUIVALENTE por dia útil" (barra do topo).
      // Total de OS = CONTEXTO (quem tem mais técnicos naturalmente faz mais OS).
      // Dias úteis POR LOJA: cada cidade tem feriados locais próprios (FERIADOS_LOCAIS).
      const diasUteisLoja = Object.fromEntries(stores.map(s => [s.slug, businessDaysBetween(dataInicio, dataFim, s.slug)]));
      const d1 = (x) => d1BR(x);
      // primaryValue passa a ser POR DIA ÚTIL; a produtividade do período vira linha.
      const prodStores = stores.map(s => ({
        ...s,
        periodoProd: s.primaryValue,
        primaryValue: (s.primaryValue != null && (diasUteisLoja[s.slug] || 0) > 0) ? s.primaryValue / diasUteisLoja[s.slug] : null,
      }));
      const primarySub = Object.fromEntries(prodStores.map(s => [s.slug,
        (s.periodoProd != null && (diasUteisLoja[s.slug] || 0) > 0) ? `${d1(s.periodoProd)} no período ÷ ${diasUteisLoja[s.slug]} dias úteis` : null]));
      const secundarios = [];
      // 2) Produtividade no período + memória (OS ÷ técnicos equivalentes).
      secundarios.push({
        label: 'Produtividade no período (OS/téc equivalente)', fmt: 'dec1', lowerBetter: false, colorize: true,
        valores: Object.fromEntries(prodStores.map(s => [s.slug, s.periodoProd])),
        subvals: Object.fromEntries(prodStores.map(s => [s.slug, (s.totOS != null && s.totTec) ? `${fmtNum(s.totOS)} OS ÷ ${fmtNum(s.totTec)} técnicos equivalentes` : null])),
      });
      if (!single) {
        for (const sg of segList) secundarios.push({ label: `Produtividade ${sg.label} (OS/téc)`, fmt: 'dec1', lowerBetter: false, colorize: true, valores: bySeg(sg.key, 'prod') });
      }
      // 3) Técnicos EQUIVALENTES (com o rateio dos híbridos visível).
      for (const sg of segList) {
        const nome = sg.key === 'lm' ? 'LM' : (sg.key === 'lb' ? 'LB' : sg.label);
        secundarios.push({
          label: single ? 'Técnicos equivalentes' : `Técnicos equivalentes — ${sg.label}`, fmt: 'num', lowerBetter: false, colorize: false,
          valores: bySeg(sg.key, 'tecEfetivo'),
          subvals: Object.fromEntries(prodStores.map(s => {
            const g = s.seg[sg.key];
            if (!g || g.tecEfetivo == null || !(g.hibShare > 0)) return [s.slug, null];
            return [s.slug, `${fmtNum(g.tecInformado)} técnicos ${nome} + ${fmtNum(g.hibShare)} provenientes dos híbridos`];
          })),
        });
      }
      // Híbridos = INFORMATIVO (já distribuídos 0,5 + 0,5 — nunca somados de novo).
      if (hasCombined) secundarios.push({
        label: `Técnicos ${metrica.combinedTecLabel || metrica.combinedLabel}`, fmt: 'num', lowerBetter: false, colorize: false,
        info: true,
        badge: 'Informativo — já distribuídos em LM e Linha Branca',
        tooltip: 'Os técnicos híbridos não são somados novamente. Cada híbrido é distribuído como 0,5 em LM e 0,5 em Linha Branca.',
        valores: byCombined('tec'),
        subvals: Object.fromEntries(prodStores.map(s => {
          const c = s.combined;
          if (!c || c.tec == null || !(c.hibShare > 0)) return [s.slug, null];
          return [s.slug, `${fmtNum(c.hibShare)} para LM + ${fmtNum(c.hibShare)} para Linha Branca`];
        })),
      });
      // 4) Total de OS = CONTEXTO.
      secundarios.push({
        label: 'Total de OS realizadas', hint: 'contexto — não define desempenho', fmt: 'num', lowerBetter: false, colorize: false,
        valores: Object.fromEntries(prodStores.map(s => [s.slug, s.totOS])),
      });
      // Contexto operacional: FIX MSC atende em ROTA (outras cidades) nos slides In Home.
      const isRota = metrica.key === 'produtividade_lm' || metrica.key === 'produtividade_lb';
      const notaRota = isRota && prodStores.some(s => s.slug === 'fix-msc')
        ? 'A FIX MSC realiza atendimentos em rota e atende outras cidades. A produtividade baseada somente na quantidade de OS não considera o tempo de deslocamento.'
        : null;
      const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const y = Number((dataInicio || '').slice(0, 4)), mo = Number((dataInicio || '').slice(5, 7));
      const periodoLabel = (mo >= 1 && mo <= 12) ? `${MESES[mo - 1]}/${y}` : '';
      const storeDesc = Object.fromEntries(prodStores.map(s => {
        if (s.primaryValue == null) return [s.slug, null];
        return [s.slug, [`${d1(s.primaryValue)} OS por técnico equivalente/dia útil`, `${d1(s.periodoProd)} OS/téc no período`]];
      }));
      // Sem cards: as barras já mostram maior/menor; a nota de rota fica no banner.
      const barLabel = 'OS por técnico equivalente / dia útil';
      return {
        ...base, template: 'produtividade', bloco: 'Produtividade técnica',
        primaryLabel: barLabel, primaryFmt: 'dec1', lowerIsBetterPrimary: false,
        stores: prodStores, secundarios,
        primarySub,
        headerChips: [`Período: ${periodoLabel}`, (() => {
          // Valor único quando todas as cidades têm os mesmos dias úteis; senão detalha por loja.
          const vals = [...new Set(stores.map(s => diasUteisLoja[s.slug]))];
          return vals.length === 1
            ? `Dias úteis considerados: ${vals[0]}`
            : `Dias úteis por loja: ${stores.map(s => `${s.name} ${diasUteisLoja[s.slug]}`).join(' · ')}`;
        })()],
        storeDesc,
        nota: notaRota,
        cards: [],
        iaContexto: {
          indicador_principal: 'OS por técnico equivalente por dia útil',
          direcao: 'quanto MAIOR, melhor — mas diferenças pequenas são "resultados semelhantes"',
          indicadores_secundarios: ['Produtividade no período (OS/téc equivalente)', 'Técnicos equivalentes'],
          indicadores_contexto: ['Total de OS realizadas', 'distribuição dos técnicos híbridos'],
          nao_usar_para_desempenho: ['total absoluto de OS (quem tem mais técnicos faz mais OS)'],
          observacoes: notaRota || 'Técnicos equivalentes incluem o rateio 0,5+0,5 dos híbridos.',
        },
        insight: buildInsight(prodStores, { lowerIsBetter: false, fmtName: v => `${d1BR(v)} OS/téc por dia útil` }),
      };
    }

    // ===== MODELO 5 — VENDAS (Acessórios): principal = ATINGIMENTO vs esperado até hoje.
    // Faturamento absoluto é importante, mas vira linha — não define sozinho a melhor.
    if (metrica.type === 'seminovos') {
      const stores = cards.map(({ company }) => {
        const cat = seminovos[company.slug]?.[metrica.categoria];
        // total null = sem fonte (VIP) ou sem CLASSIFICAÇÃO possível (JMV, produto
        // ausente no Faturadas) → barra '—'; 0 = fonte ativa e venda zero.
        const total = (cat && cat.total != null) ? cat.total : null;
        const meta = cat?.meta ?? 0;
        const espValor = meta ? (meta * businessMonthFactor(dataFim, company.slug)) : 0;
        const atg = (espValor > 0 && total != null) ? (total / espValor) * 100 : null;
        return {
          slug: company.slug, name: company.name, color: themeColor(company.slug),
          primaryValue: atg,
          total, espValor: espValor || null, meta: meta || null,
        };
      });
      return {
        ...base, template: 'vendas', bloco: 'Vendas',
        primaryLabel: 'Atingimento vs esperado até hoje', primaryFmt: 'pct1', lowerIsBetterPrimary: false,
        stores,
        primaryVsTarget: Object.fromEntries(stores.map(st => [st.slug, st.primaryValue != null ? 100 : null])),
        primarySub: Object.fromEntries(stores.map(st => [st.slug, (st.total != null && st.espValor) ? `${fmtBRL(st.total)} de ${fmtBRL(st.espValor)} esperados` : null])),
        secundarios: [
          { label: 'Faturado no período', oculto: true, fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(st => [st.slug, st.total])) },
          { label: 'Esperado até hoje', oculto: true, fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(st => [st.slug, st.espValor])) },
          { label: 'Meta mensal', fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(st => [st.slug, st.meta])) },
        ],
        cards: [],
        iaContexto: {
          indicador_principal: `Atingimento vs esperado até hoje (%) — venda de SEMINOVOS ${metrica.categoria === 'mx' ? 'MX (celular/tablet)' : 'In Home (TV + linha branca)'}`,
          direcao: 'quanto MAIOR, melhor (100% = no ritmo)',
          indicadores_secundarios: ['Faturado no período', 'Esperado até hoje'],
          indicadores_contexto: ['Meta mensal'],
          nao_usar_para_desempenho: ['faturamento absoluto isolado (metas são diferentes por unidade)'],
          observacoes: 'Loja com "—" não tem fonte de seminovos cadastrada OU a fonte não informa o produto vendido (caso da JMV) — não comparar essas lojas.',
        },
        insight: buildInsight(stores, { lowerIsBetter: false, fmtName: v => `${d1BR(v)}% do esperado` }),
      };
    }

    if (metrica.type === 'acessorios') {
      const stores = cards.map(({ company }) => {
        const acc = acessorios[company.slug];
        // 0 real (loja com dado e venda zero) ≠ '—' (sem dado carregado).
        const total = acc ? (acc.total ?? 0) : null;
        const meta = acc?.meta ?? 0;
        const espValor = meta ? (meta * businessMonthFactor(dataFim, company.slug)) : 0;
        const atg = (espValor > 0 && total != null) ? (total / espValor) * 100 : null;
        return {
          slug: company.slug, name: company.name, color: themeColor(company.slug),
          primaryValue: atg,
          total, espValor: espValor || null, meta: meta || null,
          falta: (espValor > 0 && total != null) ? Math.max(0, espValor - total) : null,
          excedente: (espValor > 0 && total != null) ? Math.max(0, total - espValor) : null,
          proj: (total != null && businessMonthFactor(dataFim, company.slug) > 0) ? total / businessMonthFactor(dataFim, company.slug) : null,
        };
      });
      return {
        ...base, template: 'vendas', bloco: 'Vendas',
        primaryLabel: 'Atingimento vs esperado até hoje', primaryFmt: 'pct1', lowerIsBetterPrimary: false,
        stores,
        primaryVsTarget: Object.fromEntries(stores.map(s => [s.slug, s.primaryValue != null ? 100 : null])),
        primarySub: Object.fromEntries(stores.map(s => [s.slug, (s.total != null && s.espValor) ? `${fmtBRL(s.total)} de ${fmtBRL(s.espValor)} esperados` : null])),
        secundarios: [
          { label: 'Faturado no período', oculto: true, fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.total])) },
          { label: 'Esperado até hoje', oculto: true, fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.espValor])) },
          { label: 'Meta mensal', fmt: 'brl', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.meta])) },
        ],
        cards: [],  // as barras coloridas vs esperado já contam a história
        iaContexto: {
          indicador_principal: 'Atingimento vs esperado até hoje (%)',
          direcao: 'quanto MAIOR, melhor (100% = no ritmo)',
          indicadores_secundarios: ['Faturado no período', 'Esperado até hoje'],
          indicadores_contexto: ['Meta mensal'],
          nao_usar_para_desempenho: ['faturamento absoluto isolado (metas são diferentes por unidade)'],
        },
        insight: buildInsight(stores, { lowerIsBetter: false, fmtName: v => `${d1BR(v)}% do esperado` }),
      };
    }

    // projeto (default/fallback): Saídas/Entradas
    const stores = cards.map(({ company, summary }) => {
      const entradas = summary?.entradas ?? null;
      const saidas = summary?.saidas ?? null;
      const has = summary && (summary.entradas || summary.saidas);
      const pct = (summary && summary.entradas > 0) ? (summary.saidas / summary.entradas) * 100 : null;
      const saldo = has ? (summary.entradas - summary.saidas) : null;
      return {
        slug: company.slug, name: company.name, color: themeColor(company.slug),
        primaryValue: has ? saidas : null,
        entradas, saidas, pct, saldo,
        backlog: (summary?.backlog && summary.backlog.total) ? summary.backlog.total : null,
      };
    });
    return {
      ...base,
      primaryLabel: 'Saídas no período', primaryFmt: 'num', lowerIsBetterPrimary: false,
      stores,
      bloco: 'Operação',
      secundarios: [
        // Entradas/Saldo/Backlog = volumetria/absoluto → SEM cor. Saídas/Entradas (%) = desempenho → COM cor.
        { label: 'Entradas', hint: 'contexto — volume não define desempenho', fmt: 'num', lowerBetter: false, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.entradas])) },
        { label: 'Saídas / Entradas', fmt: 'pct0', lowerBetter: false, colorize: true, valores: Object.fromEntries(stores.map(s => [s.slug, s.pct])) },
        { label: 'Saldo em aberto', fmt: 'num', lowerBetter: true, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.saldo])) },
        { label: 'Backlog', fmt: 'num', lowerBetter: true, colorize: false, valores: Object.fromEntries(stores.map(s => [s.slug, s.backlog])) },
      ],
      cards: cardsDesempenho(stores, { value: s => s.pct, fmt: 'pct0', kickerSemMeta: 'Melhor conversão (saídas/entradas)', kickerAtencao: 'Menor conversão' }),
      iaContexto: {
        indicador_principal: 'Saídas / Entradas (%)',
        direcao: 'quanto MAIOR, melhor',
        indicadores_contexto: ['Entradas', 'Saldo em aberto', 'Backlog'],
        nao_usar_para_desempenho: ['volume absoluto de entradas ou saídas'],
      },
      insight: buildInsight(stores, { lowerIsBetter: false, fmtName: v => `${fmtNum(v)} saídas` }),
    };
  };

  // Última atualização dos uploads por loja, na ordem das COMPANIES
  const updatesInfo = (() => {
    if (!lastUpdates) return null;
    const byAsc = new Map(lastUpdates.map(l => [l.asc_code, l]));
    return companies.map(c => {
      const l = byAsc.get(c.ascCode);
      const ts = l?.ultima_atualizacao || null;
      const porTipo = Object.entries(l?.por_tipo || {})
        .sort((a, b) => (a[1] < b[1] ? 1 : -1))
        .map(([k, v]) => `${tipoUploadLabel(k)}: ${fmtUpdateTs(v)}`);
      return {
        slug: c.slug,
        name: c.name,
        label: ts ? fmtUpdateTs(ts) : 'sem dados',
        stale: isUpdateStale(ts),
        tooltip: porTipo.length
          ? `${c.fullName}\n${porTipo.join('\n')}`
          : `${c.fullName} — sem uploads`,
      };
    });
  })();

  // Ordena METRICAS conforme ordem global salva, depois filtra ocultas
  const effOrder = buildEffectiveOrder(order);
  const byKeyMap = new Map(METRICAS.map(m => [m.key, m]));
  const metricasOrdenadas = effOrder.map(k => byKeyMap.get(k)).filter(Boolean);
  const metricasVisiveis = metricasOrdenadas.filter(m => !hidden.includes(m.key));

  // Slides extras SÓ no Modo Apresentação (não entram na tela operacional).
  // 1) Taxa de Conversão do Qless — inserida logo após Recepção.
  const CONVERSAO_METRIC = { key: 'conversao', titulo: 'Taxa de Conversão do Qless', subtitulo: 'Services do Balcão (IW + OW) / Senhas Qless', accent: '#00C3B2', type: 'conversao' };
  // 1b) Produtividade — DOIS slides, inseridos logo após LM In-Home (aprov_lm_ih):
  //     MX (bancada) e In Home (LM In-Home + Linha Branca).
  const PRODUTIVIDADE_MX_METRIC = { key: 'produtividade_mx', titulo: 'Produtividade MX', subtitulo: 'OS por técnico · celulares (modelos SM) · fonte: GSPN ACC', accent: '#6366f1', type: 'produtividade', segs: ['mx'] };
  // In Home separado em LM (TV) e Linha Branca — cada um usa téc. da linha + 0,5 dos híbridos.
  const _HIB = { combinedTecField: 'tecnicos_lm_lb', combinedLabel: 'Híbridos', combinedTecLabel: 'Híbridos (LM e LB)' };
  const PRODUTIVIDADE_LM_METRIC = { key: 'produtividade_lm', titulo: 'Produtividade LM In-Home', subtitulo: 'OS por técnico · TV / monitor / soundbar (IH + II + SR) · fonte: GSPN', accent: '#7c3aed', type: 'produtividade', segs: ['lm'], ..._HIB, barLabel: 'Produtividade LM In-Home (OS/téc)' };
  const PRODUTIVIDADE_LB_METRIC = { key: 'produtividade_lb', titulo: 'Produtividade Linha Branca', subtitulo: 'OS por técnico · lavadora / geladeira / ar (IH + II + SR) · fonte: GSPN', accent: '#0891b2', type: 'produtividade', segs: ['lb'], ..._HIB, barLabel: 'Produtividade Linha Branca (OS/téc)' };
  const baseList = [...metricasVisiveis];
  // Slides extras também respeitam a lista de ocultos (config sales_day_2_hidden).
  const extrasConversao = [CONVERSAO_METRIC].filter(m => !hidden.includes(m.key));
  const recIdx = baseList.findIndex(m => m.key === 'recepcao');
  if (recIdx >= 0) baseList.splice(recIdx + 1, 0, ...extrasConversao);
  else baseList.push(...extrasConversao);
  const extrasProd = [PRODUTIVIDADE_MX_METRIC, PRODUTIVIDADE_LM_METRIC, PRODUTIVIDADE_LB_METRIC].filter(m => !hidden.includes(m.key));
  const ihIdx = baseList.findIndex(m => m.key === 'aprov_lm_ih');
  if (ihIdx >= 0) baseList.splice(ihIdx + 1, 0, ...extrasProd);
  else baseList.push(...extrasProd);
  // 2) Canais digitais SEPARADOS — WooCommerce, Store+ e SMB, um slide cada (ao final).
  const DIGITAL_METRICS = [
    { key: 'venda_woo_b2c', titulo: 'Vendas E-commerce B2C', subtitulo: 'Consumidor · Pedidos · Ticket médio', accent: '#1428A0', type: 'digital', channel: 'woo_b2c' },
    { key: 'venda_woo_b2b', titulo: 'Vendas E-commerce B2B', subtitulo: 'Atacado · Pedidos · Ticket médio', accent: '#0077C8', type: 'digital', channel: 'woo_b2b' },
    { key: 'venda_storeplus', titulo: 'Vendas Store+', subtitulo: 'Store+ (EA) · meta Samsung', accent: '#00B5E3', type: 'digital', channel: 'storeplus' },
    { key: 'venda_smb', titulo: 'Vendas SMB', subtitulo: 'SMB · meta Samsung', accent: '#0f766e', type: 'digital', channel: 'smb' },
  ];
  // Slides temporariamente ocultos no Modo Apresentação (não some da tela operacional).
  // Para reexibir, é só remover a key deste array.
  const HIDDEN_PRESENT_SLIDES = ['conversao', 'one_stop'];
  // Ordem da apresentação (pedido 25/08): VENDAS (canais digitais) → FATURAMENTO
  // (aprovações MX/LM/LB/FG Pré + venda de acessórios) → OPERAÇÃO (recepção, GD,
  // balcão, SR, In Home). Dentro de cada bloco mantém a ordem configurada.
  const isVendas = (m) => m.type === 'digital';
  const isFaturamento = (m) => m.type === 'aprovacoes' || m.type === 'acessorios' || m.type === 'seminovos';
  const presentBase = (() => {
    const all = (b2bSales && Object.keys(b2bSales).length) ? [...baseList, ...DIGITAL_METRICS] : baseList;
    const vis = all.filter(m => !HIDDEN_PRESENT_SLIDES.includes(m.key));
    return [
      ...vis.filter(isVendas),
      ...vis.filter(isFaturamento),
      ...vis.filter(m => !isVendas(m) && !isFaturamento(m)),
    ];
  })();

  // Injeta os slides PERSONALIZADOS ativos na posição escolhida (after_key = key do slide
  // que ele deve seguir; vazio/inexistente = no início). Só aparecem no Modo Apresentação.
  const presentMetricas = (() => {
    const ativos = (customSlides || []).filter(s => s.ativo && (s.titulo || (s.tipo === 'imagem' && s.imagem)));
    // titulo pode ser vazio (imagem sem título) — CustomSlide omite o cabeçalho nesse caso.
    const toMetrica = (s) => ({ key: `custom_${s.id}`, titulo: s.titulo || '', subtitulo: s.subtitulo || '', accent: '#1428A0', type: 'custom_slide', isCustom: true, tipo: s.tipo || 'texto', bullets: s.bullets || [], imagem: s.imagem || '' });
    const out = [...presentBase];
    // No início (sem after_key válido) — mantém a ordem de criação.
    ativos.filter(s => !s.after_key).slice().reverse().forEach(s => out.unshift(toMetrica(s)));
    // Depois de um slide específico.
    ativos.filter(s => s.after_key).forEach(s => {
      const idx = out.findIndex(m => m.key === s.after_key);
      if (idx >= 0) out.splice(idx + 1, 0, toMetrica(s));
      else out.push(toMetrica(s)); // âncora sumiu → joga no fim
    });
    // Slide FIXO de encerramento — SEMPRE o último (pedido 24/09: plateia não
    // sabia quando a apresentação acabava). Só existe no modo Apresentar.
    const _dbr = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');
    out.push({
      key: 'fim', titulo: 'Encerramento', subtitulo: '', type: 'fim', isFim: true,
      periodo: (dataInicio && dataFim) ? `${_dbr(dataInicio)} a ${_dbr(dataFim)}` : '',
    });
    return out;
  })();

  // ===== Roteiro (IA): sincroniza os payloads DEPOIS que os dados assentam =====
  // Debounce de 5s após a última mudança de dados; o BACKEND compara o hash e só
  // gera (em background) para os slides cujos dados MUDARAM — abrir a página ou
  // navegar entre slides não dispara nada quando os dados são os mesmos.
  useEffect(() => {
    if (!canEdit) return;                                  // só o admin dispara geração
    if (!dataFreshTick) return;                            // espera dados FRESCOS (cache local não conta)
    if (!results || !Object.keys(results).length) return;  // sem dado nenhum ainda
    const t = setTimeout(() => {
      try {
        const slides = presentMetricas
          .filter(m => !m.isCustom && !m.isFim)
          .map(m => {
            const payload = modelToPayload(buildPresentModel(m));
            if (!payload) return null;
            if (!payload.lojas.some(l => l.valor_principal != null)) return null;  // slide sem dado
            return { key: m.key, titulo: m.titulo, subtitulo: m.subtitulo || '', payload };
          })
          .filter(Boolean);
        if (!slides.length) return;
        const fp = JSON.stringify(slides.map(s => [s.key, s.payload]));
        if (fp === lastRoteiroSyncRef.current) return;  // nada mudou desde o último sync
        lastRoteiroSyncRef.current = fp;
        syncRoteiros(slides).then(r => {
          setRoteiros(r.data.roteiros || {});
          setRoteiroGenerating(r.data.generating || []);
          if ((r.data.queued || []).length || (r.data.generating || []).length) startRoteiroPolling();
        }).catch(() => { lastRoteiroSyncRef.current = ''; });  // falhou → permite re-tentar este estado
      } catch { /* roteiro nunca pode atrapalhar o dashboard */ }
    }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, dataFreshTick, results, salesDay, acessorios, seminovos, b2bSales, headcount, gdFactors, gdMetasFixas, siteMeta, dataInicio, dataFim, hidden, order]);

  // "Gerar novamente" (manual, síncrono) e salvar edição manual do roteiro.
  const handleRoteiroGenerate = async (metrica) => {
    const payload = modelToPayload(buildPresentModel(metrica));
    if (!payload) throw new Error('Slide sem dados para gerar');
    const r = await generateRoteiro({ key: metrica.key, titulo: metrica.titulo, subtitulo: metrica.subtitulo || '', payload });
    setRoteiros(prev => ({ ...prev, [metrica.key]: { texto: r.data.texto, updated_at: new Date().toISOString(), edited: false, error: null } }));
    return r.data.texto;
  };
  const handleRoteiroSave = async (key, texto) => {
    await saveRoteiro(key, texto);
    setRoteiros(prev => ({ ...prev, [key]: { ...(prev[key] || {}), texto, edited: true, error: null } }));
  };

  return (
    <div className="warrom-page" style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 16px 48px', fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', -apple-system, sans-serif" }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        .warrom-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 400px));
          gap: 14px;
          justify-content: start;
        }
        .warrom-sec-header { display:flex; align-items:center; gap:14px; margin-bottom:14px; padding:0 4px; }
        .warrom-sec-icon-box { display:inline-flex; align-items:center; justify-content:center; width:38px; height:38px; border-radius:10px; flex-shrink:0; }
        .warrom-sec-title { font-size:16px; font-weight:800; color:#0f172a; letter-spacing:-0.015em; line-height:1.2; }
        .warrom-sec-sub { font-size:11.5px; color:#64748b; margin-top:2px; }
        .warrom-pill { display:inline-flex; align-items:center; gap:6px; font-size:11px; color:#475569; font-weight:700; padding:4px 10px; background:rgba(15,23,42,0.035); border-radius:999px; border:1px solid rgba(15,23,42,0.08); white-space:nowrap; }
        .warrom-card { background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); border-radius:14px; border:1px solid #e2e8f0; padding:16px 18px 18px; box-shadow: 0 1px 0 rgba(15,23,42,0.035) inset, 0 8px 20px rgba(15,23,42,0.10); transition: transform 0.15s, border-color 0.15s; }
        .warrom-card:hover { border-color: #cbd5e1; transform: translateY(-1px); }
        .warrom-legend { display:flex; align-items:center; gap:16px; flex-wrap:wrap; padding:12px 18px; margin-bottom:24px; background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); border-radius:12px; border:1px solid #e2e8f0; }
        @media (max-width: 600px) {
          .warrom-page { padding: 14px 10px 32px; }
          .warrom-grid { grid-template-columns: 1fr; gap: 10px; }
          .warrom-card { padding: 14px 16px 16px; border-radius: 12px; }
          .warrom-sec-title { font-size: 15px; }
          .warrom-sec-sub { font-size: 11px; }
          .warrom-sec-icon-box { width:34px; height:34px; }
          .warrom-legend { padding:10px 14px; gap:12px; margin-bottom:18px; }
        }
      `}</style>

      <FilterBar
        dataInicio={dataInicio} dataFim={dataFim}
        setDataInicio={setDataInicio} setDataFim={setDataFim}
        setMes={setMes}
        atalhos={{ mesAtual: mesAtualAtivo, mesPassado: mesPassadoAtivo }}
        filtroExpandido={filtroExpandido} setFiltroExpandido={setFiltroExpandido}
        diasNoPeriodo={diasNoPeriodo}
        loading={loading || hiddenLoading}
        onOpenSettings={openSettings}
        onOpenCustomSlides={() => setCustomSlidesOpen(true)}
        onOpenRoteiros={() => setRoteirosOpen(true)}
        onOpenHeadcount={() => setHeadcountOpen(true)}
        onOpenMapeamento={() => setMapeamentoOpen(true)}
        onOpenBacklog={() => setBacklogMapOpen(true)}
        updates={updatesInfo}
        canEdit={canEdit}
      />

      <div className="warrom-legend">
        <span style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.14em' }}>Autorizadas</span>
        {companies.map(c => (
          <div key={c.slug} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: COMPANY_THEME[c.slug]?.color,
              boxShadow: `0 0 8px ${COMPANY_THEME[c.slug]?.color}aa`,
            }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{c.name}</span>
          </div>
        ))}
      </div>

      {loading && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          padding: 48, color: '#475569',
          background: '#ffffff', borderRadius: 14, border: '1px solid #e2e8f0',
        }}>
          <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Carregando dados das lojas…</span>
        </div>
      )}
      {error && <div style={{ padding: 20, color: '#dc2626', background: 'rgba(239,68,68,0.08)', borderRadius: 12, border: '1px solid rgba(239,68,68,0.25)', textAlign: 'center' }}>{error}</div>}

      {!loading && metricasVisiveis.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: '#64748b', background: '#ffffff', borderRadius: 14, border: '1px solid #e2e8f0' }}>
          Todas as métricas estão ocultas. Clique em <strong>Configurar</strong> para reativá-las.
        </div>
      )}

      {!loading && ((renderMetricaSection) => (
        <>
          {metricasVisiveis.map(renderMetricaSection)}
          {presenting && presentMetricas.length > 0 && (
            <PresentationOverlay
              metricas={presentMetricas}
              idx={slideIdx}
              setIdx={setSlideIdx}
              onClose={() => setPresenting(false)}
              buildModel={buildPresentModel}
            />
          )}
          {gerandoPdf && presentMetricas.length > 0 && (
            <SlidesPdf
              metricas={presentMetricas}
              buildModel={buildPresentModel}
              nomeArquivo={`Sales Day - ${(dataInicio || '').split('-').reverse().slice(0, 2).join('-')} a ${(dataFim || '').split('-').reverse().join('-')}`}
              onDone={() => setGerandoPdf(false)}
            />
          )}
        </>
      ))((metrica) => {
        const cards = companies
          .filter(c => !(HIDDEN_TABS[c.slug] || []).includes(metrica.key))
          .map(c => ({ company: c, summary: getSummaryForCompany(metrica, c) }));
        if (cards.length === 0) return null;

        const Icone = metrica.Icone;
        const fontePorCard = metrica.type === 'aprovacoes' ? null : metrica.fonte;
        const aberto = infoAberto === metrica.key;

        return (
          <section key={metrica.key} style={{ marginBottom: 32 }}>
            <header className="warrom-sec-header">
              <div className="warrom-sec-icon-box" style={{
                background: `linear-gradient(135deg, ${metrica.accent}20 0%, ${metrica.accent}08 100%)`,
                border: `1px solid ${metrica.accent}30`,
              }}>
                <Icone size={18} color={metrica.accent} strokeWidth={2.2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h2 className="warrom-sec-title">{metrica.titulo}</h2>
                  <button
                    onClick={() => setInfoAberto(aberto ? null : metrica.key)}
                    aria-label={aberto ? 'Fechar informação' : 'Ver explicação'}
                    style={{
                      background: aberto ? `${metrica.accent}22` : 'rgba(15,23,42,0.04)',
                      border: `1px solid ${aberto ? metrica.accent + '50' : 'rgba(15,23,42,0.08)'}`,
                      borderRadius: 999, cursor: 'pointer',
                      width: 22, height: 22, display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}
                  >
                    <Info size={12} color={aberto ? metrica.accent : '#475569'} strokeWidth={2.5} />
                  </button>
                  {fontePorCard && (() => {
                    const ft = FONTE_COLORS[fontePorCard] || FONTE_COLORS['GSPN'];
                    return (
                      <span style={{
                        display: 'inline-flex', padding: '3px 9px',
                        background: ft.bg, border: `1px solid ${ft.border}`,
                        borderRadius: 999, fontSize: 10, fontWeight: 800,
                        color: ft.color, letterSpacing: '0.08em',
                        fontFamily: monoFont, textTransform: 'uppercase',
                      }}>Fonte · {fontePorCard}</span>
                    );
                  })()}
                  {metrica.type === 'aprovacoes' && (
                    <span style={{
                      display: 'inline-flex', padding: '3px 9px',
                      background: 'rgba(156,163,175,0.06)',
                      border: '1px solid rgba(156,163,175,0.20)',
                      borderRadius: 999, fontSize: 10, fontWeight: 700,
                      color: '#475569', letterSpacing: '0.06em',
                      fontFamily: monoFont,
                    }}>Fonte por loja ↓</span>
                  )}
                </div>
                <p className="warrom-sec-sub">{metrica.subtitulo}</p>
              </div>
              <span className="warrom-pill">{cards.length} {cards.length === 1 ? 'loja' : 'lojas'}</span>
            </header>

            {aberto && (
              <div style={{
                marginBottom: 14, padding: '14px 16px',
                background: `linear-gradient(135deg, ${metrica.accent}10 0%, ${metrica.accent}03 100%)`,
                border: `1px solid ${metrica.accent}30`,
                borderRadius: 12,
                fontSize: 13, lineHeight: 1.55, color: '#334155',
                position: 'relative',
              }}>
                <button onClick={() => setInfoAberto(null)} aria-label="Fechar" style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: 4,
                  display: 'inline-flex', color: '#475569',
                }}><X size={14} /></button>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, paddingRight: 24 }}>
                  <Info size={15} color={metrica.accent} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>{metrica.descricao}</span>
                </div>
              </div>
            )}

            <div className="warrom-grid">
              {cards.map(({ company, summary }) => {
                const onShowRule = () => abrirRegra(metrica, company);
                const hc = headcount[company.slug] || {};
                if (metrica.type === 'recepcao') {
                  // Comparação com período equivalente do mês anterior DA MESMA empresa
                  const prevSummary = summarizeRecepcao(resultsPrev[company.slug]?.recepcao_diario);
                  const avgPropria = prevSummary?.qtd || 0;
                  return <CardRecepcao key={company.slug} company={company} summary={summary} avgEmpresa={avgPropria} onShowRule={onShowRule} recepcionistas={hc.recepcionistas} />;
                }
                if (metrica.type === 'gdta') {
                  const balcaoData = results[company.slug]?.balcao_total;
                  const balcaoMX = balcaoData ? (sumDaily(balcaoData.entradas_diario || [], 'iw', 'ow')) : 0;
                  const factor = gdFactors[company.slug] ?? 50;
                  return <CardGDTA key={company.slug} company={company} summary={summary} balcaoMX={balcaoMX} factorPct={factor} metaFixa={gdMetasFixas[company.slug] || 0} dataFim={dataFim} onShowRule={onShowRule} />;
                }
                if (metrica.type === 'section') return <CardSection key={company.slug} company={company} summary={summary} onShowRule={onShowRule} />;
                if (metrica.type === 'aprovacoes') {
                  const isMX = metrica.key === 'aprov_mx';
                  const isLB = metrica.key === 'aprov_lb';
                  const isLM_IH = metrica.key === 'aprov_lm_ih';   // LM In Home → técnico LM
                  const tecLM = isLB ? hc.tecnicos_lb : isLM_IH ? hc.tecnicos_lm : hc.tecnicos_mx;
                  // Operador (> ou ≥) da MESMA aba que deu o summary, lido de /configs
                  // (override da loja ou padrão), como no modal "Ver regra da métrica".
                  const abasSD = salesDay?.empresas?.[company.slug]?.abas || {};
                  const abaCard = (metrica.abaKeys || []).find(ak => abasSD[ak]);
                  const comparison = abaCard ? aprovConfigs[company.slug]?.abas?.[abaCard]?.comparison : undefined;
                  return <CardAprovacoes
                    fatorEsperado={fatorEsperadoLoja(company.slug)}
                    key={company.slug} company={company} aba={summary} comparison={comparison}
                    diasNoPeriodo={diasNoPeriodo} diasNoMes={diasNoMes}
                    onShowRule={onShowRule}
                    onShowOSList={() => abrirOSList(metrica, company)}
                    tecnicos={tecLM}
                    showTecnicos={isMX || isLB || isLM_IH}
                    tecnicosLabel={isLB ? "Técnicos LB" : isLM_IH ? "Técnicos LM" : "Técnicos MX"}
                    tecnicosUnit={isLB ? "OS / téc LB" : isLM_IH ? "OS / téc LM" : "OS / téc"}
                  />;
                }
                if (metrica.type === 'seminovos') {
                  const sem = seminovos[company.slug] || {};
                  const cat = sem[metrica.categoria] || {};
                  return <CardAcessorios
                    fatorEsperado={fatorEsperadoLoja(company.slug)}
                    key={company.slug} company={company}
                    total={cat.total ?? null}
                    meta={cat.meta ?? 0}
                    diasNoPeriodo={diasNoPeriodo} diasNoMes={diasNoMes}
                    semDados={sem.semDados || cat.total == null}
                    notaSemDados={sem.semDados ? 'Sem fonte de seminovos nesta loja'
                      : (cat.total == null && sem.total > 0)
                        ? `${fmtBRL(sem.total)} vendidos sem separação MX/In Home`
                        : 'Sem dados no período'}
                    loading={seminovosLoading && seminovos[company.slug] === undefined}
                    editable={false}
                    ano={headcountYear} mes={headcountMonth}
                    onSaveManual={null}
                    onSaveMeta={async (valor) => {
                      await saveSeminovosMeta({ ascCode: company.ascCode, valor, categoria: metrica.categoria }, 'admin');
                      await refreshSeminovos();
                    }}
                    onShowItens={null}
                    onShowRule={onShowRule}
                  />;
                }
                if (metrica.type === 'acessorios') {
                  const acc = acessorios[company.slug] || {};
                  // VIP deixou de ser manual em 25/09/2026 — acessórios dela agora vêm
                  // da Gestor API (OP 2), como CSP/MSC. Nenhuma loja edita na mão.
                  const editable = false;
                  return <CardAcessorios
                    fatorEsperado={fatorEsperadoLoja(company.slug)}
                    key={company.slug} company={company}
                    total={acc.total ?? 0}
                    meta={acc.meta ?? 0}
                    diasNoPeriodo={diasNoPeriodo} diasNoMes={diasNoMes}
                    semDados={acc.semDados || false}
                    loading={acessoriosLoading && acessorios[company.slug] === undefined}
                    editable={editable}
                    ano={headcountYear} mes={headcountMonth}
                    onSaveManual={async (valor) => {
                      await saveAcessoriosManual({ ascCode: company.ascCode, ano: headcountYear, mes: headcountMonth, valor }, 'admin');
                      await refreshAcessorios();
                    }}
                    onSaveMeta={async (valor) => {
                      await saveAcessoriosMeta({ ascCode: company.ascCode, valor }, 'admin');
                      await refreshAcessorios();
                    }}
                    onShowItens={editable ? null : () => abrirAcessList(company)}
                    onShowRule={onShowRule}
                  />;
                }
                return <CardProjeto key={company.slug} company={company} summary={summary} onShowRule={onShowRule} />;
              })}
            </div>
          </section>
        );
      })}

      {/* Botões flutuantes: Apresentar (janela pública) e Modo Apresentador (2 janelas) */}
      {!loading && metricasVisiveis.length > 0 && !presenting && !gerandoPdf && (
        <div style={{ position: 'fixed', bottom: 70, right: 18, zIndex: 901, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
          {canEdit && (
            <button
              onClick={() => {
                // Abre o PAINEL DO APRESENTADOR em janela separada (gesto do usuário)
                // e entra na apresentação. Compartilhe SÓ a janela da apresentação.
                window.open('/apresentador', 'sd2Presenter', 'width=560,height=820');
                setSlideIdx(0); setPresenting(true);
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '11px 18px', borderRadius: 12,
                border: '1px solid rgba(15,118,110,0.45)',
                background: 'linear-gradient(180deg, #14b8a6 0%, #0f766e 100%)',
                color: '#ffffff', fontSize: 14, fontWeight: 800,
                fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', -apple-system, sans-serif", cursor: 'pointer',
                boxShadow: '0 8px 22px rgba(15,118,110,0.4)',
              }}
              title="Abre 2 janelas: a apresentação (para compartilhar) e o painel com o roteiro (só para você)"
            >
              <BookOpen size={18} />
              Iniciar modo apresentador
            </button>
          )}
          <button
            onClick={() => { setSlideIdx(0); setPresenting(true); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '11px 18px', borderRadius: 12,
              border: '1px solid rgba(5,150,105,0.45)',
              background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
              color: '#ffffff', fontSize: 14, fontWeight: 800,
              fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', -apple-system, sans-serif", cursor: 'pointer',
              boxShadow: '0 8px 22px rgba(16,185,129,0.4)',
            }}
            title="Apresentar esta tela métrica por métrica (tela cheia, fontes grandes)"
          >
            <Presentation size={18} />
            Apresentar
          </button>
          <button
            onClick={() => setGerandoPdf(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '9px 16px', borderRadius: 12,
              border: '1px solid rgba(15,23,42,0.18)',
              background: '#ffffff',
              color: '#0f172a', fontSize: 13.5, fontWeight: 800,
              fontFamily: "'Samsung Sharp Sans', 'SamsungOne', 'Inter', -apple-system, sans-serif", cursor: 'pointer',
              boxShadow: '0 6px 18px rgba(15,23,42,0.15)',
            }}
            title="Gera um PDF com todos os slides da apresentação (os ocultos não entram), um por página"
          >
            <FileDown size={17} />
            Baixar PDF
          </button>
        </div>
      )}

      {settingsOpen && (
        <SettingsModal
          hidden={hiddenDraft}
          order={orderDraft}
          onToggle={toggleDraft}
          onMove={moveDraft}
          onSaveAll={saveSettings}
          saving={saving}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {roteirosOpen && (
        <RoteirosModal
          slides={presentMetricas.filter(m => !m.isCustom && !m.isFim)}
          roteiros={roteiros}
          generating={roteiroGenerating}
          onGenerate={handleRoteiroGenerate}
          onSave={handleRoteiroSave}
          onRefresh={refreshRoteiros}
          onClose={() => setRoteirosOpen(false)}
        />
      )}

      {customSlidesOpen && (
        <CustomSlidesModal
          initialSlides={customSlides}
          anchors={presentBase.map(m => ({ key: m.key, titulo: m.titulo }))}
          saving={savingCustom}
          onSave={handleSaveCustomSlides}
          onClose={() => setCustomSlidesOpen(false)}
        />
      )}

      {regraOpen && (() => {
        const { metrica, company } = regraOpen;
        const aprovConfig = aprovConfigs[company.slug];
        let abaCfg = null;
        let abaKeyUsada = null;
        if (metrica.type === 'aprovacoes' && aprovConfig?.abas) {
          for (const ak of metrica.abaKeys || []) {
            if (aprovConfig.abas[ak]) { abaCfg = aprovConfig.abas[ak]; abaKeyUsada = ak; break; }
          }
        }
        const refreshConfigs = async () => {
          try { const r = await getAprovacoesConfigs(); setAprovConfigs(r.data || {}); }
          catch { /* noop */ }
          loadData();
        };
        const saveGdFactorFn = async (slug, pct) => {
          const newFactors = { ...gdFactors, [slug]: pct };
          await setGdProjection(newFactors);
          setGdFactors(newFactors);
        };
        return (
          <RegraModal
            metrica={metrica}
            company={company}
            distintos={distintosCache[company.slug]}
            aprovConfig={aprovConfig}
            abaCfg={abaCfg}
            abaKey={abaKeyUsada}
            loading={distintosLoading}
            onClose={() => setRegraOpen(null)}
            onChanged={refreshConfigs}
            gdFactor={gdFactors[company.slug] ?? 50}
            onSaveGdFactor={saveGdFactorFn}
          />
        );
      })()}

      {headcountOpen && (
        <HeadcountModal
          year={headcountYear}
          month={headcountMonth}
          entries={headcount}
          companies={companies}
          onSaved={() => { setHeadcountOpen(false); refreshHeadcount(); }}
          onClose={() => setHeadcountOpen(false)}
        />
      )}

      {osListOpen && (
        <OSListModal
          metrica={osListOpen.metrica}
          company={osListOpen.company}
          data={osListData}
          loading={osListLoading}
          filtro={osListFiltro}
          setFiltro={setOsListFiltro}
          onClose={() => { setOsListOpen(null); setOsListData(null); }}
        />
      )}

      {acessListOpen && (
        <AcessoriosListModal
          company={acessListOpen}
          data={acessListData}
          loading={acessListLoading}
          onClose={() => { setAcessListOpen(null); setAcessListData(null); }}
        />
      )}

      {mapeamentoOpen && (
        <MapeamentoModal
          companies={companies}
          onClose={() => setMapeamentoOpen(false)}
          onSaved={async () => {
            try { const r = await getAprovacoesConfigs(); setAprovConfigs(r.data || {}); } catch {}
            loadData();
          }}
        />
      )}

      <BacklogMapeamentoModal
        open={backlogMapOpen}
        onClose={() => setBacklogMapOpen(false)}
        ascCode="2326126"
      />

      <B2BSalesPanel dataInicio={dataInicio} dataFim={dataFim} />
      <StorePlusSMBChart onDataChanged={refreshB2bSales} />
    </div>
  );
}

export default SalesDay2Dashboard;
